import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import * as pako from "pako";
import { useSelector } from "react-redux";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import Comments from "../comments/Comments";
import MWHLoadingScreenLogo from "../../assets/images/MWHLoadingScreenLogo.png";
import useMarkupRenderer from "./markupTools/useMarkupRenderer";
import MarkupToolButtons from "./markupTools/MarkupToolButtons";
import {
  getRectCalloutGeometry,
  getRectCalloutTailEdge,
} from "./markupTools/RectCalloutToolButton";
import { DEFAULT_TOOL_ID } from "./markupTools/toolConfigs";
import {
  HOST_URL,
  LEGACY_MARKUP_CUTOFF,
  customStyles,
  markupCategories,
} from "../../helpers/constants/Constants";

const STAMP_PRESETS = ["APPROVED", "FOR REVIEW", "ISSUE", "AS-BUILT"];
const MWH_GREEN = "#93d500";
const SAVED_MARKUP_PREVIEW_MAX_DIMENSION = 320;
const SAVED_MARKUP_PREVIEW_MIME_TYPE = "image/jpeg";
const SAVED_MARKUP_PREVIEW_QUALITY = 0.72;
// Raw (pre-base64) chunk size. Chunks are transported base64-encoded as
// text/plain (WAFs commonly block application/octet-stream), so this is
// scaled down by ~3/4 to keep the base64-encoded wire size per request
// roughly equal to the previous raw binary chunk size.
const CHUNK_SIZE_BYTES = 384 * 1024;
const MARKUP_CAPTURE_SCALE_2D = 3;
const MARKUP_CAPTURE_SCALE_3D = 1;
const MARKUP_CAPTURE_MAX_DIMENSION = 8192;
const MARKUP_CANVAS_MAX_ZOOM = 5;
const MARKUP_2D_STAMP_DEFAULT_PEN_SIZE = 10;
const MARKUP_2D_CALLOUT_DEFAULT_PEN_SIZE = 10;
const MARKUP_DEFAULT_ASPECT_RATIO = 16 / 9;
const MEASURE_COORDINATE_TEXTBOX_OFFSET = { x: 18, y: -52 };
const MEASURE_COORDINATE_TEXTBOX_MIN_WIDTH = 170;
const SAVED_MARKUPS_SESSION_CACHE = new Map();
const MULTI_DISCIPLINE_CATEGORY = "Multi-Discipline";
const MARKUP_PIN_STATUS_COLORS = {
  open: {
    head: "#e53935",
    stem: "#b71c1c",
    label: "Open",
  },
  underReview: {
    head: "#ffb300",
    stem: "#f9a825",
    label: "Under review",
  },
  closed: {
    head: "#43a047",
    stem: "#1b5e20",
    label: "Closed",
  },
};
const MARKUP_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "under-review", label: "Under review" },
  { value: "closed", label: "Closed" },
];

// A markup is "legacy" if it was created before the day the new markup
// production code went live, and so was made in the older mView tool.
const LEGACY_MARKUP_CUTOFF_TIMESTAMP = Date.parse(LEGACY_MARKUP_CUTOFF);
const isLegacyMarkup = (markup) => {
  const createdValue =
    markup?.created || markup?.createdDate || markup?.createdAt;
  const createdTimestamp = Date.parse(createdValue);
  return (
    Number.isFinite(createdTimestamp) &&
    createdTimestamp < LEGACY_MARKUP_CUTOFF_TIMESTAMP
  );
};

const getMarkupSortTimestamp = (markup) => {
  const candidates = [
    markup?.updatedAt,
    markup?.modifiedAt,
    markup?.createdAt,
    markup?.created,
    markup?.createdDate,
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const timestamp = Date.parse(candidate);
    if (Number.isFinite(timestamp)) {
      return timestamp;
    }
  }

  return 0;
};

const sortMarkupsNewestFirst = (markups) => {
  const list = Array.isArray(markups) ? [...markups] : [];

  return list.sort((a, b) => {
    const timeDelta = getMarkupSortTimestamp(b) - getMarkupSortTimestamp(a);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return Number(b?.id || 0) - Number(a?.id || 0);
  });
};
const TOOL_IDS = {
  SELECT: "select",
  FREEHAND: "freehand",
  ARROW: "arrow",
  RECTANGLE: "rectangle",
  ELLIPSE: "ellipse",
  CLOUD: "cloud",
  TRIANGLE: "triangle",
  LINE: "line",
  CROSS: "cross",
  TEXT: "text",
  CALLOUT: "callout",
  RECT_CALLOUT: "rect-callout",
  POLYLINE: "polyline",
  REDACT: "redact",
  HIGHLIGHT: "highlight",
  STAMP: "stamp",
  IMAGE: "image",
};
const FILLABLE_TOOL_IDS = [
  TOOL_IDS.RECTANGLE,
  TOOL_IDS.ELLIPSE,
  TOOL_IDS.CLOUD,
  TOOL_IDS.TRIANGLE,
  TOOL_IDS.CALLOUT,
  TOOL_IDS.RECT_CALLOUT,
  TOOL_IDS.TEXT,
];
const DEFAULT_MARKUP_CATEGORY = markupCategories?.[0] || "General";

// Encodes bytes as base64url (RFC 4648 §5) in chunks to avoid call-stack
// limits from String.fromCharCode.apply on large typed arrays. The '+'/'/'
// characters standard base64 uses are avoided since, combined with random
// 'for'/'if' substrings in large blobs, they can trip WAF Windows
// FOR/IF command-injection heuristics (e.g. matches on "for /", "if /").
const encodeBytesToBase64 = (bytes) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const slice = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode.apply(null, slice);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const ScreenshotMarkup = ({
  viewerState,
  isPanelCollapsed = false,
  disableManualSave = false,
  onRegisterRejectionAutoSave = null,
  onMarkupModeChange = null,
}) => {
  const imageCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const entityCanvasRef = useRef(null);
  const zoomViewportRef = useRef(null);
  const viewerDockRef = useRef(null);
  const fillColorInputRef = useRef(null);
  const imageUploadInputRef = useRef(null);
  const savedMarkupListRef = useRef(null);
  const undoStackRef = useRef([]);
  const viewerCanvasPointerEventsRef = useRef(null);
  const measureEventCleanupRef = useRef(null);
  const measureOverlaySnapshotRef = useRef(null);
  const shouldRestoreOverlayRef = useRef(false);
  const pendingMeasureLabelsRef = useRef([]);
  const calloutCounterRef = useRef(1);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const draftEntityRef = useRef(null);
  const interactionRef = useRef({
    mode: null,
    entityId: null,
    entityIds: null,
    startPoint: null,
    startEntity: null,
    startEntities: null,
    selectionRect: null,
  });
  const entitiesRef = useRef([]);
  const selectedEntityIdRef = useRef(null);
  const selectedEntityIdsRef = useRef([]);
  const entityIdCounterRef = useRef(1);
  const canvasCursorRef = useRef("default");
  const loadedOverlayDataUrlRef = useRef(null);
  const savedMarkupPreviewCacheRef = useRef(new Map());
  const savedMarkupPreviewInFlightRef = useRef(new Set());
  const savedMarkupPreviewObserverRef = useRef(null);
  const savedMarkupPreviewNodesRef = useRef(new Map());
  const savedMarkupPinCacheRef = useRef(new Map());
  const pinOverlayLastRefreshRef = useRef(0);
  const pinLoadInFlightRef = useRef(false);
  const loadedMarkupViewerStateRef = useRef(null);
  const lockSavedMarkupListRef = useRef(false);
  const lastSavedMarkupProjectIdRef = useRef(null);
  const markupImageCacheRef = useRef(new Map());
  const lastValidScreenshotUrlRef = useRef(null);
  const middleMousePanRef = useRef({
    active: false,
    startClientX: 0,
    startClientY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
  });

  const { selectedProject, allProjectUsers, userDetails } = useSelector(
    (state) => state.globalReducer,
  );
  const { selectedModels, modelFilterState, sheetsSelected, selectedSheet } =
    useSelector(
    (state) => state.modelReducer,
  );
  const userData = useSelector((state) => state.userData?.data);

  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isAwaitingPinPlacement, setIsAwaitingPinPlacement] = useState(false);
  const [pendingCaptureMultiDiscipline, setPendingCaptureMultiDiscipline] =
    useState(false);
  const [error, setError] = useState(null);
  const [penColor, setPenColor] = useState("#ff2e2e");
  const [fillColor, setFillColor] = useState("#ffd54f");
  const [penSize, setPenSize] = useState(3);
  const [undoStack, setUndoStack] = useState([]);
  const [activeTool, setActiveTool] = useState(DEFAULT_TOOL_ID);
  const [stampPresetIndex, setStampPresetIndex] = useState(0);
  const [entities, setEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [isMarkupMode, setIsMarkupMode] = useState(false);
  const [isMultiDisciplineMode, setIsMultiDisciplineMode] = useState(false);
  const [multiDisciplineUserTagName, setMultiDisciplineUserTagName] =
    useState("");
  const [multiDisciplineUserTagDate, setMultiDisciplineUserTagDate] =
    useState("");
  const [hoveredEntityId, setHoveredEntityId] = useState(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [dockTop, setDockTop] = useState(56);
  const [dockScale, setDockScale] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [captureCanvasSize, setCaptureCanvasSize] = useState({
    width: 0,
    height: 0,
  });
  const [zoomViewportSize, setZoomViewportSize] = useState({
    width: 0,
    height: 0,
  });
  const [measureDoneAnchorRect, setMeasureDoneAnchorRect] = useState(null);
  const [isSizeMenuOpen, setIsSizeMenuOpen] = useState(false);
  const [canvasCursor, setCanvasCursor] = useState("default");
  const [pendingImagePlacement, setPendingImagePlacement] = useState(null);
  const [imageUploadMode, setImageUploadMode] = useState("insert");
  const [pendingReplaceImageEntityId, setPendingReplaceImageEntityId] =
    useState(null);
  const [lastMeasureLabel, setLastMeasureLabel] = useState("");
  const [, setImageRenderNonce] = useState(0);
  const [textEditor, setTextEditor] = useState({
    visible: false,
    x: 0,
    y: 0,
    value: "",
    entityId: null,
    fontSize: null,
    penColor: null,
    width: null,
    height: null,
    targetType: null,
  });
  const [savedMarkups, setSavedMarkups] = useState([]);
  const [savedMarkupsLoading, setSavedMarkupsLoading] = useState(false);
  const [savedMarkupPreviews, setSavedMarkupPreviews] = useState([]);
  const [markupFilterCat, setMarkupFilterCat] = useState(null);
  const [markupFilterValue, setMarkupFilterValue] = useState("");
  const [saveTitle, setSaveTitle] = useState("");
  const [saveCategory, setSaveCategory] = useState(DEFAULT_MARKUP_CATEGORY);
  const [saveForUsers, setSaveForUsers] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveModalMode, setSaveModalMode] = useState("create");
  const [saveBusy, setSaveBusy] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [pendingStatusMarkup, setPendingStatusMarkup] = useState(null);
  const [selectedStatusValue, setSelectedStatusValue] = useState("open");
  const [statusUpdateBusy, setStatusUpdateBusy] = useState(false);
  const [exitConfirmModalOpen, setExitConfirmModalOpen] = useState(false);
  const [clearConfirmModalOpen, setClearConfirmModalOpen] = useState(false);
  const [archiveConfirmModalOpen, setArchiveConfirmModalOpen] =
    useState(false);
  const [pendingArchiveMarkup, setPendingArchiveMarkup] = useState(null);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [pendingDeleteMarkup, setPendingDeleteMarkup] = useState(null);
  const [markupInfoModalOpen, setMarkupInfoModalOpen] = useState(false);
  const [selectedInfoMarkup, setSelectedInfoMarkup] = useState(null);
  const [markupInfoLoading, setMarkupInfoLoading] = useState(false);
  const [markupInfoSaving, setMarkupInfoSaving] = useState(false);
  const [selectedInfoMarkupImage, setSelectedInfoMarkupImage] = useState(null);
  const [selectedInfoMarkupImageLoading, setSelectedInfoMarkupImageLoading] =
    useState(false);
  const [currentMarkupId, setCurrentMarkupId] = useState(null);
  const [loadingMarkupId, setLoadingMarkupId] = useState(null);
  const [archivingMarkupId, setArchivingMarkupId] = useState(null);
  const [unarchivingMarkupId, setUnarchivingMarkupId] = useState(null);
  const [deletingArchivedMarkupId, setDeletingArchivedMarkupId] = useState(null);
  const [activeCapturePin, setActiveCapturePin] = useState(null);
  const [showAllSavedPins, setShowAllSavedPins] = useState(false);
  const [showPinStatusPopup, setShowPinStatusPopup] = useState(false);
  const [pinPlacementWarningModalOpen, setPinPlacementWarningModalOpen] =
    useState(false);
  const [savedMarkupPins, setSavedMarkupPins] = useState([]);
  const [savedMarkupPinsLoading, setSavedMarkupPinsLoading] = useState(false);
  const [pinLoadStatus, setPinLoadStatus] = useState("idle");
  const [pinLoadStats, setPinLoadStats] = useState({
    markupCount: 0,
    pinCount: 0,
  });
  const [pinOverlayFrame, setPinOverlayFrame] = useState(0);

  const setEntitySelection = useCallback((ids) => {
    const normalizedIds = Array.from(
      new Set(
        (Array.isArray(ids) ? ids : [ids])
          .map((id) => String(id || "").trim())
          .filter(Boolean),
      ),
    );
    setSelectedEntityIds(normalizedIds);
    setSelectedEntityId(normalizedIds[0] || null);
  }, []);

  useEffect(() => {
    // While placing a pin or creating a new markup capture, keep existing list entries
    // visible if an external refresh returns a narrower subset.
    lockSavedMarkupListRef.current =
      isCapturing ||
      isAwaitingPinPlacement ||
      (isMarkupMode && !currentMarkupId);
  }, [currentMarkupId, isAwaitingPinPlacement, isCapturing, isMarkupMode]);

  useEffect(() => {
    if (typeof onMarkupModeChange === "function") {
      onMarkupModeChange(isMarkupMode);
    }
  }, [isMarkupMode, onMarkupModeChange]);

  useEffect(
    () => () => {
      if (typeof onMarkupModeChange === "function") {
        onMarkupModeChange(false);
      }
    },
    [onMarkupModeChange],
  );

  const panelSelectStyles = {
    ...customStyles,
    menuPortal: (base) => ({ ...base, zIndex: 9999, fontSize: "12px" }),
    control: (provided, state) => ({
      ...provided,
      fontSize: "12px",
      background:
        "linear-gradient(120deg, rgba(58,58,58,0.34) 0%, rgba(0,93,133,0.42) 100%)",
      borderColor: state.isFocused
        ? "rgba(147,213,0,0.72)"
        : "rgba(242,242,242,0.34)",
      minHeight: "34px",
      height: "34px",
      borderRadius: "999px",
      boxShadow: state.isFocused
        ? "0 0 0 2px rgba(147,213,0,0.24), 0 8px 18px rgba(58,58,58,0.22)"
        : "0 8px 18px rgba(58,58,58,0.2)",
      "&:hover": {
        borderColor: "rgba(147,213,0,0.72)",
        boxShadow:
          "0 0 0 2px rgba(147,213,0,0.24), 0 8px 18px rgba(58,58,58,0.22)",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "rgba(242,242,242,0.96)",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(242,242,242,0.7)",
    }),
    input: (provided) => ({
      ...provided,
      color: "rgba(242,242,242,0.96)",
    }),
    option: (provided, state) => ({
      ...provided,
      color: "rgba(242,242,242,0.95)",
      background: state.isSelected
        ? "linear-gradient(120deg, rgba(147,213,0,0.42), rgba(156,197,202,0.28))"
        : state.isFocused
          ? "linear-gradient(120deg, rgba(147,213,0,0.26), rgba(156,197,202,0.2))"
          : "rgba(0,0,0,0)",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "12px",
      border: "1px solid rgba(242,242,242,0.24)",
      background:
        "linear-gradient(165deg, rgba(58,58,58,0.96) 0%, rgba(0,93,133,0.92) 100%)",
      boxShadow: "0 14px 28px rgba(58,58,58,0.28)",
      overflow: "hidden",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "rgba(156,197,202,0.95)",
    }),
  };

  const saveMarkupCreatedForStyles = useMemo(
    () => ({
      ...customStyles,
      control: (provided, state) => ({
        ...provided,
        fontSize: "12px",
        background:
          "linear-gradient(120deg, rgba(58,58,58,0.34) 0%, rgba(0,93,133,0.42) 100%)",
        borderColor: state.isFocused
          ? "rgba(147,213,0,0.72)"
          : "rgba(242,242,242,0.34)",
        minHeight: "30px",
        height: state.isMulti ? "auto" : "30px",
        borderRadius: "999px",
        boxShadow: state.isFocused
          ? "0 0 0 2px rgba(147,213,0,0.24), 0 8px 18px rgba(58,58,58,0.22)"
          : "0 8px 18px rgba(58,58,58,0.2)",
        alignItems: state.isMulti ? "flex-start" : "center",
        "&:hover": {
          borderColor: "rgba(147,213,0,0.72)",
          boxShadow:
            "0 0 0 2px rgba(147,213,0,0.24), 0 8px 18px rgba(58,58,58,0.22)",
        },
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        minHeight: "30px",
        height: state.isMulti ? "auto" : "30px",
        padding: state.isMulti ? "2px 6px" : "0 6px",
        alignItems: state.isMulti ? "flex-start" : "center",
      }),
      indicatorsContainer: (provided, state) => ({
        ...provided,
        height: state.isMulti ? "auto" : "30px",
        alignSelf: state.isMulti ? "stretch" : "center",
      }),
      multiValue: (provided) => ({
        ...provided,
        background: "rgba(147,213,0,0.2)",
        border: "1px solid rgba(147,213,0,0.32)",
        borderRadius: "999px",
        margin: "2px",
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: "rgba(242,242,242,0.95)",
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: "rgba(242,242,242,0.92)",
        ":hover": {
          backgroundColor: "rgba(147,213,0,0.35)",
          color: "rgba(242,242,242,1)",
        },
      }),
      placeholder: (provided) => ({
        ...provided,
        color: "rgba(242,242,242,0.7)",
      }),
      input: (provided) => ({
        ...provided,
        color: "rgba(242,242,242,0.96)",
      }),
      option: (provided, state) => ({
        ...provided,
        color: "rgba(242,242,242,0.95)",
        background: state.isSelected
          ? "linear-gradient(120deg, rgba(147,213,0,0.42), rgba(156,197,202,0.28))"
          : state.isFocused
            ? "linear-gradient(120deg, rgba(147,213,0,0.26), rgba(156,197,202,0.2))"
            : "rgba(0,0,0,0)",
      }),
      menu: (provided) => ({
        ...provided,
        borderRadius: "12px",
        border: "1px solid rgba(242,242,242,0.24)",
        background:
          "linear-gradient(165deg, rgba(58,58,58,0.96) 0%, rgba(0,93,133,0.92) 100%)",
        boxShadow: "0 14px 28px rgba(58,58,58,0.28)",
        overflow: "hidden",
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        color: "rgba(156,197,202,0.95)",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    [],
  );

  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const toCreatedForListForFilter = (createdFor) => {
    if (!createdFor) {
      return [];
    }
    if (Array.isArray(createdFor)) {
      return createdFor.filter(Boolean).map((value) => String(value).trim());
    }
    if (typeof createdFor === "string") {
      return createdFor
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
    }
    return [];
  };

  const safeMarkupCategories = useMemo(
    () =>
      Array.isArray(markupCategories) && markupCategories.length
        ? markupCategories
        : [DEFAULT_MARKUP_CATEGORY],
    [],
  );

  const categoryReferencesDrawing = useCallback((categoryValue) => {
    return String(categoryValue || "").toLowerCase().includes("drawing");
  }, []);

  const categoryReferences3D = useCallback((categoryValue) => {
    return String(categoryValue || "").toLowerCase().includes("3d");
  }, []);

  const isCategoryAllowedForCurrentContext = useCallback(
    (categoryValue) => {
      if (!categoryValue) {
        return false;
      }

      if (sheetsSelected) {
        return !categoryReferences3D(categoryValue);
      }

      return !categoryReferencesDrawing(categoryValue);
    },
    [categoryReferences3D, categoryReferencesDrawing, sheetsSelected],
  );

  const contextMarkupCategories = useMemo(() => {
    const allowed = safeMarkupCategories.filter((categoryValue) =>
      isCategoryAllowedForCurrentContext(categoryValue),
    );
    if (allowed.length > 0) {
      return allowed;
    }
    return [DEFAULT_MARKUP_CATEGORY];
  }, [
    isCategoryAllowedForCurrentContext,
    safeMarkupCategories,
  ]);

  const getEffectiveCategoryForContext = useCallback(
    (categoryValue) => {
      if (isCategoryAllowedForCurrentContext(categoryValue)) {
        return categoryValue;
      }

      const firstAllowed = contextMarkupCategories.find((candidate) =>
        isCategoryAllowedForCurrentContext(candidate),
      );

      if (firstAllowed) {
        return firstAllowed;
      }

      return DEFAULT_MARKUP_CATEGORY;
    },
    [contextMarkupCategories, isCategoryAllowedForCurrentContext],
  );

  const markupFilterOptions = useMemo(
    () => [
      { value: "", label: "No Filter" },
      { value: "title", label: "Title" },
      { value: "userName", label: "Created by" },
      { value: "createdFor", label: "Created for" },
      { value: "category", label: "Category" },
      { value: "status", label: "Status" },
      { value: "asset", label: "Asset" },
      { value: "archived", label: "Archived" },
    ],
    [],
  );

  const statusFilterOptions = useMemo(() => {
    return MARKUP_STATUS_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  const projectUserOptions = useMemo(
    () =>
      (allProjectUsers || []).map((user) => ({
        label: user.email,
        value: user.email,
      })),
    [allProjectUsers],
  );

  const savedMarkupPreviewMap = useMemo(
    () => new Map(savedMarkupPreviews.map((preview) => [preview.id, preview])),
    [savedMarkupPreviews],
  );

  const normalizeMarkupProcessArea = useCallback((markup) => {
    return String(markup?.processArea || markup?.asset || "")
      .trim()
      .toLowerCase();
  }, []);

  const isTwoDimensionalMarkup = useCallback(
    (markup) => {
      if (!markup || typeof markup !== "object") {
        return false;
      }

      if (normalizeMarkupProcessArea(markup).startsWith("2d")) {
        return true;
      }

      // Some legacy/archived 2D records are identified by sheet metadata
      // instead of processArea/asset values.
      const hasSheetContext =
        Boolean(String(markup?.sheetKey || "").trim()) ||
        Boolean(String(markup?.sheetId || "").trim()) ||
        Boolean(String(markup?.sheetName || "").trim()) ||
        Number.isFinite(Number(markup?.sheetDbId)) ||
        Boolean(String(markup?.selectedSheetId || "").trim()) ||
        Boolean(String(markup?.selectedSheetName || "").trim()) ||
        Boolean(String(markup?.location?.sheetKey || "").trim()) ||
        Boolean(String(markup?.location?.sheetId || "").trim()) ||
        Boolean(String(markup?.location?.sheetName || "").trim()) ||
        Number.isFinite(Number(markup?.location?.sheetDbId)) ||
        Boolean(String(markup?.pin?.sheetKey || "").trim()) ||
        Boolean(String(markup?.pin?.sheetId || "").trim()) ||
        Boolean(String(markup?.pin?.sheetName || "").trim()) ||
        Number.isFinite(Number(markup?.pin?.sheetDbId));

      return hasSheetContext;
    },
    [normalizeMarkupProcessArea],
  );

  const contextScopedMarkups = useMemo(() => {
    if (sheetsSelected) {
      return savedMarkups.filter((markup) => isTwoDimensionalMarkup(markup));
    }

    return savedMarkups.filter((markup) => !isTwoDimensionalMarkup(markup));
  }, [isTwoDimensionalMarkup, savedMarkups, sheetsSelected]);

  const createdByFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    contextScopedMarkups.forEach((markup) => {
      const name = String(markup?.userName || "").trim();
      if (!name) {
        return;
      }
      const normalized = name.toLowerCase();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        options.push({ value: name, label: name });
      }
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [contextScopedMarkups]);

  const createdForFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    contextScopedMarkups.forEach((markup) => {
      toCreatedForListForFilter(markup?.createdFor).forEach((email) => {
        const normalized = email.toLowerCase();
        if (!seen.has(normalized)) {
          seen.add(normalized);
          options.push({ value: email, label: email });
        }
      });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [contextScopedMarkups]);

  const categoryFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    contextScopedMarkups.forEach((markup) => {
      const category = String(markup?.category || "").trim();
      if (!category) {
        return;
      }
      const normalized = category.toLowerCase();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        options.push({ value: category, label: category });
      }
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [contextScopedMarkups]);

  const assetFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    contextScopedMarkups.forEach((markup) => {
      const asset = String(markup?.asset || markup?.processArea || "").trim();
      if (!asset) {
        return;
      }
      const normalized = asset.toLowerCase();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        options.push({ value: asset, label: asset });
      }
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [contextScopedMarkups]);

  const getMarkupStatusValueForFilter = useCallback((markup) => {
    const normalizedStatus = String(markup?.status || "")
      .trim()
      .toLowerCase();

    if (normalizedStatus === "closed" || Boolean(markup?.actioned)) {
      return "closed";
    }

    if (
      normalizedStatus === "under review" ||
      normalizedStatus === "under_review" ||
      normalizedStatus === "under-review" ||
      Boolean(markup?.approved)
    ) {
      return "under-review";
    }

    return "open";
  }, []);

  const filteredSavedMarkups = useMemo(() => {
    const filterKey = markupFilterCat?.value || "";
    const filterValue = String(markupFilterValue || "").trim();
    const normalizedFilter = filterValue.toLowerCase();
    const showArchivedOnly = filterKey === "archived";

    const baseList = contextScopedMarkups.filter((markup) =>
      showArchivedOnly ? Boolean(markup?.archived) : !Boolean(markup?.archived),
    );

    if (!filterKey || !filterValue) {
      return baseList;
    }

    return baseList.filter((markup) => {
      switch (filterKey) {
        case "title":
          return String(markup?.title || "")
            .toLowerCase()
            .includes(normalizedFilter);
        case "userName":
          return String(markup?.userName || "")
            .toLowerCase()
            .includes(normalizedFilter);
        case "createdFor":
          return toCreatedForListForFilter(markup?.createdFor)
            .join(",")
            .toLowerCase()
            .includes(normalizedFilter);
        case "category":
          return String(markup?.category || "")
            .toLowerCase()
            .includes(normalizedFilter);
        case "status":
          return getMarkupStatusValueForFilter(markup) === normalizedFilter;
        case "asset":
          return String(markup?.asset || markup?.processArea || "")
            .toLowerCase()
            .includes(normalizedFilter);
        case "archived":
          return Boolean(markup?.archived);
        default:
          return true;
      }
    });
  }, [
    contextScopedMarkups,
    getMarkupStatusValueForFilter,
    markupFilterCat,
    markupFilterValue,
  ]);

  const isViewingArchivedMarkups = useMemo(() => {
    return markupFilterCat?.value === "archived";
  }, [markupFilterCat]);

  const currentUserDisplayName = useMemo(
    () =>
      (
        `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim() ||
        userDetails?.name ||
        userData?.userName ||
        "Unknown User"
      ).trim(),
    [userData, userDetails],
  );

  const currentUserName = useMemo(
    () => currentUserDisplayName.toLowerCase(),
    [currentUserDisplayName],
  );

  const currentUserEmail = useMemo(
    () =>
      String(userData?.emailId || userDetails?.email || "")
        .trim()
        .toLowerCase(),
    [userData, userDetails],
  );

  useEffect(() => {
    if (!saveCategory || !isCategoryAllowedForCurrentContext(saveCategory)) {
      setSaveCategory(getEffectiveCategoryForContext(saveCategory));
    }
  }, [
    getEffectiveCategoryForContext,
    isCategoryAllowedForCurrentContext,
    saveCategory,
  ]);

  const normalizeModelReferenceValue = useCallback((value) => {
    if (value == null) {
      return "";
    }

    return String(value)
      .trim()
      .replace(/^urn:/i, "")
      .toLowerCase();
  }, []);

  const getViewerModelReference = useCallback(
    (viewerModel) => {
      if (!viewerModel) {
        return {
          modelRuntimeId: null,
          modelGuid: null,
          modelName: null,
        };
      }

      const documentNode = viewerModel.getDocumentNode?.();
      const viewerModelData = viewerModel.getData?.();
      const document = documentNode?.getDocument?.();
      const runtimeId = Number(viewerModel?.id);

      const modelGuidCandidates = [
        document?.myData?.guid,
        documentNode?.getModelKey?.(),
        documentNode?.data?.urn,
        viewerModelData?.urn,
        viewerModelData?.loadOptions?.bubbleNode?.getDocument?.()?.myData?.guid,
        viewerModelData?.loadOptions?.bubbleNode?.data?.urn,
        viewerModel?.myData?.guid,
        viewerModel.getSeedUrn?.(),
      ];

      const modelNameCandidates = [
        documentNode?.name?.(),
        documentNode?.data?.name,
        viewerModelData?.name,
      ];

      return {
        modelRuntimeId: Number.isFinite(runtimeId) ? runtimeId : null,
        modelGuid:
          modelGuidCandidates.find((candidate) =>
            Boolean(normalizeModelReferenceValue(candidate)),
          ) || null,
        modelName:
          modelNameCandidates.find((candidate) =>
            Boolean(String(candidate || "").trim()),
          ) || null,
      };
    },
    [normalizeModelReferenceValue],
  );

  const savedMarkupsQueryAsset = useMemo(
    () =>
      sheetsSelected
        ? "2D"
        : modelFilterState?.process?.processArea || "Everything",
    [modelFilterState, sheetsSelected],
  );

  const normalizeSheetKeyValue = useCallback((value) => {
    if (value == null) {
      return "";
    }
    return String(value).trim().toLowerCase();
  }, []);

  const active2DSheetContext = useMemo(() => {
    if (!sheetsSelected || !selectedSheet || typeof selectedSheet !== "object") {
      return null;
    }

    const rawSheetId =
      selectedSheet?.id ??
      selectedSheet?.sheetId ??
      selectedSheet?.dbId ??
      selectedSheet?.guid ??
      null;
    const sheetId =
      rawSheetId != null && String(rawSheetId).trim()
        ? String(rawSheetId).trim()
        : null;

    const rawSheetName =
      selectedSheet?.sheetName ??
      selectedSheet?.name ??
      selectedSheet?.data?.name ??
      null;
    const sheetName =
      rawSheetName != null && String(rawSheetName).trim()
        ? String(rawSheetName).trim()
        : null;

    const sheetKey = normalizeSheetKeyValue(sheetId || sheetName);
    const numericSheetDbId = Number(
      selectedSheet?.dbId ?? selectedSheet?.sheetId ?? selectedSheet?.id,
    );
    const sheetDbId = Number.isFinite(numericSheetDbId)
      ? numericSheetDbId
      : null;
    if (!sheetKey) {
      if (!sheetDbId) {
        return null;
      }
    }

    return {
      sheetKey: sheetKey || null,
      sheetId,
      sheetName,
      sheetDbId,
    };
  }, [normalizeSheetKeyValue, selectedSheet, sheetsSelected]);

  const activeMarkupAsset = useMemo(() => {
    const pinModelRuntimeId = Number(activeCapturePin?.modelId);
    const normalizedPinModelGuid = normalizeModelReferenceValue(
      activeCapturePin?.modelGuid,
    );
    const normalizedPinModelName = String(
      activeCapturePin?.modelName || "",
    )
      .trim()
      .toLowerCase();
    const hasPinModelRuntimeId = Number.isFinite(pinModelRuntimeId);

    if (Array.isArray(selectedModels) && selectedModels.length) {
      const matchedModel = selectedModels.find((model) => {
        const candidateRuntimeIds = [
          model?.modelId,
          model?.viewerModelId,
          model?.model?.id,
          model?.loadedModelId,
        ];

        const matchedByRuntimeId =
          hasPinModelRuntimeId &&
          candidateRuntimeIds.some((candidateId) => {
            const numericCandidateId = Number(candidateId);
            return (
              Number.isFinite(numericCandidateId) &&
              numericCandidateId === pinModelRuntimeId
            );
          });

        if (matchedByRuntimeId) {
          return true;
        }

        const normalizedSelectedModelId = normalizeModelReferenceValue(model?.id);
        if (
          normalizedPinModelGuid &&
          normalizedSelectedModelId &&
          normalizedSelectedModelId === normalizedPinModelGuid
        ) {
          return true;
        }

        const normalizedSelectedModelName = String(
          model?.documentName || "",
        )
          .trim()
          .toLowerCase();

        return (
          normalizedPinModelName &&
          normalizedSelectedModelName &&
          normalizedSelectedModelName === normalizedPinModelName
        );
      });

      if (matchedModel?.mwhAssetName || matchedModel?.asset) {
        return matchedModel.mwhAssetName || matchedModel.asset;
      }
    }

    return (
      modelFilterState?.process?.processArea ||
      "Everything"
    );
  }, [
    activeCapturePin,
    modelFilterState,
    normalizeModelReferenceValue,
    selectedModels,
  ]);

  const twoDimensionalAssetLabel = useMemo(() => {
    const sheetName = String(active2DSheetContext?.sheetName || "").trim();
    return sheetName ? `2D - ${sheetName}` : "2D";
  }, [active2DSheetContext]);

  const userIsProjectAdmin = useMemo(() => {
    const candidateEmails = [
      userData?.email,
      userData?.userName,
      userDetails?.email,
      userDetails?.userName,
      userDetails?.name,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);

    if (!candidateEmails.length || !Array.isArray(allProjectUsers)) {
      return false;
    }

    const membership = allProjectUsers.find((projectUser) => {
      const projectUserEmail = String(projectUser?.email || "")
        .trim()
        .toLowerCase();
      return projectUserEmail && candidateEmails.includes(projectUserEmail);
    });

    return Boolean(membership?.projectAdmin);
  }, [allProjectUsers, userData, userDetails]);

  const canDeleteMarkup = useCallback(
    (markup) => {
      if (!markup) {
        return false;
      }

      if (userIsProjectAdmin) {
        return true;
      }

      const markupUserName = String(markup.userName || "")
        .trim()
        .toLowerCase();
      
      // console.log("markupUserName:", markupUserName);
      // console.log("currentUserName:", currentUserName);
      
      return Boolean(currentUserName) && markupUserName === currentUserName;
    },
    [currentUserName, userIsProjectAdmin],
  );

  const toUserTagDate = useCallback((value) => {
    if (!value) {
      return "";
    }

    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }

    return parsed.toLocaleDateString();
  }, []);

  const buildCurrentEntityUserTag = useCallback(() => {
    const tagDate = toUserTagDate(new Date());
    return {
      userName: currentUserDisplayName,
      userEmail: currentUserEmail,
      userTagDate: tagDate,
      multiDisciplineUserTagName: currentUserDisplayName,
      multiDisciplineUserTagDate: tagDate,
    };
  }, [currentUserDisplayName, currentUserEmail, toUserTagDate]);

  // Author-locking: entities tag their creator's email; legacy entities without
  // an email fall back to matching on the display name they were saved with.
  const isEntityOwnedByCurrentUser = useCallback(
    (entity) => {
      if (!entity) {
        return false;
      }

      const entityEmail = String(entity.userEmail || "").trim().toLowerCase();
      if (entityEmail) {
        return Boolean(currentUserEmail) && entityEmail === currentUserEmail;
      }

      const entityUserName = String(entity.userName || "").trim().toLowerCase();
      return Boolean(currentUserName) && entityUserName === currentUserName;
    },
    [currentUserEmail, currentUserName],
  );

  const formatMeasureCoordinateNumber = useCallback((value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }

    if (Math.abs(numericValue) >= 1000) {
      return numericValue.toFixed(2);
    }

    return numericValue.toFixed(3);
  }, []);

  const createMeasureCoordinateEntityFromWidgets = useCallback(
    (widgets, canvasWidth, canvasHeight, fallbackPenColor = "#ff2e2e") => {
      if (!Array.isArray(widgets) || !widgets.length) {
        return null;
      }

      const sourceWidget = widgets.find((widget) => {
        if (
          !Number.isFinite(Number(widget?.x)) ||
          !Number.isFinite(Number(widget?.y))
        ) {
          return false;
        }

        const axisEntries = Array.isArray(widget?.axisEntries)
          ? widget.axisEntries
          : [];
        if (!axisEntries.length) {
          return false;
        }

        const normalizedAxes = new Set(
          axisEntries.map((entry) => String(entry?.axis || "").trim().toUpperCase()),
        );

        // Spot coordinate widgets expose axis values; prefer those over generic distance labels.
        return (
          normalizedAxes.has("X") ||
          normalizedAxes.has("Y") ||
          normalizedAxes.has("Z")
        );
      });

      if (!sourceWidget) {
        return null;
      }

      const orderedAxes = ["X", "Y", "Z"];
      const axisEntries = Array.isArray(sourceWidget.axisEntries)
        ? sourceWidget.axisEntries
        : [];

      const lines = orderedAxes
        .map((axisName) => {
          const entry = axisEntries.find(
            (item) =>
              String(item?.axis || "").trim().toUpperCase() === axisName,
          );

          const formatted = formatMeasureCoordinateNumber(entry?.value);
          if (!formatted) {
            return null;
          }

          return `${axisName}: ${formatted}`;
        })
        .filter(Boolean);

      if (!lines.length) {
        return null;
      }

      const sourceWidth = Number(sourceWidget.sourceWidth || 0);
      const sourceHeight = Number(sourceWidget.sourceHeight || 0);
      const scaleX =
        sourceWidth > 0 && Number(canvasWidth || 0) > 0
          ? Number(canvasWidth) / sourceWidth
          : 1;
      const scaleY =
        sourceHeight > 0 && Number(canvasHeight || 0) > 0
          ? Number(canvasHeight) / sourceHeight
          : 1;

      const anchorX = Number(sourceWidget.x) * scaleX;
      const anchorY = Number(sourceWidget.y) * scaleY;

      const textboxWidth = MEASURE_COORDINATE_TEXTBOX_MIN_WIDTH;
      const estimatedTextboxHeight = 62;

      let textX = anchorX + MEASURE_COORDINATE_TEXTBOX_OFFSET.x;
      let textY = anchorY + MEASURE_COORDINATE_TEXTBOX_OFFSET.y;

      const maxX = Math.max(8, Number(canvasWidth || 0) - textboxWidth - 8);
      const maxY = Math.max(
        8,
        Number(canvasHeight || 0) - estimatedTextboxHeight - 8,
      );
      textX = Math.min(Math.max(8, textX), maxX);
      textY = Math.min(Math.max(8, textY), maxY);

      const id = `entity-${entityIdCounterRef.current}`;
      entityIdCounterRef.current += 1;

      return {
        id,
        type: TOOL_IDS.TEXT,
        point: { x: textX, y: textY },
        text: lines.join("\n"),
        fontSize: 14,
        penColor: fallbackPenColor,
        penSize: 2,
        rotation: 0,
        ...buildCurrentEntityUserTag(),
      };
    },
    [buildCurrentEntityUserTag, formatMeasureCoordinateNumber],
  );

  const normalizePinPoint = useCallback((pin) => {
    if (!pin || typeof pin !== "object") {
      return null;
    }

    const rawX = Number(pin.x);
    const rawY = Number(pin.y);
    const rawXRatio = Number(pin.xRatio ?? pin.normalizedX ?? pin.normalizedx);
    const rawYRatio = Number(pin.yRatio ?? pin.normalizedY ?? pin.normalizedy);
    const rawSourceWidth = Number(pin.sourceWidth ?? pin.capturedWidth);
    const rawSourceHeight = Number(pin.sourceHeight ?? pin.capturedHeight);
    const rawWorldX = Number(pin.worldX ?? pin.world?.x);
    const rawWorldY = Number(pin.worldY ?? pin.world?.y);
    const rawWorldZ = Number(pin.worldZ ?? pin.world?.z);
    const rawDbId = Number(pin.dbId);
    const rawModelId = Number(pin.modelId);
    const normalizedSheetKey = normalizeSheetKeyValue(pin.sheetKey);
    const normalizedSheetId =
      pin.sheetId != null && String(pin.sheetId).trim()
        ? String(pin.sheetId).trim()
        : null;
    const normalizedSheetName =
      pin.sheetName != null && String(pin.sheetName).trim()
        ? String(pin.sheetName).trim()
        : null;

    const hasAbsolute = Number.isFinite(rawX) && Number.isFinite(rawY);
    const hasRatio = Number.isFinite(rawXRatio) && Number.isFinite(rawYRatio);
    const hasWorld =
      Number.isFinite(rawWorldX) &&
      Number.isFinite(rawWorldY) &&
      Number.isFinite(rawWorldZ);

    if (!hasAbsolute && !hasRatio && !hasWorld) {
      return null;
    }

    return {
      x: hasAbsolute ? rawX : null,
      y: hasAbsolute ? rawY : null,
      xRatio: hasRatio ? rawXRatio : null,
      yRatio: hasRatio ? rawYRatio : null,
      sourceWidth:
        Number.isFinite(rawSourceWidth) && rawSourceWidth > 0
          ? rawSourceWidth
          : null,
      sourceHeight:
        Number.isFinite(rawSourceHeight) && rawSourceHeight > 0
          ? rawSourceHeight
          : null,
      worldX: hasWorld ? rawWorldX : null,
      worldY: hasWorld ? rawWorldY : null,
      worldZ: hasWorld ? rawWorldZ : null,
      dbId: Number.isFinite(rawDbId) ? rawDbId : null,
      modelId: Number.isFinite(rawModelId) ? rawModelId : null,
      modelGuid: pin.modelGuid ? String(pin.modelGuid).trim() : null,
      modelName: pin.modelName ? String(pin.modelName).trim() : null,
      sheetKey: normalizedSheetKey || null,
      sheetId: normalizedSheetId,
      sheetName: normalizedSheetName,
    };
  }, [normalizeSheetKeyValue]);

  const withActiveSheetPinContext = useCallback(
    (pin) => {
      const normalizedPin = normalizePinPoint(pin);
      if (!normalizedPin) {
        return null;
      }

      if (!sheetsSelected) {
        return normalizedPin;
      }

      return {
        ...normalizedPin,
        sheetKey: active2DSheetContext?.sheetKey || normalizedPin.sheetKey || null,
        sheetId: active2DSheetContext?.sheetId || normalizedPin.sheetId || null,
        sheetName:
          active2DSheetContext?.sheetName || normalizedPin.sheetName || null,
        dbId:
          Number.isFinite(Number(active2DSheetContext?.sheetDbId))
            ? Number(active2DSheetContext.sheetDbId)
            : normalizedPin.dbId,
      };
    },
    [active2DSheetContext, normalizePinPoint, sheetsSelected],
  );

  const projectWorldPinToClient = useCallback((pin, viewer) => {
    if (!pin || !viewer) {
      return null;
    }

    const worldX = Number(pin.worldX);
    const worldY = Number(pin.worldY);
    const worldZ = Number(pin.worldZ);
    if (
      !Number.isFinite(worldX) ||
      !Number.isFinite(worldY) ||
      !Number.isFinite(worldZ)
    ) {
      return null;
    }

    const projectionAttempts = [
      () => viewer.impl?.worldToClient?.(worldX, worldY, worldZ),
      () => viewer.worldToClient?.({ x: worldX, y: worldY, z: worldZ }),
      () => viewer.impl?.worldToClient?.({ x: worldX, y: worldY, z: worldZ }),
    ];

    for (const getProjected of projectionAttempts) {
      try {
        const projected = getProjected();
        if (
          projected &&
          Number.isFinite(projected.x) &&
          Number.isFinite(projected.y)
        ) {
          return { x: projected.x, y: projected.y };
        }
      } catch (err) {
        // Try the next projection strategy.
      }
    }

    return null;
  }, []);

  const getPinRenderPosition = useCallback(
    (pin, container) => {
      if (!pin || !container) {
        return null;
      }

      const containerWidth = Number(container.clientWidth || 0);
      const containerHeight = Number(container.clientHeight || 0);
      if (!containerWidth || !containerHeight) {
        return null;
      }

      let x = null;
      let y = null;

      const isSheetContext = Boolean(sheetsSelected);

      if (isSheetContext) {
        // 2D sheet markups are captured in screen space, so prefer ratio/absolute
        // coordinates and avoid world projection fallbacks.
        if (Number.isFinite(pin.xRatio) && Number.isFinite(pin.yRatio)) {
          x = pin.xRatio * containerWidth;
          y = pin.yRatio * containerHeight;
        } else if (
          Number.isFinite(pin.x) &&
          Number.isFinite(pin.y) &&
          Number.isFinite(pin.sourceWidth) &&
          Number.isFinite(pin.sourceHeight) &&
          pin.sourceWidth > 0 &&
          pin.sourceHeight > 0
        ) {
          x = (pin.x / pin.sourceWidth) * containerWidth;
          y = (pin.y / pin.sourceHeight) * containerHeight;
        } else if (Number.isFinite(pin.x) && Number.isFinite(pin.y)) {
          x = pin.x;
          y = pin.y;
        }
      }

      if (!isSheetContext) {
        const projectedWorldPoint = projectWorldPinToClient(
          pin,
          viewerState?.viewer,
        );
        if (projectedWorldPoint) {
          x = projectedWorldPoint.x;
          y = projectedWorldPoint.y;
        }
      }

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        if (Number.isFinite(pin.xRatio) && Number.isFinite(pin.yRatio)) {
          x = pin.xRatio * containerWidth;
          y = pin.yRatio * containerHeight;
        } else if (
          Number.isFinite(pin.x) &&
          Number.isFinite(pin.y) &&
          Number.isFinite(pin.sourceWidth) &&
          Number.isFinite(pin.sourceHeight) &&
          pin.sourceWidth > 0 &&
          pin.sourceHeight > 0
        ) {
          x = (pin.x / pin.sourceWidth) * containerWidth;
          y = (pin.y / pin.sourceHeight) * containerHeight;
        } else if (Number.isFinite(pin.x) && Number.isFinite(pin.y)) {
          x = pin.x;
          y = pin.y;
        }
      }

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }

      const safeX = Math.min(Math.max(8, x), Math.max(8, containerWidth - 8));
      const safeY = Math.min(Math.max(8, y), Math.max(8, containerHeight - 8));

      return { x: safeX, y: safeY };
    },
    [projectWorldPinToClient, sheetsSelected, viewerState],
  );

  const getMarkupStatusDisplay = useCallback((markup) => {
    const normalizedStatus = String(markup?.status || "")
      .trim()
      .toLowerCase();
    const palette =
      normalizedStatus === "closed" || Boolean(markup?.actioned)
        ? MARKUP_PIN_STATUS_COLORS.closed
        : normalizedStatus === "under review" ||
            normalizedStatus === "under_review" ||
            normalizedStatus === "under-review" ||
            Boolean(markup?.approved)
          ? MARKUP_PIN_STATUS_COLORS.underReview
          : MARKUP_PIN_STATUS_COLORS.open;

    return {
      value:
        palette.label === MARKUP_PIN_STATUS_COLORS.closed.label
          ? "closed"
          : palette.label === MARKUP_PIN_STATUS_COLORS.underReview.label
            ? "under-review"
            : "open",
      label: palette.label,
      head: palette.head,
      stem: palette.stem,
      badgeStyle: {
        color: "#ffffff",
        background: `linear-gradient(135deg, ${palette.head}, ${palette.stem})`,
        border: "1px solid rgba(255, 255, 255, 0.22)",
      },
    };
  }, []);

  const buildMarkupStatusPayload = useCallback((markup, statusValue) => {
    const normalizedStatus = String(statusValue || "open").trim().toLowerCase();

    if (normalizedStatus === "closed") {
      return {
        status: "Closed",
        approved: true,
        actioned: true,
      };
    }

    if (normalizedStatus === "under-review") {
      return {
        status: "Under review",
        approved: true,
        actioned: false,
      };
    }

    return {
      status: "Open",
      approved: false,
      actioned: false,
    };
  }, []);

  const buildPinRecordFromPersistedData = useCallback(
    (persisted, markup) => {
      const markupId = Number(markup?.id);
      if (!markupId || !persisted) {
        return null;
      }

      const pinCandidates = [];
      if (persisted?.pin) {
        pinCandidates.push(persisted.pin);
      }
      if (markup?.pin) {
        pinCandidates.push(markup.pin);
      }
      if (markup?.location) {
        pinCandidates.push(markup.location);
      }

      const effectivePin = pinCandidates
        .map((candidate) => normalizePinPoint(candidate))
        .find(Boolean);

      if (!effectivePin) {
        return null;
      }

      const pinStatus = getMarkupStatusDisplay(markup);

      return {
        id: `${markupId}-0`,
        markupId,
        title: markup?.title || `Markup ${markupId}`,
        userName: markup?.userName || "",
        statusLabel: pinStatus.label,
        pinHeadColor: pinStatus.head,
        pinStemColor: pinStatus.stem,
        ...effectivePin,
      };
    },
    [getMarkupStatusDisplay, normalizePinPoint],
  );

  const resetOverlayState = useCallback(() => {
    setScreenshotUrl((prev) => {
      if (prev?.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    undoStackRef.current = [];
    measureOverlaySnapshotRef.current = null;
    shouldRestoreOverlayRef.current = false;
    pendingMeasureLabelsRef.current = [];
    calloutCounterRef.current = 1;
    draftEntityRef.current = null;
    interactionRef.current = {
      mode: null,
      entityId: null,
      entityIds: null,
      startPoint: null,
      startEntity: null,
      startEntities: null,
      selectionRect: null,
    };
    entitiesRef.current = [];
    selectedEntityIdRef.current = null;
    selectedEntityIdsRef.current = [];
    entityIdCounterRef.current = 1;
    setUndoStack([]);
    setEntities([]);
    setEntitySelection([]);
    setTextEditor({
      visible: false,
      x: 0,
      y: 0,
      value: "",
      entityId: null,
      fontSize: null,
      penColor: null,
      width: null,
      height: null,
      targetType: null,
    });
    setIsMarkupMode(false);
    setIsAwaitingPinPlacement(false);
    setPendingCaptureMultiDiscipline(false);
    setIsMultiDisciplineMode(false);
    setMultiDisciplineUserTagName("");
    setMultiDisciplineUserTagDate("");
    setHoveredEntityId(null);
    setIsMeasuring(false);
    setDockTop(56);
    setDockScale(1);
    setZoomLevel(1);
    setZoomOffset({ x: 0, y: 0 });
    setCaptureCanvasSize({ width: 0, height: 0 });
    setZoomViewportSize({ width: 0, height: 0 });
    setIsSizeMenuOpen(false);
    setPendingImagePlacement(null);
    setImageUploadMode("insert");
    setPendingReplaceImageEntityId(null);
    setActiveTool(DEFAULT_TOOL_ID);
    canvasCursorRef.current = "default";
    setCanvasCursor("default");
    setCurrentMarkupId(null);
    setSaveTitle("");
    setSaveCategory(DEFAULT_MARKUP_CATEGORY);
    setSaveForUsers([]);
    setSaveModalMode("create");
    setSaveModalOpen(false);
    setActiveCapturePin(null);
    setShowAllSavedPins(false);
    setPinPlacementWarningModalOpen(false);
    setSavedMarkupPins([]);
    setSavedMarkupPinsLoading(false);
    setPinLoadStatus("idle");
    setPinLoadStats({ markupCount: 0, pinCount: 0 });
    setPinOverlayFrame(0);
    pinOverlayLastRefreshRef.current = 0;
    loadedMarkupViewerStateRef.current = null;
    setError(null);
  }, [setEntitySelection]);

  useEffect(() => {
    const viewer = viewerState?.viewer;
    if (!isMarkupMode || !viewer) {
      return undefined;
    }

    return () => {
      const restoreMeasureTool = async () => {
        try {
          if (!viewer.getExtension?.("Autodesk.Measure")) {
            const measureExt = await viewer.loadExtension?.("Autodesk.Measure");
            measureExt?.setUnits?.("mm");
          }
        } catch (err) {
          console.warn("Unable to restore the viewer measure tool.", err);
        }
      };

      void restoreMeasureTool();
    };
  }, [isMarkupMode, viewerState?.viewer]);

  useEffect(() => {
    if (!isMarkupMode || isMeasuring) {
      return undefined;
    }

    const updateDockPosition = () => {
      const dock = viewerDockRef.current;
      const parent = dock?.parentElement;
      if (!dock || !parent) {
        return;
      }

      const parentHeight = parent.clientHeight;
      const dockHeight = dock.offsetHeight;
      if (!parentHeight || !dockHeight) {
        return;
      }

      const maxUsableHeight = Math.max(1, parentHeight - 20);
      const nextScale = Math.min(1, maxUsableHeight / dockHeight);
      const scaledDockHeight = dockHeight * nextScale;

      const minTop = 10;
      const maxTop = Math.max(minTop, parentHeight - scaledDockHeight - 10);
      const centeredTop = (parentHeight - scaledDockHeight) / 2;
      const nextTop = Math.max(minTop, Math.min(maxTop, centeredTop));

      setDockScale((prev) =>
        Math.abs(prev - nextScale) < 0.005
          ? prev
          : Number(nextScale.toFixed(3)),
      );
      setDockTop((prev) =>
        Math.abs(prev - nextTop) < 0.5 ? prev : Number(nextTop.toFixed(2)),
      );
    };

    updateDockPosition();
    window.addEventListener("resize", updateDockPosition);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateDockPosition();
      });

      if (viewerDockRef.current) {
        resizeObserver.observe(viewerDockRef.current);
      }
      if (viewerDockRef.current?.parentElement) {
        resizeObserver.observe(viewerDockRef.current.parentElement);
      }
    }

    return () => {
      window.removeEventListener("resize", updateDockPosition);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isMarkupMode, isMeasuring]);

  useEffect(() => {
    if (!isMarkupMode || isMeasuring) {
      return undefined;
    }

    const viewport = zoomViewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const updateViewportSize = () => {
      const nextWidth = Number(viewport.clientWidth || 0);
      const nextHeight = Number(viewport.clientHeight || 0);
      setZoomViewportSize((prev) => {
        if (prev.width === nextWidth && prev.height === nextHeight) {
          return prev;
        }
        return { width: nextWidth, height: nextHeight };
      });
    };

    updateViewportSize();
    const rafId = window.requestAnimationFrame(updateViewportSize);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateViewportSize);
      resizeObserver.observe(viewport);
    }

    window.addEventListener("resize", updateViewportSize);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateViewportSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isMarkupMode, isMeasuring]);

  const fittedCanvasFrame = useMemo(() => {
    const viewportWidth = Number(zoomViewportSize.width || 0);
    const viewportHeight = Number(zoomViewportSize.height || 0);
    const sourceWidth = Number(captureCanvasSize.width || 0);
    const sourceHeight = Number(captureCanvasSize.height || 0);

    if (!viewportWidth || !viewportHeight) {
      return {
        width: 0,
        height: 0,
        left: 0,
        top: 0,
      };
    }

    const aspectRatio =
      sourceWidth > 0 && sourceHeight > 0
        ? sourceWidth / sourceHeight
        : MARKUP_DEFAULT_ASPECT_RATIO;

    let width = viewportWidth;
    let height = width / aspectRatio;

    if (height > viewportHeight) {
      height = viewportHeight;
      width = height * aspectRatio;
    }

    const safeWidth = Math.max(1, Math.floor(width));
    const safeHeight = Math.max(1, Math.floor(height));

    return {
      width: safeWidth,
      height: safeHeight,
      left: Math.floor((viewportWidth - safeWidth) / 2),
      top: Math.floor((viewportHeight - safeHeight) / 2),
    };
  }, [
    captureCanvasSize.height,
    captureCanvasSize.width,
    zoomViewportSize.height,
    zoomViewportSize.width,
  ]);

  const normalizeCreatedFor = useCallback((createdFor) => {
    if (!createdFor) {
      return [];
    }
    if (Array.isArray(createdFor)) {
      return createdFor.filter(Boolean);
    }
    if (typeof createdFor === "string") {
      return createdFor
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
    }
    return [];
  }, []);

  const serializeMarkupData = useCallback((data) => {
    try {
      return JSON.stringify(data);
    } catch (err) {
      return null;
    }
  }, []);

  const decodeUint8ArrayToString = useCallback((bytes) => {
    if (!bytes || !bytes.length) {
      return "";
    }
    try {
      return new TextDecoder().decode(new Uint8Array(bytes));
    } catch (err) {
      let result = "";
      for (let i = 0; i < bytes.length; i += 1) {
        result += String.fromCharCode(bytes[i]);
      }
      return result;
    }
  }, []);

  const parsePersistedMarkupData = useCallback(
    (rawData) => {
      const parseMarkupBytes = (bytes) => {
        const decoded = decodeUint8ArrayToString(bytes);
        try {
          return JSON.parse(decoded);
        } catch (jsonErr) {
          try {
            return JSON.parse(
              pako.inflate(new Uint8Array(bytes), { toText: true }),
            );
          } catch (inflateErr) {
            return null;
          }
        }
      };

      if (!rawData) {
        return null;
      }

      if (typeof rawData === "string") {
        try {
          return JSON.parse(rawData);
        } catch (err) {
          return null;
        }
      }

      if (rawData?.type === "Buffer" && Array.isArray(rawData?.data)) {
        return parseMarkupBytes(rawData.data);
      }

      if (Array.isArray(rawData)) {
        return parseMarkupBytes(rawData);
      }

      if (
        rawData instanceof ArrayBuffer ||
        Object.prototype.toString.call(rawData) === "[object ArrayBuffer]"
      ) {
        return parseMarkupBytes(Array.from(new Uint8Array(rawData)));
      }

      if (ArrayBuffer.isView(rawData)) {
        return parseMarkupBytes(
          Array.from(
            new Uint8Array(rawData.buffer, rawData.byteOffset, rawData.byteLength),
          ),
        );
      }

      if (
        typeof rawData === "object" &&
        !Array.isArray(rawData) &&
        Object.keys(rawData).length > 0 &&
        Object.keys(rawData).every((key) => /^\d+$/.test(key))
      ) {
        return parseMarkupBytes(
          Object.keys(rawData)
            .sort((left, right) => Number(left) - Number(right))
            .map((key) => rawData[key]),
        );
      }

      if (typeof rawData === "object" && !Array.isArray(rawData)) {
        return rawData;
      }

      return null;
    },
    [decodeUint8ArrayToString],
  );

  const parseMarkupViewerState = useCallback(
    (rawState) => {
      if (!rawState) {
        return null;
      }

      if (typeof rawState === "string") {
        try {
          return JSON.parse(rawState);
        } catch (err) {
          return null;
        }
      }

      if (rawState?.type === "Buffer" && Array.isArray(rawState?.data)) {
        const decoded = decodeUint8ArrayToString(rawState.data);
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (Array.isArray(rawState)) {
        const decoded = decodeUint8ArrayToString(rawState);
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (typeof rawState === "object") {
        return rawState;
      }

      return null;
    },
    [decodeUint8ArrayToString],
  );

  const getMarkupSheetKey = useCallback(
    (markup) => {
      if (!markup || typeof markup !== "object") {
        return "";
      }

      const directSheetKey = normalizeSheetKeyValue(markup.sheetKey);
      if (directSheetKey) {
        return directSheetKey;
      }

      const directSheetId = normalizeSheetKeyValue(
        markup.sheetId ?? markup.selectedSheetId ?? markup.dbId,
      );
      if (directSheetId) {
        return directSheetId;
      }

      const directSheetName = normalizeSheetKeyValue(
        markup.sheetName ?? markup.selectedSheetName,
      );
      if (directSheetName) {
        return directSheetName;
      }

      const persisted = parsePersistedMarkupData(markup.data);
      if (!persisted || typeof persisted !== "object") {
        return "";
      }

      const persistedSheetKey = normalizeSheetKeyValue(persisted.sheetKey);
      if (persistedSheetKey) {
        return persistedSheetKey;
      }

      const persistedSheetId = normalizeSheetKeyValue(
        persisted.sheetId ?? persisted.selectedSheetId ?? persisted.sheet?.id,
      );
      if (persistedSheetId) {
        return persistedSheetId;
      }

      return normalizeSheetKeyValue(
        persisted.sheetName ??
          persisted.selectedSheetName ??
          persisted.sheet?.sheetName ??
          persisted.sheet?.name,
      );
    },
    [normalizeSheetKeyValue, parsePersistedMarkupData],
  );

  const parseEntityNumericId = useCallback((rawId) => {
    const normalizedId = String(rawId || "").trim();
    if (!normalizedId) {
      return null;
    }

    const trailingNumberMatch = normalizedId.match(/(\d+)(?!.*\d)/);
    if (!trailingNumberMatch) {
      return null;
    }

    const parsedNumber = Number(trailingNumberMatch[1]);
    return Number.isInteger(parsedNumber) && parsedNumber > 0
      ? parsedNumber
      : null;
  }, []);

  const normalizeLoadedEntities = useCallback(
    (rawEntities) => {
      if (!Array.isArray(rawEntities) || !rawEntities.length) {
        return {
          entities: [],
          nextEntityCounter: 1,
        };
      }

      const normalizedEntities = [];
      const seenIds = new Set();
      let maxNumericId = 0;

      rawEntities.forEach((candidate) => {
        if (!candidate || typeof candidate !== "object") {
          return;
        }

        const clonedEntity = JSON.parse(JSON.stringify(candidate));
        let entityId = String(clonedEntity.id || "").trim();

        const parsedExistingId = parseEntityNumericId(entityId);
        if (Number.isInteger(parsedExistingId)) {
          maxNumericId = Math.max(maxNumericId, parsedExistingId);
        }

        if (!entityId || seenIds.has(entityId)) {
          let generatedId = Math.max(1, maxNumericId + 1);
          while (seenIds.has(`entity-${generatedId}`)) {
            generatedId += 1;
          }

          entityId = `entity-${generatedId}`;
          maxNumericId = Math.max(maxNumericId, generatedId);
        }

        clonedEntity.id = entityId;
        seenIds.add(entityId);
        normalizedEntities.push(clonedEntity);
      });

      return {
        entities: normalizedEntities,
        nextEntityCounter: Math.max(1, maxNumericId + 1),
      };
    },
    [parseEntityNumericId],
  );

  const clearViewerSectionArtifacts = useCallback(async (viewer) => {
    if (!viewer) {
      return;
    }

    const runSectionToolExitCycle = async () => {
      let sectionExtension = viewer.getExtension?.("Autodesk.Section");

      if (!sectionExtension && typeof viewer.loadExtension === "function") {
        try {
          sectionExtension = await viewer.loadExtension("Autodesk.Section");
        } catch (err) {
          sectionExtension = null;
        }
      }

      try {
        sectionExtension?.activate?.("X");
      } catch (err) {
        // Ignore if mode is unsupported in this viewer build.
      }

      sectionExtension?.deactivate?.();
      sectionExtension?.clearSection?.();

      viewer.toolController?.deactivateTool?.("section");
      viewer.toolController?.deactivateTool?.("sectionBox");
      viewer.toolController?.deactivateTool?.("sectionbox");
    };

    const clearOnce = async () => {
      await runSectionToolExitCycle();

      viewer.setCutPlanes?.([]);
      viewer.setCutPlanes?.(null);
      viewer.impl?.setCutPlanes?.([]);
      viewer.impl?.setCutPlanes?.(null);

      await runSectionToolExitCycle();

      viewer.impl?.sceneUpdated?.(true);
      viewer.impl?.invalidate?.(true, true, true);
    };

    await clearOnce();

    await new Promise((resolve) => {
      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        window.requestAnimationFrame(() => resolve());
        return;
      }

      setTimeout(resolve, 0);
    });

    await clearOnce();
  }, []);

  const projectId = useMemo(() => {
    const project = selectedProject?.[0];
    if (!project?.id) {
      return null;
    }
    const idParts = String(project.id).split("/");
    return idParts[idParts.length - 1] || null;
  }, [selectedProject]);

  const mviewProjectId = useMemo(() => {
    const project = selectedProject?.[0];
    const candidate =
      project?.mviewProjectInfo?.id ?? project?.mviewProject?.id ?? null;

    if (candidate == null) {
      return null;
    }

    const normalized = String(candidate).trim();
    return normalized || null;
  }, [selectedProject]);

  const postChunkedCompressedMarkup = useCallback(
    async (endpointPath, compressedPayload, uploadScope = "markup") => {
      const payloadLength = Number(compressedPayload?.length || 0);

      if (!payloadLength) {
        throw new Error("No payload provided for upload");
      }

      const totalChunks = Math.max(
        1,
        Math.ceil(payloadLength / CHUNK_SIZE_BYTES),
      );
      const uploadId = `${uploadScope}-${String(projectId || "project")}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
      let finalResponse = null;

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
        const start = chunkIndex * CHUNK_SIZE_BYTES;
        const end = Math.min(start + CHUNK_SIZE_BYTES, payloadLength);
        const chunk = compressedPayload.subarray(start, end);
        // JSON + base64 avoids WAF rules blocking application/octet-stream
        // and text/plain uploads, without altering the underlying markup data.
        const chunkBase64 = encodeBytesToBase64(chunk);
        const isFinalChunk = chunkIndex === totalChunks - 1;

        finalResponse = await axios({
          method: "post",
          url: `${HOST_URL}${endpointPath}`,
          data: { data: chunkBase64 },
          headers: {
            "Content-Type": "application/json",
            "X-Upload-Id": uploadId,
            "X-Chunk-Index": String(chunkIndex),
            "X-Chunk-Count": String(totalChunks),
            "X-Chunk-Size": String(chunk.length),
            "X-Original-Size": String(payloadLength),
            "X-Is-Final-Chunk": String(isFinalChunk),
            "X-Compression": "deflate",
            "X-Chunk-Encoding": "base64url",
          },
          withCredentials: true,
        });
      }

      return finalResponse;
    },
    [projectId],
  );

  const postChunkedCompressedMarkupUpdate = useCallback(
    async (payload, uploadScope = "updateMarkup") => {
      const compressedPayload = pako.deflate(JSON.stringify(payload));
      return postChunkedCompressedMarkup(
        "/markups/updateMarkup",
        compressedPayload,
        uploadScope,
      );
    },
    [postChunkedCompressedMarkup],
  );

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const cached = SAVED_MARKUPS_SESSION_CACHE.get(String(projectId));
    if (Array.isArray(cached) && cached.length) {
      setSavedMarkups(sortMarkupsNewestFirst(cached));
    }
  }, [projectId]);

  const normalizeMarkupIdentifier = useCallback((markup) => {
    if (!markup || typeof markup !== "object") {
      return null;
    }

    const identifierCandidates = [
      markup.id,
      markup.markupId,
      markup._id,
      markup.markup?.id,
      markup.data?.id,
      markup.data?.markupId,
    ];

    for (const candidate of identifierCandidates) {
      if (candidate == null) {
        continue;
      }

      const normalizedCandidate = String(candidate).trim();
      if (!normalizedCandidate) {
        continue;
      }

      const numericCandidate = Number(normalizedCandidate);
      if (Number.isInteger(numericCandidate) && numericCandidate > 0) {
        return numericCandidate;
      }

      return normalizedCandidate;
    }

    return null;
  }, []);

  const normalizeMarkupIdentity = useCallback(
    (markup) => {
      if (!markup || typeof markup !== "object") {
        return markup;
      }

      const normalizedId = normalizeMarkupIdentifier(markup);
      if (normalizedId == null) {
        return markup;
      }

      return {
        ...markup,
        id: normalizedId,
      };
    },
    [normalizeMarkupIdentifier],
  );

  const normalizeMarkupsPayload = useCallback((response) => {
    const data = response?.data;
    if (Array.isArray(data)) {
      return data.map((item) => normalizeMarkupIdentity(item));
    }
    if (Array.isArray(data?.data)) {
      return data.data.map((item) => normalizeMarkupIdentity(item));
    }
    return [];
  }, [normalizeMarkupIdentity]);

  const normalizeMarkupByIdPayload = useCallback(
    (response) => {
      const decodePayloadCandidate = (candidate) => {
        if (!candidate) {
          return null;
        }

        if (typeof candidate === "string") {
          try {
            return JSON.parse(candidate);
          } catch (err) {
            return null;
          }
        }

        if (candidate?.type === "Buffer" && Array.isArray(candidate?.data)) {
          const decoded = decodeUint8ArrayToString(candidate.data);
          try {
            return JSON.parse(decoded);
          } catch (err) {
            return null;
          }
        }

        if (Array.isArray(candidate)) {
          const decoded = decodeUint8ArrayToString(candidate);
          try {
            return JSON.parse(decoded);
          } catch (err) {
            return null;
          }
        }

        if (candidate instanceof ArrayBuffer) {
          const decoded = decodeUint8ArrayToString(
            Array.from(new Uint8Array(candidate)),
          );
          try {
            return JSON.parse(decoded);
          } catch (err) {
            return null;
          }
        }

        if (ArrayBuffer.isView(candidate)) {
          const decoded = decodeUint8ArrayToString(Array.from(candidate));
          try {
            return JSON.parse(decoded);
          } catch (err) {
            return null;
          }
        }

        if (typeof candidate === "object" && !Array.isArray(candidate)) {
          return candidate;
        }

        return null;
      };

      const looksLikeMarkupRecord = (candidate) => {
        if (
          !candidate ||
          typeof candidate !== "object" ||
          Array.isArray(candidate)
        ) {
          return false;
        }

        const recordKeys = [
          "id",
          "title",
          "markupTitle",
          "imagePreview",
          "imageUrl",
          "markupState",
          "category",
          "userName",
          "created",
          "createdFor",
          "pin",
          "data",
          "markupData",
        ];

        return recordKeys.some((key) =>
          Object.prototype.hasOwnProperty.call(candidate, key),
        );
      };

      const data = decodePayloadCandidate(response?.data);
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        return null;
      }

      if (looksLikeMarkupRecord(data)) {
        return normalizeMarkupIdentity(data);
      }

      const nestedParsed = decodePayloadCandidate(data.data);
      if (
        nestedParsed &&
        typeof nestedParsed === "object" &&
        !Array.isArray(nestedParsed)
      ) {
        if (looksLikeMarkupRecord(nestedParsed)) {
          return nestedParsed;
        }

        const wrapperKeys = ["data", "status", "success", "message", "error"];
        const isWrapper =
          Object.keys(data).length <= 6 &&
          Object.keys(data).every((key) => wrapperKeys.includes(key));

        if (isWrapper) {
          return normalizeMarkupIdentity(nestedParsed);
        }
      }

      return normalizeMarkupIdentity(data);
    },
    [decodeUint8ArrayToString, normalizeMarkupIdentity],
  );

  const normalizeMarkupPreview = useCallback((rawPreview) => {
    if (!rawPreview || typeof rawPreview !== "string") {
      return null;
    }

    if (rawPreview.startsWith("data:image/")) {
      return rawPreview;
    }

    if (rawPreview.startsWith("data:")) {
      try {
        const decoded = atob(rawPreview.split(",")[1] || "");
        if (decoded.startsWith("data:image/")) {
          return decoded;
        }
      } catch (err) {
        return null;
      }
    }

    return null;
  }, []);
  const normalizeLegacyImageDataUrl = useCallback((rawValue) => {
    if (!rawValue) {
      return null;
    }

    if (typeof rawValue === "object") {
      const getRawBytes = (value) => {
        if (value instanceof ArrayBuffer) {
          return Array.from(new Uint8Array(value));
        }
        if (ArrayBuffer.isView(value)) {
          return Array.from(
            new Uint8Array(value.buffer, value.byteOffset, value.byteLength),
          );
        }
        if (Array.isArray(value)) {
          return value;
        }
        if (value?.type === "Buffer") {
          return getRawBytes(value.data);
        }
        if (
          value &&
          typeof value === "object" &&
          Object.keys(value).some((key) => /^\d+$/.test(key))
        ) {
          return Object.keys(value)
            .filter((key) => /^\d+$/.test(key))
            .sort((left, right) => Number(left) - Number(right))
            .map((key) => value[key]);
        }
        return null;
      };
      const rawBytes = getRawBytes(rawValue);

      if (!rawBytes?.length) {
        return null;
      }

      const bytes = Uint8Array.from(rawBytes);
      const decoded = decodeUint8ArrayToString(bytes).trim();
      if (decoded.startsWith("data:")) {
        return normalizeLegacyImageDataUrl(decoded);
      }

      let mimeType = null;
      if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e) {
        mimeType = "image/png";
      } else if (bytes[0] === 0xff && bytes[1] === 0xd8) {
        mimeType = "image/jpeg";
      } else if (
        bytes[0] === 0x47 &&
        bytes[1] === 0x49 &&
        bytes[2] === 0x46
      ) {
        mimeType = "image/gif";
      }

      if (mimeType) {
        let binary = "";
        const chunkSize = 0x8000;
        for (let index = 0; index < bytes.length; index += chunkSize) {
          binary += String.fromCharCode(
            ...bytes.subarray(index, Math.min(index + chunkSize, bytes.length)),
          );
        }
        return `data:${mimeType};base64,${btoa(binary)}`;
      }

      return null;
    }

    if (typeof rawValue !== "string") {
      return null;
    }

    const trimmedValue = rawValue.trim();
    if (trimmedValue.startsWith("{") || trimmedValue.startsWith("[")) {
      try {
        const parsedValue = JSON.parse(trimmedValue);
        if (parsedValue && parsedValue !== rawValue) {
          return normalizeLegacyImageDataUrl(parsedValue);
        }
      } catch (parseErr) {
        // Continue treating the value as a regular image source.
      }
    }

    if (
      trimmedValue.startsWith("data:image/") ||
      trimmedValue.startsWith("blob:") ||
      /^https?:\/\//i.test(trimmedValue)
    ) {
      return trimmedValue;
    }

    if (trimmedValue.startsWith("data:")) {
      try {
        const decoded = atob(trimmedValue.split(",")[1] || "");
        if (decoded.startsWith("data:image/")) {
          return decoded;
        }
      } catch (err) {
        return null;
      }
    }

    return null;
  }, [decodeUint8ArrayToString]);

  const drawLegacyPersistedEntity = useCallback((ctx, entity) => {
    const from = entity?.from || { x: 0, y: 0 };
    const to = entity?.to || { x: 0, y: 0 };
    const points = Array.isArray(entity?.points) ? entity.points : [];
    const stroke =
      entity?.penColor || entity?.strokeColor || entity?.color || "#ff2e2e";
    const fillEnabled = Boolean(entity?.fillEnabled);
    const fillColor = entity?.fillColor || entity?.fill || "transparent";
    const fill = fillEnabled ? fillColor : "transparent";
    const lineWidth = Number(
      entity?.penSize || entity?.size || entity?.lineWidth || 2,
    );
    const fontSize = Number(entity?.fontSize || 16);

    ctx.save();
    ctx.strokeStyle = stroke;
    ctx.fillStyle = fill;
    ctx.lineWidth = Number.isFinite(lineWidth) ? lineWidth : 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const left = Math.min(from.x || 0, to.x || 0);
    const top = Math.min(from.y || 0, to.y || 0);
    const width = Math.abs((to.x || 0) - (from.x || 0));
    const height = Math.abs((to.y || 0) - (from.y || 0));
    const type = String(entity?.type || "").toLowerCase();

    switch (type) {
      case "line":
      case "arrow":
      case "cross":
      case "highlight":
      case "redact":
        ctx.beginPath();
        ctx.moveTo(from.x || 0, from.y || 0);
        ctx.lineTo(to.x || 0, to.y || 0);
        ctx.stroke();
        break;
      case "rectangle":
      case "rect":
        if (fill !== "transparent") {
          ctx.fillRect(left, top, width, height);
        }
        ctx.strokeRect(left, top, width, height);
        break;
      case "ellipse":
        ctx.beginPath();
        ctx.ellipse(
          left + width / 2,
          top + height / 2,
          Math.max(1, width / 2),
          Math.max(1, height / 2),
          0,
          0,
          Math.PI * 2,
        );
        if (fill !== "transparent") {
          ctx.fill();
        }
        ctx.stroke();
        break;
      case "polyline":
      case "freehand":
      case "cloud":
      case "triangle":
        if (points.length) {
          ctx.beginPath();
          ctx.moveTo(points[0].x || 0, points[0].y || 0);
          for (let index = 1; index < points.length; index += 1) {
            ctx.lineTo(points[index].x || 0, points[index].y || 0);
          }
          if (type === "triangle") {
            ctx.closePath();
            if (fill !== "transparent") {
              ctx.fill();
            }
          }
          ctx.stroke();
        }
        break;
      case "text":
      case "callout":
      case "rect-callout": {
        const text = String(entity?.text || "").trim();
        if (text) {
          ctx.fillStyle = stroke;
          ctx.font = `${Math.max(10, fontSize)}px Arial`;
          ctx.textBaseline = "top";
          ctx.fillText(text, from.x || left, from.y || top);
        }
        break;
      }
      default:
        break;
    }

    ctx.restore();
  }, []);

  const fetchSavedMarkups = useCallback(async () => {
    if (!projectId) {
      setSavedMarkups([]);
      return [];
    }

    setSavedMarkupsLoading(true);
    try {
      const params = {
        projectId,
        projectAsset: "Everything",
      };

      let response;
      try {
        response = await axios({
          method: "get",
          url: `${HOST_URL}/markups/getMarkups`,
          params,
          withCredentials: true,
        });
      } catch (getErr) {
        const formData = new FormData();
        formData.append("formData", JSON.stringify(params));
        response = await axios({
          method: "post",
          url: `${HOST_URL}/markups/getMarkups`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        });
      }

      const allMarkups = normalizeMarkupsPayload(response);
      const sortedIncoming = sortMarkupsNewestFirst(allMarkups);

      setSavedMarkups((prev) => {
        const projectChanged = lastSavedMarkupProjectIdRef.current !== projectId;
        lastSavedMarkupProjectIdRef.current = projectId;

        if (projectChanged || !Array.isArray(prev) || !prev.length) {
          SAVED_MARKUPS_SESSION_CACHE.set(String(projectId), sortedIncoming);
          return sortedIncoming;
        }

        if (!lockSavedMarkupListRef.current) {
          // Same project: merge to avoid unintended list narrowing from transient
          // server-side asset scoping during pin placement / viewer interactions.
          const mergedById = new Map();
          prev.forEach((item) => {
            if (item?.id != null) {
              mergedById.set(String(item.id), item);
            }
          });
          sortedIncoming.forEach((item) => {
            if (item?.id != null) {
              mergedById.set(String(item.id), item);
            }
          });

          const merged = sortMarkupsNewestFirst(Array.from(mergedById.values()));
          SAVED_MARKUPS_SESSION_CACHE.set(String(projectId), merged);
          return merged;
        }

        const mergedById = new Map();
        prev.forEach((item) => {
          if (item?.id != null) {
            mergedById.set(String(item.id), item);
          }
        });
        sortedIncoming.forEach((item) => {
          if (item?.id != null) {
            mergedById.set(String(item.id), item);
          }
        });

        const merged = sortMarkupsNewestFirst(Array.from(mergedById.values()));
        SAVED_MARKUPS_SESSION_CACHE.set(String(projectId), merged);
        return merged;
      });

      return sortedIncoming;
    } catch (err) {
      setError("Unable to load saved markups.");
      setSavedMarkups([]);
      return [];
    } finally {
      setSavedMarkupsLoading(false);
    }
  }, [normalizeMarkupsPayload, projectId]);

  const extractSavedMarkupIdFromResponse = useCallback((response) => {
    const candidates = [
      response?.data?.id,
      response?.data?.markupId,
      response?.data?.markup?.id,
      response?.data?.data?.id,
      response?.data?.data?.markupId,
    ];

    for (const candidate of candidates) {
      const numericId = Number(candidate);
      if (Number.isInteger(numericId) && numericId > 0) {
        return numericId;
      }
    }

    return null;
  }, []);

  // Seeds preview state for the current filtered list from cache, without
  // fetching. Actual fetches are triggered lazily by IntersectionObserver
  // as cards scroll into (or near) view, so any card can trigger a load
  // as soon as it's visible instead of only once the list bottoms out.
  useEffect(() => {
    setSavedMarkupPreviews(
      filteredSavedMarkups.map((markup) => {
        const cachedPreview = savedMarkupPreviewCacheRef.current.get(
          markup.id,
        );
        if (cachedPreview) {
          return { ...cachedPreview, id: markup.id };
        }
        return {
          id: markup.id,
          imagePreview: null,
          imageLoading: false,
          imageDeferred: true,
          imageOptimizing: false,
          imageError: false,
        };
      }),
    );
  }, [filteredSavedMarkups]);

  const requestSavedMarkupPreview = useCallback(
    async (markup) => {
      const markupId = markup?.id;
      if (!markupId) {
        return;
      }

      const cachedPreview = savedMarkupPreviewCacheRef.current.get(markupId);
      const alreadyResolved =
        cachedPreview &&
        !cachedPreview.imageOptimizing &&
        (cachedPreview.imagePreview || cachedPreview.imageError);
      if (alreadyResolved || savedMarkupPreviewInFlightRef.current.has(markupId)) {
        return;
      }

      savedMarkupPreviewInFlightRef.current.add(markupId);
      setSavedMarkupPreviews((prev) =>
        prev.map((item) =>
          item.id === markupId
            ? { ...item, imageLoading: true, imageDeferred: false }
            : item,
        ),
      );

      let result;
      try {
        const res = await axios.get(
          `${HOST_URL}/markups/getMarkupPreview/${markupId}`,
          { responseType: "text", withCredentials: true },
        );
        const imagePreview = normalizeMarkupPreview(res.data);
        result = {
          id: markupId,
          imagePreview,
          imageLoading: false,
          imageDeferred: false,
          imageOptimizing: false,
          imageError: !imagePreview,
        };
      } catch (err) {
        result = {
          id: markupId,
          imagePreview: null,
          imageLoading: false,
          imageDeferred: false,
          imageOptimizing: false,
          imageError: true,
        };
      }

      savedMarkupPreviewInFlightRef.current.delete(markupId);
      savedMarkupPreviewCacheRef.current.set(markupId, result);
      setSavedMarkupPreviews((prev) =>
        prev.map((item) => (item.id === markupId ? { ...item, ...result } : item)),
      );
    },
    [normalizeMarkupPreview],
  );

  const filteredSavedMarkupsRef = useRef(filteredSavedMarkups);
  filteredSavedMarkupsRef.current = filteredSavedMarkups;

  // Registers/unregisters each card's preview node with a shared
  // IntersectionObserver so previews load as soon as a card scrolls
  // into (or near) the visible area, in either scroll direction.
  const registerSavedMarkupPreviewNode = useCallback((markupId) => (node) => {
    const nodesMap = savedMarkupPreviewNodesRef.current;
    const observer = savedMarkupPreviewObserverRef.current;
    const prevNode = nodesMap.get(markupId);

    if (prevNode && prevNode !== node && observer) {
      observer.unobserve(prevNode);
    }

    if (node) {
      nodesMap.set(markupId, node);
      if (observer) {
        observer.observe(node);
      }
    } else {
      nodesMap.delete(markupId);
    }
  }, []);

  useEffect(() => {
    const rootEl = savedMarkupListRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const markupId = Number(entry.target.dataset.markupId);
          const markup = filteredSavedMarkupsRef.current.find(
            (item) => item.id === markupId,
          );
          if (markup) {
            requestSavedMarkupPreview(markup);
          }
          observer.unobserve(entry.target);
        });
      },
      {
        root: rootEl,
        // Preload previews slightly before they enter the visible area.
        rootMargin: "400px 0px",
        threshold: 0.01,
      },
    );

    savedMarkupPreviewObserverRef.current = observer;
    savedMarkupPreviewNodesRef.current.forEach((node) => observer.observe(node));

    return () => {
      observer.disconnect();
      savedMarkupPreviewObserverRef.current = null;
    };
  }, [filteredSavedMarkups, requestSavedMarkupPreview]);


  const removeMarkupFromLocalState = useCallback(
    (markupId) => {
      const normalizedMarkupId = String(markupId);
      setSavedMarkups((prev) =>
        prev.filter((item) => String(item?.id) !== normalizedMarkupId),
      );
      setSavedMarkupPreviews((prev) =>
        prev.filter((preview) => String(preview?.id) !== normalizedMarkupId),
      );
      savedMarkupPreviewCacheRef.current.delete(markupId);
      savedMarkupPinCacheRef.current.delete(markupId);
      setSavedMarkupPins((prev) =>
        prev.filter((pin) => String(pin?.markupId) !== normalizedMarkupId),
      );

      if (String(currentMarkupId) === normalizedMarkupId) {
        resetOverlayState();
      }
    },
    [currentMarkupId, resetOverlayState],
  );

  const archiveMarkup = useCallback(
    async (markup) => {
      const markupId = Number(markup?.id);
      if (!Number.isInteger(markupId) || markupId <= 0) {
        return false;
      }

      if (!canDeleteMarkup(markup)) {
        setError("Only the markup owner or project admin can archive markups.");
        return false;
      }

      setArchivingMarkupId(markupId);
      setError(null);

      try {
        await postChunkedCompressedMarkupUpdate(
          {
            id: markupId,
            archived: true,
            archivedBy: userData?.emailId || userDetails?.email || null,
          },
          "archiveMarkup",
        );

        const archivedBy = userData?.emailId || userDetails?.email || null;
        setSavedMarkups((prev) => {
          const updated = prev.map((item) =>
            String(item?.id) === String(markupId)
              ? {
                  ...item,
                  archived: true,
                  archivedBy,
                }
              : item,
          );
          SAVED_MARKUPS_SESSION_CACHE.set(String(projectId), updated);
          return updated;
        });
        return true;
      } catch (err) {
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setError(
          serverMessage
            ? `Unable to archive markup. ${serverMessage}`
            : "Unable to archive markup.",
        );
        return false;
      } finally {
        setArchivingMarkupId(null);
      }
    },
    [
      canDeleteMarkup,
      postChunkedCompressedMarkupUpdate,
      projectId,
      userData,
      userDetails,
    ],
  );

  const openArchiveConfirmModal = useCallback((markup) => {
    setPendingArchiveMarkup(markup || null);
    setArchiveConfirmModalOpen(true);
  }, []);

  const closeArchiveConfirmModal = useCallback(() => {
    if (archivingMarkupId) {
      return;
    }
    setArchiveConfirmModalOpen(false);
    setPendingArchiveMarkup(null);
  }, [archivingMarkupId]);

  const confirmArchiveMarkup = useCallback(async () => {
    if (!pendingArchiveMarkup) {
      return;
    }

    const archived = await archiveMarkup(pendingArchiveMarkup);
    if (archived) {
      setArchiveConfirmModalOpen(false);
      setPendingArchiveMarkup(null);
    }
  }, [archiveMarkup, pendingArchiveMarkup]);

  const unarchiveMarkup = useCallback(
    async (markup) => {
      const markupId = Number(markup?.id);
      if (!Number.isInteger(markupId) || markupId <= 0) {
        return;
      }

      if (!canDeleteMarkup(markup)) {
        setError("Only the markup owner or project admin can unarchive markups.");
        return;
      }

      const confirmed = window.confirm(
        `Unarchive markup "${markup.title || markup.id}"?`,
      );
      if (!confirmed) {
        return;
      }

      setUnarchivingMarkupId(markupId);
      setError(null);

      try {
        await postChunkedCompressedMarkupUpdate(
          {
            id: markupId,
            archived: false,
            archivedBy: null,
          },
          "unarchiveMarkup",
        );

        setSavedMarkups((prev) =>
          prev.map((item) =>
            item.id === markupId
              ? {
                  ...item,
                  archived: false,
                  archivedBy: null,
                }
              : item,
          ),
        );
      } catch (err) {
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setError(
          serverMessage
            ? `Unable to unarchive markup. ${serverMessage}`
            : "Unable to unarchive markup.",
        );
      } finally {
        setUnarchivingMarkupId(null);
      }
    },
    [canDeleteMarkup, postChunkedCompressedMarkupUpdate],
  );

  const deleteArchivedMarkup = useCallback(
    async (markup) => {
      const markupIdentifier = normalizeMarkupIdentifier(markup);
      if (!markupIdentifier) {
        return false;
      }

      if (!canDeleteMarkup(markup)) {
        setError("Only the markup owner or project admin can delete markups.");
        return false;
      }

      setDeletingArchivedMarkupId(markupIdentifier);
      setError(null);

      try {
        const numericMarkupId = Number(markupIdentifier);
        const requestPayload =
          Number.isInteger(numericMarkupId) && numericMarkupId > 0
            ? { id: numericMarkupId }
            : { id: markupIdentifier };
        const formData = new FormData();
        formData.append("formData", JSON.stringify(requestPayload));

        await axios({
          method: "post",
          url: `${HOST_URL}/markups/deleteMarkup`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        });

        removeMarkupFromLocalState(markupIdentifier);
        return true;
      } catch (err) {
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setError(
          serverMessage
            ? `Unable to delete archived markup. ${serverMessage}`
            : "Unable to delete archived markup.",
        );
        return false;
      } finally {
        setDeletingArchivedMarkupId(null);
      }
    },
    [
      canDeleteMarkup,
      normalizeMarkupIdentifier,
      removeMarkupFromLocalState,
    ],
  );

  const openDeleteConfirmModal = useCallback((markup) => {
    setPendingDeleteMarkup(markup || null);
    setDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal = useCallback(() => {
    if (deletingArchivedMarkupId) {
      return;
    }
    setDeleteConfirmModalOpen(false);
    setPendingDeleteMarkup(null);
  }, [deletingArchivedMarkupId]);

  const confirmDeleteMarkup = useCallback(async () => {
    if (!pendingDeleteMarkup) {
      return;
    }

    const deleted = await deleteArchivedMarkup(pendingDeleteMarkup);
    if (deleted) {
      setDeleteConfirmModalOpen(false);
      setPendingDeleteMarkup(null);
    }
  }, [deleteArchivedMarkup, pendingDeleteMarkup]);

  const getCompositedMarkupDataUrl = useCallback((maxDimension = null) => {
    const imageCanvas = imageCanvasRef.current;
    const drawCanvas = drawCanvasRef.current;
    const entityCanvas = entityCanvasRef.current;
    if (!imageCanvas || !drawCanvas || !entityCanvas) {
      return null;
    }

    const sourceWidth = imageCanvas.width;
    const sourceHeight = imageCanvas.height;
    if (!sourceWidth || !sourceHeight) {
      return null;
    }

    const largestDimension = Math.max(sourceWidth, sourceHeight);
    const scale =
      maxDimension && largestDimension > maxDimension
        ? maxDimension / largestDimension
        : 1;

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = Math.max(1, Math.round(sourceWidth * scale));
    exportCanvas.height = Math.max(1, Math.round(sourceHeight * scale));
    const exportCtx = exportCanvas.getContext("2d");
    exportCtx.drawImage(
      imageCanvas,
      0,
      0,
      exportCanvas.width,
      exportCanvas.height,
    );
    exportCtx.drawImage(
      drawCanvas,
      0,
      0,
      exportCanvas.width,
      exportCanvas.height,
    );
    exportCtx.drawImage(
      entityCanvas,
      0,
      0,
      exportCanvas.width,
      exportCanvas.height,
    );
    return exportCanvas.toDataURL(
      SAVED_MARKUP_PREVIEW_MIME_TYPE,
      SAVED_MARKUP_PREVIEW_QUALITY,
    );
  }, []);

  const getValidProjectAssetTaskVersionIds = useCallback(() => {
    return (selectedModels || [])
      .map((model) => {
        const candidate =
          model?.selectedVersion?.projectAssetTaskVersion?.id ??
          model?.selectedVersion?.projectAssetTaskVersionId ??
          model?.selectedVersion?.id ??
          model?.selectedVersion;

        const numericId = Number(candidate);
        return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
      })
      .filter((id) => id !== null);
  }, [selectedModels]);

  const saveScreenshotMarkupInternal = useCallback(async (overrides = {}) => {
    if (!isMarkupMode || !screenshotUrl) {
      setError("Capture a screenshot before saving.");
      return { success: false, markupId: null, error: "Capture a screenshot before saving." };
    }

    if (!projectId) {
      setError("No project selected.");
      return { success: false, markupId: null, error: "No project selected." };
    }

    const overrideTitle =
      typeof overrides?.titleOverride === "string"
        ? overrides.titleOverride.trim()
        : "";
    const title = overrideTitle || saveTitle.trim();

    const overrideCreatedFor = Array.isArray(overrides?.createdForOverride)
      ? overrides.createdForOverride
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      : null;

    const createdForEmails =
      overrideCreatedFor && overrideCreatedFor.length > 0
        ? overrideCreatedFor
        : saveForUsers
            .map((user) => String(user?.value || "").trim())
            .filter(Boolean);

    if (!title || createdForEmails.length < 1) {
      setError("Enter a title and at least one user before saving.");
      return {
        success: false,
        markupId: null,
        error: "Enter a title and at least one user before saving.",
      };
    }

    const normalizedActivePin = withActiveSheetPinContext(activeCapturePin);
    if (!normalizedActivePin) {
      setError("Place a pin before saving this markup.");
      return {
        success: false,
        markupId: null,
        error: "Place a pin before saving this markup.",
      };
    }

    setSaveBusy(true);
    setError(null);

    try {
      const drawOverlayDataUrl =
        drawCanvasRef.current?.toDataURL("image/png") || null;
      const screenshotDataUrl =
        imageCanvasRef.current?.toDataURL("image/png") || null;
      const markupPreview = getCompositedMarkupDataUrl(
        SAVED_MARKUP_PREVIEW_MAX_DIMENSION,
      );
      const markupState = viewerState?.viewer?.getState?.() || null;

      const markupDataState = {
        source: "ScreenshotMarkup",
        version: 1,
        screenshotUrl,
        screenshotDataUrl,
        drawOverlayDataUrl,
        viewerState: markupState,
        sheetKey: sheetsSelected ? active2DSheetContext?.sheetKey || null : null,
        sheetId: sheetsSelected ? active2DSheetContext?.sheetId || null : null,
        sheetName: sheetsSelected ? active2DSheetContext?.sheetName || null : null,
        sheetDbId:
          sheetsSelected && Number.isFinite(Number(active2DSheetContext?.sheetDbId))
            ? Number(active2DSheetContext.sheetDbId)
            : null,
        pin: normalizedActivePin,
        multiDisciplineUserTagName,
        multiDisciplineUserTagDate,
        entities: entitiesRef.current.map((entity) =>
          JSON.parse(JSON.stringify(entity)),
        ),
        calloutCounter: calloutCounterRef.current,
        penColor,
        fillColor,
        penSize,
        stampPresetIndex,
        lastMeasureLabel,
      };
      const serializedMarkupData = serializeMarkupData(markupDataState);
      if (!serializedMarkupData) {
        setError("Unable to serialize markup data.");
        return {
          success: false,
          markupId: null,
          error: "Unable to serialize markup data.",
        };
      }
      const effectiveCategory = getEffectiveCategoryForContext(
        overrides?.categoryOverride || saveCategory || DEFAULT_MARKUP_CATEGORY,
      );
      const normalizedLocation = withActiveSheetPinContext(normalizedActivePin);
      if (!normalizedLocation) {
        setError("A pin location is required to save markup.");
        return {
          success: false,
          markupId: null,
          error: "A pin location is required to save markup.",
        };
      }

      const payload = {
        projectId: String(projectId),
        processArea: sheetsSelected ? "2D" : activeMarkupAsset,
        markupData: {
          markupTitle: title,
          imagePreview: markupPreview,
          userName: currentUserDisplayName,
          originator: userData?.emailId || userDetails?.email || "",
          created: new Date(),
          createdFor: createdForEmails,
          actioned: false,
          approved: false,
          asset: sheetsSelected ? twoDimensionalAssetLabel : activeMarkupAsset,
          processArea: sheetsSelected ? "2D" : activeMarkupAsset,
          sheetKey: sheetsSelected ? active2DSheetContext?.sheetKey || null : null,
          sheetId: sheetsSelected ? active2DSheetContext?.sheetId || null : null,
          sheetName: sheetsSelected ? active2DSheetContext?.sheetName || null : null,
          sheetDbId:
            sheetsSelected && Number.isFinite(Number(active2DSheetContext?.sheetDbId))
              ? Number(active2DSheetContext.sheetDbId)
              : null,
          selectedModels: selectedModels || [],
          category: effectiveCategory,
          markupState,
          markupData: serializedMarkupData,
          location: normalizedLocation,
        },
        projectAssetTasksVersions: getValidProjectAssetTaskVersionIds(),
      };

      const compressedPayload = pako.deflate(JSON.stringify(payload));
      const response = await postChunkedCompressedMarkup(
        "/markups/saveMarkup",
        compressedPayload,
        "saveMarkup",
      );

      let savedMarkupId = extractSavedMarkupIdFromResponse(response);

      const refreshedMarkups = await fetchSavedMarkups();
      if (!savedMarkupId && Array.isArray(refreshedMarkups)) {
        const titleMatch = refreshedMarkups.find((item) => {
          const itemTitle = String(item?.title || item?.markupTitle || "").trim();
          return itemTitle === title;
        });

        const fallbackLatest = refreshedMarkups[0];
        const resolvedIdCandidate = titleMatch?.id ?? fallbackLatest?.id;
        const numericId = Number(resolvedIdCandidate);
        if (Number.isInteger(numericId) && numericId > 0) {
          savedMarkupId = numericId;
        }
      }

      setCurrentMarkupId(savedMarkupId || null);
      setSaveTitle(title);
      return { success: true, markupId: savedMarkupId, title };
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      const errorMessage = serverMessage
        ? `Unable to save markup. ${serverMessage}`
        : "Unable to save markup.";
      setError(
        errorMessage,
      );
      return { success: false, markupId: null, error: errorMessage };
    } finally {
      setSaveBusy(false);
    }
  }, [
    extractSavedMarkupIdFromResponse,
    serializeMarkupData,
    fetchSavedMarkups,
    fillColor,
    getValidProjectAssetTaskVersionIds,
    getCompositedMarkupDataUrl,
    currentUserDisplayName,
    isMarkupMode,
    lastMeasureLabel,
    multiDisciplineUserTagName,
    multiDisciplineUserTagDate,
    active2DSheetContext,
    activeCapturePin,
    activeMarkupAsset,
    withActiveSheetPinContext,
    penColor,
    penSize,
    saveCategory,
    getEffectiveCategoryForContext,
    saveForUsers,
    saveTitle,
    selectedModels,
    screenshotUrl,
    stampPresetIndex,
    twoDimensionalAssetLabel,
    userData,
    userDetails,
    viewerState,
    projectId,
    postChunkedCompressedMarkup,
    sheetsSelected,
  ]);

  const saveScreenshotMarkup = useCallback(async () => {
    const result = await saveScreenshotMarkupInternal();
    return Boolean(result?.success);
  }, [saveScreenshotMarkupInternal]);

  const autoSaveMarkupForRejection = useCallback(
    async (options = {}) => {
      return saveScreenshotMarkupInternal({
        titleOverride: options?.title,
        createdForOverride: options?.createdForEmails,
        categoryOverride: options?.category,
      });
    },
    [saveScreenshotMarkupInternal],
  );

  useEffect(() => {
    if (typeof onRegisterRejectionAutoSave !== "function") {
      return undefined;
    }

    onRegisterRejectionAutoSave(autoSaveMarkupForRejection);
    return () => {
      onRegisterRejectionAutoSave(null);
    };
  }, [autoSaveMarkupForRejection, onRegisterRejectionAutoSave]);

  const updateScreenshotMarkup = useCallback(async () => {
    if (!currentMarkupId) {
      setError("Load a saved markup before updating.");
      return false;
    }
    if (!isMarkupMode || !screenshotUrl) {
      setError("Capture or load a markup before updating.");
      return false;
    }

    setSaveBusy(true);
    setError(null);

    try {
      const drawOverlayDataUrl =
        drawCanvasRef.current?.toDataURL("image/png") || null;
      const screenshotDataUrl =
        imageCanvasRef.current?.toDataURL("image/png") || null;
      const markupPreview = getCompositedMarkupDataUrl(
        SAVED_MARKUP_PREVIEW_MAX_DIMENSION,
      );
      const markupState = viewerState?.viewer?.getState?.() || null;
      const selectedSavedMarkup = savedMarkups.find(
        (item) => item.id === currentMarkupId,
      );

      const selectedPersistedData = parsePersistedMarkupData(
        selectedSavedMarkup?.data,
      );

      const normalizedActivePin = withActiveSheetPinContext(activeCapturePin);

      const persistedPinCandidates = [];
      if (selectedPersistedData?.pin) {
        persistedPinCandidates.push(selectedPersistedData.pin);
      }
      if (selectedSavedMarkup?.pin) {
        persistedPinCandidates.push(selectedSavedMarkup.pin);
      }
      if (selectedSavedMarkup?.location) {
        persistedPinCandidates.push(selectedSavedMarkup.location);
      }

      const normalizedPersistedPin = persistedPinCandidates
        .map((candidate) => normalizePinPoint(candidate))
        .find(Boolean);

      const normalizedCachedPin = normalizePinPoint(
        savedMarkupPinCacheRef.current.get(currentMarkupId),
      );

      // Always preserve an existing pin unless the user has explicitly set a new one.
      const effectiveCapturePin =
        normalizedActivePin || normalizedPersistedPin || normalizedCachedPin;
      const effectiveCapturePinWithSheet = withActiveSheetPinContext(
        effectiveCapturePin,
      );

      const effectiveCategory = getEffectiveCategoryForContext(
        saveCategory ||
          selectedSavedMarkup?.category ||
          DEFAULT_MARKUP_CATEGORY,
      );

      const updatedMarkupDataState = {
        source: "ScreenshotMarkup",
        version: 1,
        screenshotUrl,
        screenshotDataUrl,
        drawOverlayDataUrl,
        viewerState: markupState,
        sheetKey: sheetsSelected ? active2DSheetContext?.sheetKey || null : null,
        sheetId: sheetsSelected ? active2DSheetContext?.sheetId || null : null,
        sheetName: sheetsSelected ? active2DSheetContext?.sheetName || null : null,
        sheetDbId:
          sheetsSelected && Number.isFinite(Number(active2DSheetContext?.sheetDbId))
            ? Number(active2DSheetContext.sheetDbId)
            : null,
        pin: effectiveCapturePinWithSheet || null,
        multiDisciplineUserTagName,
        multiDisciplineUserTagDate,
        entities: entitiesRef.current.map((entity) =>
          JSON.parse(JSON.stringify(entity)),
        ),
        calloutCounter: calloutCounterRef.current,
        penColor,
        fillColor,
        penSize,
        stampPresetIndex,
        lastMeasureLabel,
      };
      const serializedUpdatedData = serializeMarkupData(updatedMarkupDataState);
      if (!serializedUpdatedData) {
        setError("Unable to serialize markup data.");
        return false;
      }

      const normalizedLocation = withActiveSheetPinContext(
        effectiveCapturePinWithSheet,
      );

      const updatePayload = {
        ...selectedSavedMarkup,
        id: currentMarkupId,
        imagePreview: markupPreview,
        asset: sheetsSelected ? twoDimensionalAssetLabel : activeMarkupAsset,
        processArea: sheetsSelected ? "2D" : activeMarkupAsset,
        category: effectiveCategory,
        createdFor:
          saveForUsers.length > 0
            ? saveForUsers.map((user) => user.value)
            : selectedSavedMarkup?.createdFor,
        sheetKey: sheetsSelected ? active2DSheetContext?.sheetKey || null : null,
        sheetId: sheetsSelected ? active2DSheetContext?.sheetId || null : null,
        sheetName: sheetsSelected ? active2DSheetContext?.sheetName || null : null,
        sheetDbId:
          sheetsSelected && Number.isFinite(Number(active2DSheetContext?.sheetDbId))
            ? Number(active2DSheetContext.sheetDbId)
            : null,
        markupState,
        location: normalizedLocation || selectedSavedMarkup?.location || null,
        data: serializedUpdatedData,
      };

      await postChunkedCompressedMarkupUpdate(
        updatePayload,
        "updateScreenshotMarkup",
      );

      const updatedPreviewState = {
        id: currentMarkupId,
        imagePreview: markupPreview,
        imageLoading: false,
        imageDeferred: false,
        imageOptimizing: false,
        imageError: false,
      };

      savedMarkupPreviewCacheRef.current.set(
        currentMarkupId,
        updatedPreviewState,
      );
      setSavedMarkupPreviews((prev) => {
        const hasExisting = prev.some((item) => item.id === currentMarkupId);
        if (hasExisting) {
          return prev.map((item) =>
            item.id === currentMarkupId
              ? { ...item, ...updatedPreviewState }
              : item,
          );
        }
        return [...prev, updatedPreviewState];
      });
      setSavedMarkups((prev) =>
        prev.map((item) =>
          item.id === currentMarkupId
            ? { ...item, imagePreview: markupPreview }
            : item,
        ),
      );

      if (effectiveCapturePinWithSheet) {
        savedMarkupPinCacheRef.current.set(
          currentMarkupId,
          effectiveCapturePinWithSheet,
        );
      }

      await fetchSavedMarkups();
      return true;
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      setError(
        serverMessage
          ? `Unable to update markup. ${serverMessage}`
          : "Unable to update markup.",
      );
      return false;
    } finally {
      setSaveBusy(false);
    }
  }, [
    serializeMarkupData,
    currentMarkupId,
    fetchSavedMarkups,
    fillColor,
    isMarkupMode,
    lastMeasureLabel,
    multiDisciplineUserTagName,
    multiDisciplineUserTagDate,
    active2DSheetContext,
    activeCapturePin,
    activeMarkupAsset,
    parsePersistedMarkupData,
    getCompositedMarkupDataUrl,
    penColor,
    penSize,
    normalizePinPoint,
    getEffectiveCategoryForContext,
    withActiveSheetPinContext,
    saveCategory,
    saveForUsers,
    savedMarkups,
    screenshotUrl,
    sheetsSelected,
    stampPresetIndex,
    twoDimensionalAssetLabel,
    viewerState,
    postChunkedCompressedMarkupUpdate,
  ]);

  const loadScreenshotMarkup = useCallback(
    async (markupId) => {
      if (!markupId) {
        return;
      }

      setLoadingMarkupId(markupId);
      setError(null);
      try {
        const response = await axios({
          method: "get",
          url: `${HOST_URL}/markups/getMarkupById`,
          params: { markupId },
          withCredentials: true,
        });

        const payload = normalizeMarkupByIdPayload(response);

        if (!payload) {
          setError("Unable to load selected markup.");
          return;
        }
        const persisted = parsePersistedMarkupData(payload?.data);

        if (!persisted || persisted.source !== "ScreenshotMarkup") {
          setError(
            "This saved markup is from the legacy tool and cannot be edited here.",
          );
          return;
        }

        if (!persisted.screenshotUrl) {
          setError("Saved markup is missing screenshot data.");
          return;
        }

        if (viewerState?.viewer) {
          try {
            await clearViewerSectionArtifacts(viewerState.viewer);
          } catch (sectionClearErr) {
            // Keep loading markup even if section cleanup fails.
          }
        }

        const restoredMarkupState =
          parseMarkupViewerState(payload?.markupState) ||
          parseMarkupViewerState(persisted?.viewerState) ||
          parseMarkupViewerState(persisted?.markupState);

        loadedMarkupViewerStateRef.current = restoredMarkupState
          ? JSON.parse(JSON.stringify(restoredMarkupState))
          : null;

        if (restoredMarkupState && viewerState?.viewer) {
          try {
            const viewerClone = JSON.parse(JSON.stringify(restoredMarkupState));
            const didRestoreViaViewer =
              viewerState.viewer.restoreState?.(viewerClone);

            if (!didRestoreViaViewer) {
              const markupExtension = await viewerState.viewer.loadExtension?.(
                "Autodesk.Viewing.MarkupsCore",
              );

              markupExtension?.viewer?.restoreState(
                JSON.parse(JSON.stringify(restoredMarkupState)),
              );
            }
          } catch (restoreErr) {
            // Keep loading markup screenshot even if state restore fails.
          }
        }

        loadedOverlayDataUrlRef.current = persisted.drawOverlayDataUrl || null;
        calloutCounterRef.current = Number(persisted.calloutCounter || 1);
        undoStackRef.current = [];
        setUndoStack([]);
        draftEntityRef.current = null;
        setEntitySelection([]);
        setCurrentMarkupId(markupId);
        setSaveTitle(payload?.title || "");
        setSaveCategory(payload?.category || DEFAULT_MARKUP_CATEGORY);
        const loadedIsMultiDiscipline =
          String(payload?.category || "") === MULTI_DISCIPLINE_CATEGORY;
        setIsMultiDisciplineMode(loadedIsMultiDiscipline);
        const persistedTagDate = String(
          persisted.multiDisciplineUserTagDate || "",
        ).trim();
        const persistedTagName = String(
          persisted.multiDisciplineUserTagName || payload?.userName || "",
        ).trim();
        setMultiDisciplineUserTagName(persistedTagName);
        setMultiDisciplineUserTagDate(
          persistedTagName
            ? persistedTagDate || toUserTagDate(payload?.created)
            : "",
        );
        setSaveForUsers(
          normalizeCreatedFor(payload?.createdFor).map((email) => ({
            label: email,
            value: email,
          })),
        );
        setPenColor(persisted.penColor || "#ff2e2e");
        setFillColor(persisted.fillColor || "#ffd54f");
        setPenSize(Number(persisted.penSize || 3));
        setStampPresetIndex(Number(persisted.stampPresetIndex || 0));
        setPendingImagePlacement(null);
        setActiveTool(DEFAULT_TOOL_ID);
        setLastMeasureLabel(persisted.lastMeasureLabel || "");
        const loadedEntityState = normalizeLoadedEntities(
          Array.isArray(persisted.entities) ? persisted.entities : [],
        );
        entityIdCounterRef.current = loadedEntityState.nextEntityCounter;
        setEntities(loadedEntityState.entities);
        const loadedPin = buildPinRecordFromPersistedData(persisted, payload);
        setActiveCapturePin(loadedPin || null);
        if (loadedPin) {
          const { id, title, userName, ...normalizedPin } = loadedPin;
          savedMarkupPinCacheRef.current.set(markupId, normalizedPin);
        } else {
          savedMarkupPinCacheRef.current.delete(markupId);
        }
        setScreenshotUrl(persisted.screenshotUrl);
        setIsMarkupMode(true);

        // Match the measure-tool workflow: re-apply the saved viewport so
        // the camera/frame jumps to the markup area immediately on load.
        if (restoredMarkupState && viewerState?.viewer?.restoreState) {
          const restoreViewport = () => {
            try {
              viewerState.viewer.restoreState(
                JSON.parse(JSON.stringify(restoredMarkupState)),
                null,
                true,
              );
              viewerState.viewer.impl?.invalidate?.(true, true, true);
            } catch (restoreErr) {
              // Keep loaded markup visible even if viewport restore fails.
            }
          };

          restoreViewport();
          window.requestAnimationFrame(restoreViewport);
        }
      } catch (err) {
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setError(
          serverMessage
            ? `Unable to load selected markup. ${serverMessage}`
            : "Unable to load selected markup.",
        );
      } finally {
        setLoadingMarkupId(null);
      }
    },
    [
      normalizeCreatedFor,
      buildPinRecordFromPersistedData,
      normalizeMarkupByIdPayload,
      parseMarkupViewerState,
      parsePersistedMarkupData,
      normalizeLoadedEntities,
      clearViewerSectionArtifacts,
      setEntitySelection,
      toUserTagDate,
      viewerState,
    ],
  );

  const openSaveModal = useCallback(() => {
    if (!isMarkupMode || !screenshotUrl) {
      setError("Capture a screenshot before saving.");
      return;
    }
    setSaveModalMode("create");
    if (!saveCategory) {
      setSaveCategory(DEFAULT_MARKUP_CATEGORY);
    }
    setSaveModalOpen(true);
  }, [isMarkupMode, saveCategory, screenshotUrl]);

  const openMarkupInfoModal = useCallback(
    async (markup) => {
      if (!markup) {
        return;
      }

      const markupId = Number(markup.id);
      if (!Number.isInteger(markupId) || markupId <= 0) {
        return;
      }

      setSelectedInfoMarkup(markup);
      setMarkupInfoModalOpen(true);
      setSelectedInfoMarkupImage(null);
      setSelectedInfoMarkupImageLoading(true);
      setMarkupInfoLoading(true);

      try {
        const response = await axios({
          method: "get",
          url: `${HOST_URL}/markups/getMarkupById`,
          params: { markupId },
          withCredentials: true,
        });
        const payload = normalizeMarkupByIdPayload(response);
        if (payload) {
          setSelectedInfoMarkup((prev) => ({
            ...(prev || {}),
            ...payload,
            id: markupId,
          }));
          setSavedMarkups((prev) =>
            prev.map((item) =>
              item.id === markupId
                ? {
                    ...item,
                    ...payload,
                    id: markupId,
                  }
                : item,
            ),
          );
        }
      } catch (err) {
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setError(
          serverMessage
            ? `Unable to load markup info. ${serverMessage}`
            : "Unable to load markup info.",
        );
      } finally {
        setMarkupInfoLoading(false);
      }
    },
    [normalizeMarkupByIdPayload],
  );

  const closeMarkupInfoModal = useCallback(() => {
    if (markupInfoSaving) {
      return;
    }
    setMarkupInfoModalOpen(false);
    setSelectedInfoMarkup(null);
    setSelectedInfoMarkupImage(null);
    setSelectedInfoMarkupImageLoading(false);
    setMarkupInfoLoading(false);
  }, [markupInfoSaving]);

  const openStatusModal = useCallback(
    (markup) => {
      if (!markup) {
        return;
      }

      if (!canDeleteMarkup(markup)) {
        setError("Only the markup owner or project admin can update markup status.");
        return;
      }

      setPendingStatusMarkup(markup);
      setSelectedStatusValue(getMarkupStatusDisplay(markup).value || "open");
      setStatusModalOpen(true);
      setError(null);
    },
    [canDeleteMarkup, getMarkupStatusDisplay],
  );

  const closeStatusModal = useCallback(() => {
    if (statusUpdateBusy) {
      return;
    }

    setStatusModalOpen(false);
    setPendingStatusMarkup(null);
    setSelectedStatusValue("open");
  }, [statusUpdateBusy]);

  const submitStatusUpdate = useCallback(async () => {
    const markupId = Number(pendingStatusMarkup?.id);
    if (!Number.isInteger(markupId) || markupId <= 0) {
      return;
    }

    if (!canDeleteMarkup(pendingStatusMarkup)) {
      setError("Only the markup owner or project admin can update markup status.");
      return;
    }

      const statusPatch = buildMarkupStatusPayload(
        pendingStatusMarkup,
        selectedStatusValue,
      );
      const updatedMarkup = {
        id: markupId,
        ...statusPatch,
      };
    const nextStatusDisplay = getMarkupStatusDisplay(updatedMarkup);

    setStatusUpdateBusy(true);
    setError(null);

    try {
      await postChunkedCompressedMarkupUpdate(
        updatedMarkup,
        "updateMarkupStatus",
      );

      setPendingStatusMarkup(updatedMarkup);
      setSelectedInfoMarkup((prev) =>
        Number(prev?.id) === markupId
          ? {
              ...prev,
              ...updatedMarkup,
            }
          : prev,
      );
      setSavedMarkups((prev) =>
        prev.map((item) =>
          Number(item?.id) === markupId
            ? {
                ...item,
                ...updatedMarkup,
              }
            : item,
        ),
      );
      setSavedMarkupPins((prev) =>
        prev.map((pin) =>
          Number(pin?.markupId) === markupId
            ? {
                ...pin,
                statusLabel: nextStatusDisplay.label,
                pinHeadColor: nextStatusDisplay.head,
                pinStemColor: nextStatusDisplay.stem,
              }
            : pin,
        ),
      );

      setStatusModalOpen(false);
      setPendingStatusMarkup(null);
      setSelectedStatusValue("open");
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      setError(
        serverMessage
          ? `Unable to update markup status. ${serverMessage}`
          : "Unable to update markup status.",
      );
    } finally {
      setStatusUpdateBusy(false);
    }
  }, [
    buildMarkupStatusPayload,
    canDeleteMarkup,
    getMarkupStatusDisplay,
    pendingStatusMarkup,
    postChunkedCompressedMarkupUpdate,
    selectedStatusValue,
  ]);

  const updateMarkupInfoComments = useCallback(
    async (comments) => {
      const markupId = Number(selectedInfoMarkup?.id);
      if (!Number.isInteger(markupId) || markupId <= 0) {
        return;
      }

      const updatedMarkup = {
        id: markupId,
        comments: comments || [],
      };

      setMarkupInfoSaving(true);
      setError(null);
      try {
        await postChunkedCompressedMarkupUpdate(
          updatedMarkup,
          "updateMarkupComments",
        );

        setSelectedInfoMarkup((prev) =>
          prev
            ? {
                ...prev,
                comments: comments || [],
              }
            : prev,
        );
        setSavedMarkups((prev) =>
          prev.map((item) =>
            item.id === markupId
              ? {
                  ...item,
                  comments: comments || [],
                }
              : item,
          ),
        );
      } catch (err) {
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setError(
          serverMessage
            ? `Unable to update markup comments. ${serverMessage}`
            : "Unable to update markup comments.",
        );
      } finally {
        setMarkupInfoSaving(false);
      }
    },
    [postChunkedCompressedMarkupUpdate, selectedInfoMarkup],
  );

  const updateLoadedMarkup = useCallback(async () => {
    const actionSucceeded = await updateScreenshotMarkup();

    if (actionSucceeded) {
      if (typeof measureEventCleanupRef.current === "function") {
        measureEventCleanupRef.current();
        measureEventCleanupRef.current = null;
      }
      resetOverlayState();
    }
  }, [resetOverlayState, updateScreenshotMarkup]);

  const resetSaveModalFields = useCallback(() => {
    setSaveTitle("");
    setSaveCategory(DEFAULT_MARKUP_CATEGORY);
    setSaveForUsers([]);
    setSaveModalMode("create");
  }, []);

  const closeSaveModal = useCallback(() => {
    if (saveBusy) {
      return;
    }
    resetSaveModalFields();
    setSaveModalOpen(false);
  }, [resetSaveModalFields, saveBusy]);

  const submitSaveModal = useCallback(async () => {
    const actionSucceeded =
      saveModalMode === "update"
        ? await updateScreenshotMarkup()
        : await saveScreenshotMarkup();
    if (actionSucceeded) {
      resetSaveModalFields();
      setSaveModalOpen(false);
      if (typeof measureEventCleanupRef.current === "function") {
        measureEventCleanupRef.current();
        measureEventCleanupRef.current = null;
      }
      resetOverlayState();
    }
  }, [
    resetSaveModalFields,
    resetOverlayState,
    saveModalMode,
    saveScreenshotMarkup,
    updateScreenshotMarkup,
  ]);

  useEffect(() => {
    fetchSavedMarkups();
  }, [fetchSavedMarkups]);

  const getAggregatedSavedPins = useCallback(() => {
    const activeSheetKey = normalizeSheetKeyValue(active2DSheetContext?.sheetKey);
    const activeSheetDbId = Number(active2DSheetContext?.sheetDbId);
    const hasActiveSheetDbId = Number.isFinite(activeSheetDbId);

    return contextScopedMarkups.flatMap((markup) => {
      const markupId = Number(markup.id);
      const normalizedPin = normalizePinPoint(
        savedMarkupPinCacheRef.current.get(markupId),
      );

      if (!normalizedPin) {
        return [];
      }

      if (sheetsSelected && (activeSheetKey || hasActiveSheetDbId)) {
        if (hasActiveSheetDbId && Number.isFinite(Number(normalizedPin.dbId))) {
          if (Number(normalizedPin.dbId) !== activeSheetDbId) {
            return [];
          }
        }

        const pinSheetKey = normalizeSheetKeyValue(
          normalizedPin.sheetKey || normalizedPin.sheetId || normalizedPin.sheetName,
        );
        const markupSheetKey = getMarkupSheetKey(markup);
        const effectiveSheetKey = pinSheetKey || markupSheetKey;
        if (
          !hasActiveSheetDbId &&
          (!effectiveSheetKey || effectiveSheetKey !== activeSheetKey)
        ) {
          return [];
        }
      }

      const pinStatus = getMarkupStatusDisplay(markup);

      return [
        {
          id: `${markupId}-0`,
          markupId,
          title: markup?.title || `Markup ${markupId}`,
          userName: markup?.userName || "",
          statusLabel: pinStatus.label,
          pinHeadColor: pinStatus.head,
          pinStemColor: pinStatus.stem,
          ...normalizedPin,
        },
      ];
    });
  }, [
    active2DSheetContext,
    contextScopedMarkups,
    getMarkupSheetKey,
    getMarkupStatusDisplay,
    normalizePinPoint,
    normalizeSheetKeyValue,
    sheetsSelected,
  ]);

  const loadSavedMarkupPins = useCallback(async () => {
    if (savedMarkupsLoading) {
      setPinLoadStatus("waiting");
      return;
    }

    if (pinLoadInFlightRef.current) {
      return;
    }

    if (!savedMarkups.length) {
      setSavedMarkupPins([]);
      setPinLoadStatus("empty");
      setPinLoadStats({ markupCount: 0, pinCount: 0 });
      return;
    }

    setPinLoadStatus("loading");
    pinLoadInFlightRef.current = true;
    setSavedMarkupPinsLoading(true);

    let statusResolved = false;
    const hardStopTimerId = window.setTimeout(() => {
      if (statusResolved) {
        return;
      }
      statusResolved = true;
      setSavedMarkupPinsLoading(false);
      setPinLoadStatus("error");
      setError(
        "Loading saved pins timed out. Please click the pin button again.",
      );
    }, 22000);

    try {
      if (!projectId) {
        setSavedMarkupPins([]);
        setPinLoadStatus("error");
        setPinLoadStats({ markupCount: 0, pinCount: 0 });
        statusResolved = true;
        return;
      }

      const params = {
        projectId,
        projectAsset: savedMarkupsQueryAsset,
      };

      const abortController = new AbortController();
      const requestTimerId = window.setTimeout(() => {
        abortController.abort();
      }, 20000);

      let response;
      try {
        response = await axios({
          method: "get",
          url: `${HOST_URL}/markups/getMarkupPins`,
          params,
          signal: abortController.signal,
          withCredentials: true,
        });
      } catch (getErr) {
        const status = Number(getErr?.response?.status);
        const shouldFallbackToPost =
          !status || status === 404 || status === 405 || status === 415;

        if (!shouldFallbackToPost) {
          throw getErr;
        }

        const formData = new FormData();
        formData.append("formData", JSON.stringify(params));

        response = await axios({
          method: "post",
          url: `${HOST_URL}/markups/getMarkupPins`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          signal: abortController.signal,
          withCredentials: true,
        });
      } finally {
        window.clearTimeout(requestTimerId);
      }

      const responseData = response?.data;
      const pinItems = Array.isArray(responseData?.items)
        ? responseData.items
        : Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];

      savedMarkupPinCacheRef.current.clear();

      pinItems.forEach((item) => {
        const markupId = Number(item?.id);
        if (!markupId) {
          return;
        }
        const pinRecord = buildPinRecordFromPersistedData(
          {
            pin: item?.pin,
          },
          item,
        );
        if (!pinRecord) {
          return;
        }
        const { id, title, userName, ...normalizedPin } = pinRecord;
        savedMarkupPinCacheRef.current.set(markupId, normalizedPin);
      });

      const aggregatedPins = getAggregatedSavedPins();
      setSavedMarkupPins(aggregatedPins);
      setPinLoadStats({
        markupCount: pinItems.length,
        pinCount: aggregatedPins.length,
      });
      setPinLoadStatus(aggregatedPins.length > 0 ? "loaded" : "empty");
      statusResolved = true;
    } catch (err) {
      setSavedMarkupPins([]);
      setPinLoadStatus("error");
      setPinLoadStats({ markupCount: 0, pinCount: 0 });
      const isRequestAborted =
        err?.code === "ERR_CANCELED" ||
        String(err?.message || "").toLowerCase().includes("aborted") ||
        String(err?.message || "").toLowerCase().includes("canceled");
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (isRequestAborted
          ? "The request timed out while loading pins."
          : err?.message);
      setError(
        serverMessage
          ? `Unable to load saved pins. ${serverMessage}`
          : "Unable to load saved pins.",
      );
      statusResolved = true;
    } finally {
      window.clearTimeout(hardStopTimerId);
      pinLoadInFlightRef.current = false;
      setSavedMarkupPinsLoading(false);
    }
  }, [
    buildPinRecordFromPersistedData,
    getAggregatedSavedPins,
    savedMarkupsLoading,
    savedMarkups,
    savedMarkupsQueryAsset,
    projectId,
  ]);

  useEffect(() => {
    if (!showAllSavedPins || savedMarkupPinsLoading) {
      return;
    }

    if (pinLoadStatus !== "idle" && pinLoadStatus !== "waiting") {
      return;
    }

    loadSavedMarkupPins();
  }, [
    loadSavedMarkupPins,
    pinLoadStatus,
    savedMarkupPinsLoading,
    showAllSavedPins,
  ]);

  const pinStatusLabel = useMemo(() => {
    if (!showAllSavedPins) {
      return "";
    }

    if (savedMarkupPinsLoading || pinLoadStatus === "loading") {
      return "Loading pins...";
    }

    if (pinLoadStatus === "waiting") {
      return "Waiting for saved markups...";
    }

    if (pinLoadStatus === "error") {
      return "Failed to load pins";
    }

    if (pinLoadStatus === "loaded") {
      return savedMarkupPins.length > 0
        ? `Loaded ${savedMarkupPins.length} pins`
        : "No saved pins found";
    }

    if (pinLoadStatus === "empty") {
      return "No saved pins found";
    }

    return "";
  }, [
    pinLoadStatus,
    savedMarkupPins.length,
    savedMarkupPinsLoading,
    showAllSavedPins,
  ]);

  useEffect(() => {
    if (!showAllSavedPins || !pinStatusLabel) {
      setShowPinStatusPopup(false);
      return;
    }

    setShowPinStatusPopup(true);
    const shouldAutoHide =
      pinLoadStatus === "loaded" ||
      pinLoadStatus === "empty" ||
      pinLoadStatus === "error";

    if (!shouldAutoHide) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setShowPinStatusPopup(false);
    }, 3500);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [pinLoadStatus, pinStatusLabel, showAllSavedPins]);

  useEffect(() => {
    if (!error) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setError(null);
    }, 5000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [error]);

  useEffect(() => {
    if (!showAllSavedPins || !viewerState?.viewer) {
      return undefined;
    }

    const viewer = viewerState.viewer;
    const cameraChangeEvent =
      typeof window !== "undefined" &&
      window.Autodesk?.Viewing?.CAMERA_CHANGE_EVENT
        ? window.Autodesk.Viewing.CAMERA_CHANGE_EVENT
        : null;

    let frameRequestId = null;
    const requestOverlayRefresh = () => {
      const now =
        typeof window !== "undefined" &&
        typeof window.performance?.now === "function"
          ? window.performance.now()
          : Date.now();

      if (now - pinOverlayLastRefreshRef.current < 90) {
        return;
      }

      if (frameRequestId !== null) {
        return;
      }

      frameRequestId = window.requestAnimationFrame(() => {
        frameRequestId = null;
        pinOverlayLastRefreshRef.current =
          typeof window !== "undefined" &&
          typeof window.performance?.now === "function"
            ? window.performance.now()
            : Date.now();
        setPinOverlayFrame((prev) => (prev + 1) % 1000000);
      });
    };

    if (cameraChangeEvent) {
      viewer.addEventListener(cameraChangeEvent, requestOverlayRefresh);
    }
    window.addEventListener("resize", requestOverlayRefresh);

    return () => {
      if (cameraChangeEvent) {
        viewer.removeEventListener(cameraChangeEvent, requestOverlayRefresh);
      }
      window.removeEventListener("resize", requestOverlayRefresh);
      if (frameRequestId !== null) {
        window.cancelAnimationFrame(frameRequestId);
      }
    };
  }, [showAllSavedPins, viewerState]);

  const getNextCalloutNumber = useCallback(() => {
    const next = calloutCounterRef.current;
    calloutCounterRef.current += 1;
    return next;
  }, []);

  const selectStampPreset = useCallback((preset) => {
    const nextIndex = STAMP_PRESETS.indexOf(preset);
    if (nextIndex >= 0) {
      setStampPresetIndex(nextIndex);
    }
  }, []);

  const getStampText = useCallback(() => {
    const preset = STAMP_PRESETS[stampPresetIndex] || STAMP_PRESETS[0];
    const now = new Date();
    const timestamp = now.toLocaleString();
    return `${preset}\n${timestamp}`;
  }, [stampPresetIndex]);

  const queueImageEntityRender = useCallback(() => {
    setImageRenderNonce((prev) => (prev + 1) % 1000000);
  }, []);

  const cloneEntity = useCallback(
    (entity) => JSON.parse(JSON.stringify(entity)),
    [],
  );

  const translateEntity = useCallback(
    (entity, dx, dy) => {
      const next = cloneEntity(entity);
      if (next.points?.length) {
        next.points = next.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
      }
      if (next.from) {
        next.from = { x: next.from.x + dx, y: next.from.y + dy };
      }
      if (next.to) {
        next.to = { x: next.to.x + dx, y: next.to.y + dy };
      }
      if (next.point) {
        next.point = { x: next.point.x + dx, y: next.point.y + dy };
      }
      return next;
    },
    [cloneEntity],
  );

  const {
    getOrCreateImageEntityAsset,
    getEntityBounds,
    getRectCalloutTextBox,
    drawEntityPrimitive,
  } = useMarkupRenderer(markupImageCacheRef, queueImageEntityRender);

  const renderEntities = useCallback(
    (draftEntity = null) => {
      const canvas = entityCanvasRef.current;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const drawWithTransform = (entity) => {
        const bounds = getEntityBounds(entity, ctx);
        const cx = (bounds.left + bounds.right) / 2;
        const cy = (bounds.top + bounds.bottom) / 2;
        const rotation = entity.rotation || 0;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rotation);
        ctx.translate(-cx, -cy);
        drawEntityPrimitive(ctx, entity);
        ctx.restore();
        return { bounds, cx, cy };
      };

      entitiesRef.current.forEach((entity) => {
        const { bounds, cx, cy } = drawWithTransform(entity);
        const selectedIds = selectedEntityIdsRef.current;
        const isSelected = selectedIds.includes(entity.id);
        if (!isSelected) {
          return;
        }

        const rotation = entity.rotation || 0;
        const corners = [
          { x: bounds.left, y: bounds.top },
          { x: bounds.right, y: bounds.top },
          { x: bounds.right, y: bounds.bottom },
          { x: bounds.left, y: bounds.bottom },
        ].map((p) => {
          const dx = p.x - cx;
          const dy = p.y - cy;
          return {
            x: cx + dx * Math.cos(rotation) - dy * Math.sin(rotation),
            y: cy + dx * Math.sin(rotation) + dy * Math.cos(rotation),
          };
        });
        const topMid = {
          x: (corners[0].x + corners[1].x) / 2,
          y: (corners[0].y + corners[1].y) / 2,
        };
        const rotateHandle = {
          x: topMid.x + 22 * Math.sin(rotation),
          y: topMid.y - 22 * Math.cos(rotation),
        };
        const tailHandle =
          entity.type === TOOL_IDS.RECT_CALLOUT && entity.from && entity.to
            ? (() => {
                const geometry = getRectCalloutGeometry({
                  from: entity.from,
                  to: entity.to,
                  tailEdge: entity.tailEdge,
                  tailTip: entity.tailTip,
                });
                return {
                  x:
                    cx +
                    (geometry.tailTip.x - cx) * Math.cos(rotation) -
                    (geometry.tailTip.y - cy) * Math.sin(rotation),
                  y:
                    cy +
                    (geometry.tailTip.x - cx) * Math.sin(rotation) +
                    (geometry.tailTip.y - cy) * Math.cos(rotation),
                };
              })()
            : null;

        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = "#0d6efd";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        ctx.lineTo(corners[1].x, corners[1].y);
        ctx.lineTo(corners[2].x, corners[2].y);
        ctx.lineTo(corners[3].x, corners[3].y);
        ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);

        const isPrimarySelection =
          selectedIds.length === 1 && entity.id === selectedEntityIdRef.current;
        if (!isPrimarySelection) {
          ctx.restore();
          return;
        }

        ctx.beginPath();
        ctx.moveTo(
          (corners[0].x + corners[1].x) / 2,
          (corners[0].y + corners[1].y) / 2,
        );
        ctx.lineTo(rotateHandle.x, rotateHandle.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(rotateHandle.x, rotateHandle.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = "#0d6efd";
        ctx.fill();

        if (tailHandle) {
          ctx.beginPath();
          ctx.arc(tailHandle.x, tailHandle.y, 6, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          ctx.fill();
          ctx.strokeStyle = "#0d6efd";
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        corners.forEach((corner) => {
          const size = 8;
          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#0d6efd";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.rect(corner.x - size / 2, corner.y - size / 2, size, size);
          ctx.fill();
          ctx.stroke();
        });
        ctx.restore();
      });

      if (draftEntity) {
        drawWithTransform(draftEntity);
      }

      const activeInteraction = interactionRef.current;
      if (
        activeInteraction?.mode === "marquee" &&
        activeInteraction.selectionRect
      ) {
        const { left, top, width, height } = activeInteraction.selectionRect;
        ctx.save();
        ctx.setLineDash([5, 3]);
        ctx.strokeStyle = "rgba(13, 110, 253, 0.95)";
        ctx.fillStyle = "rgba(13, 110, 253, 0.12)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.rect(left, top, width, height);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }

      if (hoveredEntityId) {
        const hoveredEntity = entitiesRef.current.find(
          (entity) => entity.id === hoveredEntityId,
        );
        if (hoveredEntity) {
          const fallbackTagName = String(multiDisciplineUserTagName || "").trim();
          const fallbackTagDate = String(multiDisciplineUserTagDate || "").trim();
          const hoveredTagName = String(
            hoveredEntity.multiDisciplineUserTagName ||
              hoveredEntity.userName ||
              fallbackTagName,
          ).trim();
          if (!hoveredTagName) {
            return;
          }
          const hoveredTagDate = String(
            hoveredEntity.multiDisciplineUserTagDate ||
              hoveredEntity.userTagDate ||
              (hoveredTagName === fallbackTagName ? fallbackTagDate : ""),
          ).trim();
          const bounds = getEntityBounds(hoveredEntity, ctx);
          const canvasDisplayWidth = Number(canvas.clientWidth || 0);
          const canvasScale =
            canvasDisplayWidth > 0
              ? Math.max(1, canvas.width / canvasDisplayWidth)
              : 1;
          const fontSize = Math.max(11, Math.min(32, Math.round(11 * canvasScale)));
          const paddingX = Math.max(7, Math.round(7 * canvasScale));
          const paddingY = Math.max(3, Math.round(3 * canvasScale));
          const text = hoveredTagDate
            ? `By: ${hoveredTagName} - ${hoveredTagDate}`
            : `By: ${hoveredTagName}`;

          ctx.save();
          ctx.font = `700 ${fontSize}px Arial`;
          ctx.textBaseline = "top";
          const textWidth = Math.ceil(ctx.measureText(text).width);
          const boxWidth = textWidth + paddingX * 2;
          const boxHeight = fontSize + paddingY * 2;
          const x = Math.max(
            8,
            Math.min(canvas.width - boxWidth - 8, bounds.left),
          );
          const y = Math.max(8, bounds.top - boxHeight - 6);

          ctx.fillStyle = "rgba(13, 110, 253, 0.88)";
          ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.rect(x, y, boxWidth, boxHeight);
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = "#ffffff";
          ctx.fillText(text, x + paddingX, y + paddingY);
          ctx.restore();
        }
      }
    },
    [
      TOOL_IDS.RECT_CALLOUT,
      drawEntityPrimitive,
      getEntityBounds,
      hoveredEntityId,
      multiDisciplineUserTagName,
      multiDisciplineUserTagDate,
    ],
  );

  useEffect(() => {
    entitiesRef.current = entities;
    renderEntities(draftEntityRef.current);
  }, [entities, renderEntities]);

  useEffect(() => {
    selectedEntityIdRef.current = selectedEntityId;
    renderEntities(draftEntityRef.current);
  }, [renderEntities, selectedEntityId]);

  useEffect(() => {
    selectedEntityIdsRef.current = selectedEntityIds;
    renderEntities(draftEntityRef.current);
  }, [renderEntities, selectedEntityIds]);

  useEffect(() => {
    renderEntities(draftEntityRef.current);
  }, [renderEntities]);

  useEffect(() => {
    if (!selectedEntityId || selectedEntityIds.length !== 1) {
      return;
    }

    const selected = entities.find((entity) => entity.id === selectedEntityId);
    if (selected?.penColor && selected.penColor !== penColor) {
      setPenColor(selected.penColor);
    }
  }, [entities, penColor, selectedEntityId, selectedEntityIds]);

  useEffect(() => {
    if (!selectedEntityId || selectedEntityIds.length !== 1) {
      return;
    }

    const selected = entities.find((entity) => entity.id === selectedEntityId);
    if (selected?.fillColor && selected.fillColor !== fillColor) {
      setFillColor(selected.fillColor);
    }
  }, [entities, fillColor, selectedEntityId, selectedEntityIds]);

  useEffect(() => {
    return () => {
      if (typeof measureEventCleanupRef.current === "function") {
        measureEventCleanupRef.current();
        measureEventCleanupRef.current = null;
      }

      if (screenshotUrl) {
        URL.revokeObjectURL(screenshotUrl);
      }
    };
  }, [screenshotUrl]);

  const composeLegacyMarkupScreenshot = useCallback(
    async (markup) => {
      const persistedCandidates = [
        markup,
        markup?.data,
        markup?.markupData,
        markup?.data?.markupData,
        markup?.data?.data,
        markup?.markupData?.data,
        markup?.markup?.data,
        markup?.markup?.markupData,
      ];
      const hasPersistedMarkupFields = (candidate) =>
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate) &&
        (candidate.screenshotUrl ||
          candidate.screenshotDataUrl ||
          candidate.imageUrl ||
          candidate.drawOverlayDataUrl ||
          Array.isArray(candidate.entities));
      let persistedData = null;

      for (const candidate of persistedCandidates) {
        let current = candidate;
        for (let depth = 0; depth < 4 && current; depth += 1) {
          let parsed = parsePersistedMarkupData(current);
          if (!parsed && typeof current?.arrayBuffer === "function") {
            try {
              parsed = parsePersistedMarkupData(await current.arrayBuffer());
            } catch (decodeErr) {
              parsed = null;
            }
          }
          if (!parsed) {
            break;
          }
          if (hasPersistedMarkupFields(parsed)) {
            persistedData = parsed;
            break;
          }
          current = parsed.markupData || parsed.data;
        }
        if (persistedData) {
          break;
        }
      }
      const baseScreenshot = normalizeLegacyImageDataUrl(
        persistedData?.screenshotUrl ||
          persistedData?.screenshotDataUrl ||
          persistedData?.imageUrl ||
          markup?.imagePreview ||
          markup?.screenshotUrl ||
          markup?.imageUrl,
      );
      const loadImage = (source) =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error("Image decode failed"));
          image.src = source;
        });
      const overlayScreenshot = normalizeLegacyImageDataUrl(
        persistedData?.drawOverlayDataUrl || markup?.drawOverlayDataUrl,
      );

      let previewScreenshot = null;
      let previewSourceKind = null;
      try {
        const previewResponse = await axios.get(
          `${HOST_URL}/markups/getMarkupPreview/${markup?.id}`,
          { responseType: "text", withCredentials: true },
        );
        let rawPreview = previewResponse.data || null;
        if (
          typeof rawPreview === "string" &&
          rawPreview.startsWith("data:") &&
          !rawPreview.startsWith("data:image/")
        ) {
          try {
            const decodedPreview = atob(rawPreview.split(",")[1] || "");
            if (decodedPreview.startsWith("data:image/")) {
              rawPreview = decodedPreview;
            }
          } catch (decodeErr) {
            // Keep the original value if it is not double-encoded.
          }
        }
        previewScreenshot = normalizeLegacyImageDataUrl(rawPreview);
        previewSourceKind = previewScreenshot ? "getMarkupPreview" : null;
      } catch {
      }

      if (!previewScreenshot) {
        previewScreenshot = baseScreenshot;
        previewSourceKind = previewScreenshot ? "persisted" : null;
      }

      if (!previewScreenshot) {
        return overlayScreenshot || null;
      }

      // Production displays this preview directly; do not re-render it through
      // canvas when the endpoint provided a usable image.
      if (previewSourceKind === "getMarkupPreview") {
        try {
          const previewImage = await loadImage(previewScreenshot);
          const previewWidth =
            previewImage.naturalWidth || previewImage.width || 0;
          let persistedWidth = 0;

          if (baseScreenshot) {
            const persistedImage = await loadImage(baseScreenshot);
            persistedWidth =
              persistedImage.naturalWidth || persistedImage.width || 0;
          }

          if (persistedWidth > previewWidth) {
            previewScreenshot = baseScreenshot;
            previewSourceKind = "persisted";
          } else {
            return previewScreenshot;
          }
        } catch (comparisonErr) {
          return previewScreenshot;
        }
      }

      try {
        const baseImage = await loadImage(previewScreenshot);
        const overlayImage = overlayScreenshot
          ? await loadImage(overlayScreenshot).catch(() => null)
          : null;
        const persistedEntities = Array.isArray(persistedData?.entities)
          ? persistedData.entities
          : [];
        if (!overlayImage && !persistedEntities.length) {
          return previewScreenshot;
        }
        const width = baseImage.naturalWidth || baseImage.width;
        const height = baseImage.naturalHeight || baseImage.height;
        if (!width || !height) {
          return previewScreenshot;
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return previewScreenshot;
        }

        ctx.drawImage(baseImage, 0, 0, width, height);
        if (overlayImage) {
          ctx.drawImage(overlayImage, 0, 0, width, height);
        }

        persistedEntities.forEach((entity) => {
          if (entity && typeof entity === "object") {
            drawLegacyPersistedEntity(ctx, entity);
          }
        });

        return canvas.toDataURL("image/png");
      } catch (err) {
        return previewScreenshot;
      }
    },
    [
      drawLegacyPersistedEntity,
      normalizeLegacyImageDataUrl,
      parsePersistedMarkupData,
    ],
  );

  const buildInfoModalCompositedImage = useCallback(
    async (markup) => {
      if (!markup) {
        return null;
      }

      if (isLegacyMarkup(markup)) {
        return composeLegacyMarkupScreenshot(markup);
      }

      const loadImageSource = (source) =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error("Image load failed."));
          image.src = source;
        });

      const persistedCandidates = [
        markup?.data,
        markup?.markupData,
        markup?.data?.markupData,
        markup?.data?.data,
        markup?.markupData?.data,
      ];
      const newMarkupData = persistedCandidates
        .map((candidate) => parsePersistedMarkupData(candidate))
        .find(
          (candidate) =>
            candidate &&
            typeof candidate === "object" &&
            candidate.source === "ScreenshotMarkup" &&
            (candidate.screenshotUrl ||
              candidate.screenshotDataUrl ||
              candidate.imageUrl),
        );
      const legacyMarkupData = parsePersistedMarkupData(
        markup?.data || markup?.markupData,
      );
      const reconstructionAttempts = [
        {
          kind: "new",
          data: newMarkupData,
          screenshotSource:
            newMarkupData &&
            (newMarkupData.screenshotUrl ||
              newMarkupData.screenshotDataUrl ||
              newMarkupData.imageUrl),
          overlaySource:
            newMarkupData?.drawOverlayDataUrl,
        },
        {
          kind: "legacy",
          data: legacyMarkupData,
          screenshotSource:
            legacyMarkupData?.screenshotUrl ||
            legacyMarkupData?.imageUrl ||
            markup?.screenshotUrl ||
            markup?.imageUrl,
          overlaySource:
            legacyMarkupData?.drawOverlayDataUrl ||
            markup?.drawOverlayDataUrl,
        },
      ].filter(
        (attempt, index, attempts) =>
          (attempt.screenshotSource ||
            (attempt.kind === "legacy" && attempt.overlaySource)) &&
          attempts.findIndex(
            (candidate) =>
              candidate.kind === attempt.kind &&
              candidate.screenshotSource === attempt.screenshotSource &&
              candidate.overlaySource === attempt.overlaySource,
          ) === index,
      );

      for (const attempt of reconstructionAttempts) {
        if (attempt.kind === "legacy") {
          const legacyImage = await composeLegacyMarkupScreenshot(markup);
          if (legacyImage) {
            return legacyImage;
          }
          continue;
        }

        try {
          const baseImage = await loadImageSource(attempt.screenshotSource);
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(
            1,
            Number(baseImage.naturalWidth || baseImage.width || 0),
          );
          canvas.height = Math.max(
            1,
            Number(baseImage.naturalHeight || baseImage.height || 0),
          );
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            continue;
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

          const overlaySource = String(attempt.overlaySource || "").trim();
          if (overlaySource) {
            try {
              const overlayImage = await loadImageSource(overlaySource);
              ctx.drawImage(overlayImage, 0, 0, canvas.width, canvas.height);
            } catch (overlayErr) {
              // Keep composing even if overlay decode fails.
            }
          }

          const markupEntities = Array.isArray(attempt.data?.entities)
            ? attempt.data.entities
            : Array.isArray(markup?.entities)
              ? markup.entities
              : [];

          await Promise.all(
            markupEntities
              .filter((entity) => entity?.type === TOOL_IDS.IMAGE)
              .map((entity) =>
                getOrCreateImageEntityAsset(entity.imageDataUrl || entity.imageSrc)
                  ?.ready,
              ),
          );

          for (const entity of markupEntities) {
            if (!entity || typeof entity !== "object") {
              continue;
            }

            const bounds = getEntityBounds(entity, ctx);
            const cx = (bounds.left + bounds.right) / 2;
            const cy = (bounds.top + bounds.bottom) / 2;
            const rotation = entity.rotation || 0;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(rotation);
            ctx.translate(-cx, -cy);
            drawEntityPrimitive(ctx, entity);
            ctx.restore();
          }

          return canvas.toDataURL("image/png");
        } catch (err) {
          continue;
        }
      }

      return null;
    },
    [
      composeLegacyMarkupScreenshot,
      drawEntityPrimitive,
      getEntityBounds,
      getOrCreateImageEntityAsset,
      parsePersistedMarkupData,
    ],
  );

  useEffect(() => {
    if (!markupInfoModalOpen || !selectedInfoMarkup) {
      setSelectedInfoMarkupImage(null);
      setSelectedInfoMarkupImageLoading(false);
      return;
    }

    if (markupInfoLoading) {
      return;
    }

    let cancelled = false;
    setSelectedInfoMarkupImageLoading(true);

    buildInfoModalCompositedImage(selectedInfoMarkup)
      .then((nextImageUrl) => {
        if (cancelled) {
          return;
        }
        setSelectedInfoMarkupImage(nextImageUrl || null);
      })
      .finally(() => {
        if (!cancelled) {
          setSelectedInfoMarkupImageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    buildInfoModalCompositedImage,
    markupInfoLoading,
    markupInfoModalOpen,
    selectedInfoMarkup,
  ]);

  const formatMeasureCandidate = useCallback((candidate, unitsHint = "mm") => {
    if (candidate == null) {
      return null;
    }

    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      return trimmed ? trimmed : null;
    }

    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return `${candidate.toFixed(2)} ${unitsHint}`;
    }

    if (Array.isArray(candidate)) {
      for (const entry of candidate) {
        const parsed = formatMeasureCandidate(entry, unitsHint);
        if (parsed) {
          return parsed;
        }
      }
      return null;
    }

    if (typeof candidate === "object") {
      const unit =
        candidate.units ||
        candidate.unit ||
        candidate.displayUnits ||
        candidate.unitString ||
        unitsHint;

      const text =
        candidate.label ||
        candidate.text ||
        candidate.formatted ||
        candidate.displayValue ||
        candidate.distanceText ||
        candidate.resultText;
      if (typeof text === "string" && text.trim()) {
        return text.trim();
      }

      const numeric =
        candidate.distance ??
        candidate.totalDistance ??
        candidate.value ??
        candidate.result ??
        candidate.length ??
        candidate.measurementValue ??
        candidate?.measurement?.distance;

      if (typeof numeric === "number" && Number.isFinite(numeric)) {
        return `${numeric.toFixed(2)} ${unit}`;
      }

      const nestedKeys = [
        "measurement",
        "result",
        "data",
        "detail",
        "payload",
        "value",
        "currentMeasurement",
        "lastMeasurement",
      ];
      for (const key of nestedKeys) {
        const parsed = formatMeasureCandidate(candidate[key], unit);
        if (parsed) {
          return parsed;
        }
      }
    }

    return null;
  }, []);

  const extractMeasureLabel = useCallback(
    (viewer, measureExt, eventPayload = null) => {
      const unitsHint =
        measureExt?.getUnits?.() ||
        measureExt?.measureTool?.getUnits?.() ||
        "mm";

      const candidates = [
        eventPayload,
        eventPayload?.data,
        eventPayload?.detail,
        eventPayload?.measurement,
        measureExt?.getMeasurement?.(),
        measureExt?.getResult?.(),
        measureExt?.getResults?.(),
        measureExt?.measureTool?.getMeasurement?.(),
        measureExt?.measureTool?.getResult?.(),
        measureExt?.measureTool?.getResults?.(),
        measureExt?.measureTool?.measurement,
        measureExt?.measureTool?.currentMeasurement,
        measureExt?.currentMeasurement,
        measureExt?.lastMeasurement,
        measureExt?._measurement,
        viewer?.getState?.(),
      ];

      for (const candidate of candidates) {
        const label = formatMeasureCandidate(candidate, unitsHint);
        if (label) {
          return label;
        }
      }

      return null;
    },
    [formatMeasureCandidate],
  );

  const extractMeasureLabelFromDom = useCallback((viewer) => {
    const container = viewer?.container;
    if (!container) {
      return null;
    }

    const selectors = [
      "[class*='measure']",
      "[class*='Measure']",
      "[class*='distance']",
      "[class*='Distance']",
    ];
    const unitPattern = /\b(mm|cm|m|km|in|ft|yd)\b/i;
    const axisPattern = /\b[xyz]\s*[:=]?\s*-?\d/i;

    let best = null;
    selectors.forEach((selector) => {
      const nodes = container.querySelectorAll(selector);
      nodes.forEach((node) => {
        const text = (node.textContent || "").replace(/\s+/g, " ").trim();
        if (!text) {
          return;
        }

        const hasNumber = /\d/.test(text);
        const hasUnit = unitPattern.test(text);
        const hasAxis = axisPattern.test(text);
        if (!hasNumber || (!hasUnit && !hasAxis)) {
          return;
        }

        if (!best || text.length > best.length) {
          best = text;
        }
      });
    });

    return best;
  }, []);

  const extractMeasureLabelsFromDom = useCallback((viewer) => {
    const container = viewer?.container;
    if (!container) {
      return [];
    }

    const containerRect = container.getBoundingClientRect();
    const selectors = [
      "[class*='measure']",
      "[class*='Measure']",
      "[class*='distance']",
      "[class*='Distance']",
    ];
    const unitPattern = /\b(mm|cm|m|km|in|ft|yd)\b/i;
    const axisPattern = /\b[xyz]\s*[:=]?\s*-?\d/i;
    const labels = [];

    selectors.forEach((selector) => {
      const nodes = container.querySelectorAll(selector);
      nodes.forEach((node) => {
        const text = (node.textContent || "").replace(/\s+/g, " ").trim();
        const hasNumber = /\d/.test(text);
        const hasUnit = unitPattern.test(text);
        const hasAxis = axisPattern.test(text);
        if (!text || !hasNumber || (!hasUnit && !hasAxis)) {
          return;
        }

        const rect = node.getBoundingClientRect();
        if (!rect.width || !rect.height) {
          return;
        }

        const x = rect.left - containerRect.left;
        const y = rect.top - containerRect.top;

        if (
          x < -5 ||
          y < -5 ||
          x > containerRect.width ||
          y > containerRect.height
        ) {
          return;
        }

        let depth = 0;
        let current = node;
        while (current?.parentElement) {
          depth += 1;
          current = current.parentElement;
        }

        labels.push({
          text,
          x,
          y,
          width: rect.width,
          height: rect.height,
          sourceWidth: containerRect.width,
          sourceHeight: containerRect.height,
          depth,
        });
      });
    });

    const deduped = [];
    const POS_TOLERANCE = 8;

    labels.forEach((candidate) => {
      const existingIndex = deduped.findIndex((item) => {
        if (item.text !== candidate.text) {
          return false;
        }
        return (
          Math.abs(item.x - candidate.x) <= POS_TOLERANCE &&
          Math.abs(item.y - candidate.y) <= POS_TOLERANCE
        );
      });

      if (existingIndex < 0) {
        deduped.push(candidate);
        return;
      }

      const existing = deduped[existingIndex];
      const existingArea = existing.width * existing.height;
      const candidateArea = candidate.width * candidate.height;

      // Prefer the outer container so we keep the full measurement label box.
      const shouldReplace =
        candidate.depth < existing.depth ||
        (candidate.depth === existing.depth && candidateArea > existingArea);

      if (shouldReplace) {
        deduped[existingIndex] = candidate;
      }
    });

    return deduped;
  }, []);

  const loadImage = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load image data."));
      img.src = src;
    });
  }, []);

  const extractStructuredMeasureWidgetsFromDom = useCallback((viewer) => {
    const container = viewer?.container;
    if (!container) {
      return [];
    }

    const containerRect = container.getBoundingClientRect();

    const lengthNodes = Array.from(
      container.querySelectorAll(".measure-length"),
    );
    if (!lengthNodes.length) {
      return [];
    }

    const axisConfigs = [
      { axis: "X", selector: ".adsk-icon-axis-delta-x", fallback: "#e53935" },
      { axis: "Y", selector: ".adsk-icon-axis-delta-y", fallback: "#43a047" },
      { axis: "Z", selector: ".adsk-icon-axis-delta-z", fallback: "#1e88e5" },
    ];

    const widgets = lengthNodes
      .map((lengthNode) => {
        const root =
          lengthNode.closest("[class*='measure'], [class*='Measure']") ||
          lengthNode.parentElement ||
          lengthNode;
        const rootRect = root.getBoundingClientRect();

        const totalText = (lengthNode.textContent || "")
          .replace(/\s+/g, " ")
          .trim();
        const rootText = (root.textContent || "").replace(/\s+/g, " ").trim();

        const hasValidRect =
          Boolean(rootRect?.width) &&
          Boolean(rootRect?.height) &&
          Boolean(containerRect?.width) &&
          Boolean(containerRect?.height);

        const x = hasValidRect ? rootRect.left - containerRect.left : null;
        const y = hasValidRect ? rootRect.top - containerRect.top : null;

        const axisEntries = axisConfigs
          .map(({ axis, selector, fallback }) => {
            const iconNode = root.querySelector(selector);
            if (!iconNode) {
              return null;
            }

            const color =
              (typeof window !== "undefined"
                ? window.getComputedStyle(iconNode).color
                : "") || fallback;

            const iconScopeText = (iconNode.parentElement?.textContent || "")
              .replace(/\s+/g, " ")
              .trim();

            let value = "";
            if (iconScopeText && /\d/.test(iconScopeText)) {
              value = iconScopeText
                .replace(new RegExp(`^\\s*${axis}\\s*[:=]?\\s*`, "i"), "")
                .trim();
            }

            if (!value) {
              const axisMatch = rootText.match(
                new RegExp(
                  `${axis}\\s*[:=]?\\s*([-+]?\\d*\\.?\\d+(?:\\s*(?:mm|cm|m|km|in|ft|yd))?)`,
                  "i",
                ),
              );
              value = axisMatch?.[1] || "";
            }

            if (!value) {
              return null;
            }

            return { axis, color, value };
          })
          .filter(Boolean);

        if (!totalText && !axisEntries.length) {
          return null;
        }

        return {
          key: `${totalText}|${axisEntries.map((entry) => `${entry.axis}:${entry.value}`).join("|")}`,
          totalText,
          axisEntries,
          x,
          y,
          sourceWidth: containerRect.width || null,
          sourceHeight: containerRect.height || null,
        };
      })
      .filter(Boolean);

    const seen = new Set();
    return widgets.filter((widget) => {
      if (seen.has(widget.key)) {
        return false;
      }
      seen.add(widget.key);
      return true;
    });
  }, []);

  const extractStructuredMeasureWidgetsFromExtension = useCallback(
    (viewer, measureExt, eventPayload = null) => {
      const unitsHint =
        measureExt?.getUnits?.() ||
        measureExt?.measureTool?.getUnits?.() ||
        "mm";

      const unitOrAxisPattern =
        /\b(mm|cm|m|km|in|ft|yd)\b|\b[xyz]\s*[:=]?\s*-?\d/i;

      const normalizeWidgetText = (candidate) => {
        const label = formatMeasureCandidate(candidate, unitsHint);
        if (!label) {
          return null;
        }

        const text = String(label).replace(/\s+/g, " ").trim();
        if (!text || !/\d/.test(text)) {
          return null;
        }

        if (!unitOrAxisPattern.test(text) && typeof candidate !== "number") {
          return null;
        }

        return {
          key: text,
          totalText: text,
          axisEntries: [],
        };
      };

      const seenObjects = new WeakSet();
      const widgets = [];

      const collect = (candidate) => {
        if (candidate == null) {
          return;
        }

        const widget = normalizeWidgetText(candidate);
        if (widget) {
          widgets.push(widget);
        }

        if (Array.isArray(candidate)) {
          candidate.forEach((item) => collect(item));
          return;
        }

        if (typeof candidate !== "object") {
          return;
        }

        if (seenObjects.has(candidate)) {
          return;
        }
        seenObjects.add(candidate);

        const nestedKeys = [
          "data",
          "detail",
          "payload",
          "measurement",
          "measurements",
          "result",
          "results",
          "items",
          "currentMeasurement",
          "lastMeasurement",
          "value",
        ];

        nestedKeys.forEach((key) => {
          if (
            Object.prototype.hasOwnProperty.call(candidate, key) &&
            candidate[key] != null
          ) {
            collect(candidate[key]);
          }
        });
      };

      const candidates = [
        eventPayload,
        eventPayload?.data,
        eventPayload?.detail,
        eventPayload?.measurement,
        measureExt?.getMeasurement?.(),
        measureExt?.getResult?.(),
        measureExt?.getResults?.(),
        measureExt?.measureTool?.getMeasurement?.(),
        measureExt?.measureTool?.getResult?.(),
        measureExt?.measureTool?.getResults?.(),
        measureExt?.measureTool?.measurement,
        measureExt?.measureTool?.currentMeasurement,
        measureExt?.currentMeasurement,
        measureExt?.lastMeasurement,
        measureExt?._measurement,
        viewer?.getState?.(),
      ];

      candidates.forEach((candidate) => collect(candidate));

      const deduped = [];
      const seenKeys = new Set();
      widgets.forEach((widget) => {
        if (!widget?.key || seenKeys.has(widget.key)) {
          return;
        }
        seenKeys.add(widget.key);
        deduped.push(widget);
      });

      return deduped;
    },
    [formatMeasureCandidate],
  );

  const mergeStructuredMeasureWidgets = useCallback((primary, secondary) => {
    const merged = [];
    const indexByKey = new Map();

    const normalizeKey = (widget) => {
      const totalText = String(widget?.totalText || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
      if (totalText) {
        return `text:${totalText}`;
      }

      return String(widget?.key || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    };

    const combineAxisEntries = (first, second) => {
      const combined = [];
      const seenAxis = new Set();

      [first, second].forEach((entries) => {
        if (!Array.isArray(entries)) {
          return;
        }

        entries.forEach((entry) => {
          const axis = String(entry?.axis || "")
            .trim()
            .toUpperCase();
          const value = String(entry?.value || "").trim();
          const key = `${axis}:${value}`;
          if (!axis || !value || seenAxis.has(key)) {
            return;
          }

          seenAxis.add(key);
          combined.push(entry);
        });
      });

      return combined;
    };

    [primary, secondary].forEach((source) => {
      if (!Array.isArray(source)) {
        return;
      }

      source.forEach((widget) => {
        const normalizedKey = normalizeKey(widget);
        if (!normalizedKey) {
          return;
        }

        const existingIndex = indexByKey.get(normalizedKey);
        if (typeof existingIndex !== "number") {
          indexByKey.set(normalizedKey, merged.length);
          merged.push({
            ...widget,
            key: normalizedKey,
          });
          return;
        }

        const existing = merged[existingIndex];
        merged[existingIndex] = {
          ...existing,
          ...widget,
          key: normalizedKey,
          totalText:
            String(existing?.totalText || "").trim().length >=
            String(widget?.totalText || "").trim().length
              ? existing.totalText
              : widget.totalText,
          axisEntries: combineAxisEntries(
            existing?.axisEntries,
            widget?.axisEntries,
          ),
          x:
            Number.isFinite(existing?.x) && Number.isFinite(existing?.y)
              ? existing.x
              : widget?.x,
          y:
            Number.isFinite(existing?.x) && Number.isFinite(existing?.y)
              ? existing.y
              : widget?.y,
          sourceWidth:
            Number.isFinite(existing?.sourceWidth) &&
            Number.isFinite(existing?.sourceHeight)
              ? existing.sourceWidth
              : widget?.sourceWidth,
          sourceHeight:
            Number.isFinite(existing?.sourceWidth) &&
            Number.isFinite(existing?.sourceHeight)
              ? existing.sourceHeight
              : widget?.sourceHeight,
        };
      });
    });

    return merged;
  }, []);

  const bakeStructuredMeasureWidgetsIntoImageDataUrl = useCallback(
    async (imageDataUrl, widgets, fallbackWidth = 0, fallbackHeight = 0) => {
      if (!imageDataUrl || !Array.isArray(widgets) || !widgets.length) {
        return imageDataUrl;
      }

      let baseImage;
      try {
        baseImage = await loadImage(imageDataUrl);
      } catch (err) {
        return imageDataUrl;
      }

      const canvas = document.createElement("canvas");
      canvas.width = baseImage.width || fallbackWidth || 0;
      canvas.height = baseImage.height || fallbackHeight || 0;

      if (!canvas.width || !canvas.height) {
        return imageDataUrl;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return imageDataUrl;
      }

      ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

      const paddingX = 8;
      const paddingY = 7;
      const titleFont = "600 12px Arial";
      const axisFont = "600 11px Arial";
      const lineHeight = 15;
      ctx.textBaseline = "middle";

      widgets.forEach((widget) => {
        const axisLines = Array.isArray(widget.axisEntries)
          ? widget.axisEntries
          : [];

        const titleLine = String(widget.totalText || "").trim();
        const hasTitle = Boolean(titleLine);
        const textLines = hasTitle ? 1 + axisLines.length : axisLines.length;
        if (!textLines) {
          return;
        }

        ctx.save();
        let maxTextWidth = 0;
        if (hasTitle) {
          ctx.font = titleFont;
          maxTextWidth = Math.max(
            maxTextWidth,
            ctx.measureText(titleLine).width,
          );
        }
        ctx.font = axisFont;
        axisLines.forEach((entry) => {
          const axisText = `${entry.axis}: ${entry.value}`;
          maxTextWidth = Math.max(
            maxTextWidth,
            ctx.measureText(axisText).width,
          );
        });

        const boxWidth = Math.ceil(maxTextWidth + paddingX * 2);
        const boxHeight = Math.ceil(textLines * lineHeight + paddingY * 2);

        const hasWidgetPosition =
          Number.isFinite(widget?.x) &&
          Number.isFinite(widget?.y) &&
          Number.isFinite(widget?.sourceWidth) &&
          Number.isFinite(widget?.sourceHeight) &&
          widget.sourceWidth > 0 &&
          widget.sourceHeight > 0;

        if (!hasWidgetPosition) {
          ctx.restore();
          return;
        }

        const scaleX = canvas.width / widget.sourceWidth;
        const scaleY = canvas.height / widget.sourceHeight;
        let boxX = Math.round(widget.x * scaleX);
        let boxY = Math.round(widget.y * scaleY);

        boxX = Math.max(
          8,
          Math.min(boxX, Math.max(8, canvas.width - boxWidth - 8)),
        );
        boxY = Math.max(
          8,
          Math.min(boxY, Math.max(8, canvas.height - boxHeight - 8)),
        );

        ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
        ctx.strokeStyle = "rgba(0, 0, 0, 0.35)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.rect(boxX, boxY, boxWidth, boxHeight);
        ctx.fill();
        ctx.stroke();

        const textBlockHeight = textLines * lineHeight;
        let textY =
          boxY +
          Math.max(paddingY, (boxHeight - textBlockHeight) / 2) +
          lineHeight / 2;
        if (hasTitle) {
          ctx.font = titleFont;
          ctx.fillStyle = "#121212";
          ctx.fillText(titleLine, boxX + paddingX, textY);
          textY += lineHeight;
        }

        ctx.font = axisFont;
        axisLines.forEach((entry) => {
          ctx.fillStyle = entry.color || "#202020";
          ctx.fillText(`${entry.axis}: ${entry.value}`, boxX + paddingX, textY);
          textY += lineHeight;
        });

        ctx.restore();
      });

      return canvas.toDataURL("image/png");
    },
    [loadImage],
  );

  const drawPendingMeasureLabels = useCallback((ctx, drawWidth, drawHeight) => {
    const labels = pendingMeasureLabelsRef.current;
    if (!labels || !labels.length) {
      return;
    }

    labels.forEach((label) => {
      const scaleX = label.sourceWidth ? drawWidth / label.sourceWidth : 1;
      const scaleY = label.sourceHeight ? drawHeight / label.sourceHeight : 1;
      const x = Math.max(0, Math.round(label.x * scaleX));
      const y = Math.max(0, Math.round(label.y * scaleY));
      const fontSize = Math.max(
        10,
        Math.min(14, Math.round((label.height || 16) * scaleY)),
      );
      const paddingX = 4;
      const paddingY = 2;

      ctx.font = `600 ${fontSize}px Arial`;
      ctx.textBaseline = "top";
      const width = Math.ceil(ctx.measureText(label.text).width);

      ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
      ctx.strokeStyle = "rgba(0, 0, 0, 0.45)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.rect(
        x - paddingX,
        y - paddingY,
        width + paddingX * 2,
        fontSize + paddingY * 2,
      );
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#000";
      ctx.fillText(label.text, x, y);
    });
  }, []);

  useEffect(() => {
    const viewer = viewerState?.viewer;
    if (!viewer) {
      return;
    }

    const canvas = viewer.impl?.canvas;

    if (isMarkupMode && screenshotUrl) {
      if (typeof viewer.setNavigationLock === "function") {
        viewer.setNavigationLock(true);
      }

      if (!isMeasuring && canvas) {
        if (viewerCanvasPointerEventsRef.current === null) {
          viewerCanvasPointerEventsRef.current =
            canvas.style.pointerEvents || "";
        }
        canvas.style.pointerEvents = "none";
      }
      return;
    }

    if (typeof viewer.setNavigationLock === "function") {
      viewer.setNavigationLock(false);
    }

    if (canvas && viewerCanvasPointerEventsRef.current !== null) {
      canvas.style.pointerEvents = viewerCanvasPointerEventsRef.current;
      viewerCanvasPointerEventsRef.current = null;
    }

    return () => {
      if (typeof viewer.setNavigationLock === "function") {
        viewer.setNavigationLock(false);
      }
      if (canvas && viewerCanvasPointerEventsRef.current !== null) {
        canvas.style.pointerEvents = viewerCanvasPointerEventsRef.current;
        viewerCanvasPointerEventsRef.current = null;
      }
    };
  }, [isMarkupMode, isMeasuring, screenshotUrl, viewerState]);

  const getPointerPosition = useCallback((event) => {
    const canvas =
      drawCanvasRef.current ||
      entityCanvasRef.current ||
      imageCanvasRef.current;
    if (!canvas) {
      return null;
    }
    const rect = canvas.getBoundingClientRect();
    if (!rect?.width || !rect?.height) {
      return null;
    }
    const clientX =
      event.touches?.[0]?.clientX ??
      event.changedTouches?.[0]?.clientX ??
      event.clientX;
    const clientY =
      event.touches?.[0]?.clientY ??
      event.changedTouches?.[0]?.clientY ??
      event.clientY;
    if (typeof clientX !== "number" || typeof clientY !== "number") {
      return null;
    }
    const x = (clientX - rect.left) * (canvas.width / rect.width);
    const y = (clientY - rect.top) * (canvas.height / rect.height);
    return { x, y };
  }, []);

  const getEditorViewportScale = useCallback(() => {
    const canvas =
      entityCanvasRef.current ||
      drawCanvasRef.current ||
      imageCanvasRef.current;
    const viewport = zoomViewportRef.current;

    const canvasWidth = Number(canvas?.width) || 0;
    const canvasHeight = Number(canvas?.height) || 0;
    const viewportWidth =
      Number(canvas?.clientWidth) || Number(viewport?.clientWidth) || canvasWidth;
    const viewportHeight =
      Number(canvas?.clientHeight) || Number(viewport?.clientHeight) || canvasHeight;

    if (!canvasWidth || !canvasHeight || !viewportWidth || !viewportHeight) {
      return { scaleX: 1, scaleY: 1, fontScale: 1 };
    }

    const scaleX = viewportWidth / canvasWidth;
    const scaleY = viewportHeight / canvasHeight;
    return { scaleX, scaleY, fontScale: Math.min(scaleX, scaleY) };
  }, []);

  const getDefaultEditorFontSize = useCallback(
    (size = penSize) => {
      const safeSize = Number.isFinite(size) ? size : penSize;
      const computed = Math.min(24, safeSize * 2 + 8);
      const { fontScale } = getEditorViewportScale();
      const targetDisplayFontSize = Boolean(sheetsSelected)
        ? 16
        : Math.max(12, computed);
      return targetDisplayFontSize / Math.max(0.0001, fontScale);
    },
    [getEditorViewportScale, penSize, sheetsSelected],
  );

  const getTextEditorDisplayMetrics = useCallback(() => {
    const canvas =
      entityCanvasRef.current ||
      drawCanvasRef.current ||
      imageCanvasRef.current;

    const canvasWidth = Number(canvas?.width) || 0;
    const canvasHeight = Number(canvas?.height) || 0;
    if (!canvasWidth || !canvasHeight) {
      return {
        x: textEditor.x,
        y: textEditor.y,
        width: textEditor.width,
        height: textEditor.height,
        fontSize: textEditor.fontSize || getDefaultEditorFontSize(),
      };
    }

    const { scaleX, scaleY, fontScale } = getEditorViewportScale();
    const baseFontSize = textEditor.fontSize || getDefaultEditorFontSize();
    const scaledFontSize = baseFontSize * fontScale;
    const minimumDisplayFontSize = Boolean(sheetsSelected) ? 16 : 12;

    return {
      x: textEditor.x * scaleX,
      y: textEditor.y * scaleY,
      width: Number.isFinite(textEditor.width) ? textEditor.width * scaleX : null,
      height: Number.isFinite(textEditor.height)
        ? textEditor.height * scaleY
        : null,
      fontSize: Math.max(minimumDisplayFontSize, scaledFontSize),
    };
  }, [getDefaultEditorFontSize, getEditorViewportScale, sheetsSelected, textEditor]);

  const textEditorDisplayMetrics = getTextEditorDisplayMetrics();

  const drawScreenshotToCanvas = useCallback(
    (blobUrl) => {
      const imageCanvas = imageCanvasRef.current;
      const drawCanvas = drawCanvasRef.current;
      const entityCanvas = entityCanvasRef.current;
      if (!imageCanvas || !drawCanvas || !entityCanvas) return;

      const ctx = imageCanvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        imageCanvas.width = img.width;
        imageCanvas.height = img.height;
        drawCanvas.width = img.width;
        drawCanvas.height = img.height;
        entityCanvas.width = img.width;
        entityCanvas.height = img.height;
        setCaptureCanvasSize({ width: img.width, height: img.height });

        ctx.clearRect(0, 0, imageCanvas.width, imageCanvas.height);
        ctx.drawImage(img, 0, 0, imageCanvas.width, imageCanvas.height);

        const drawCtx = drawCanvas.getContext("2d");
        drawCtx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
        setTextEditor((prev) => ({
          ...prev,
          visible: false,
          value: "",
          entityId: null,
          fontSize: null,
          penColor: null,
        }));

        if (loadedOverlayDataUrlRef.current) {
          const loadedOverlay = new Image();
          loadedOverlay.onload = () => {
            drawCtx.drawImage(
              loadedOverlay,
              0,
              0,
              drawCanvas.width,
              drawCanvas.height,
            );
            loadedOverlayDataUrlRef.current = null;
            renderEntities(draftEntityRef.current);
          };
          loadedOverlay.onerror = () => {
            loadedOverlayDataUrlRef.current = null;
            renderEntities(draftEntityRef.current);
          };
          loadedOverlay.src = loadedOverlayDataUrlRef.current;
          return;
        }

        if (measureOverlaySnapshotRef.current) {
          const overlayImg = new Image();
          overlayImg.onload = () => {
            drawCtx.drawImage(
              overlayImg,
              0,
              0,
              drawCanvas.width,
              drawCanvas.height,
            );
            drawPendingMeasureLabels(
              drawCtx,
              drawCanvas.width,
              drawCanvas.height,
            );
            shouldRestoreOverlayRef.current = false;
            renderEntities(draftEntityRef.current);
          };
          overlayImg.onerror = () => {
            drawPendingMeasureLabels(
              drawCtx,
              drawCanvas.width,
              drawCanvas.height,
            );
            shouldRestoreOverlayRef.current = false;
            renderEntities(draftEntityRef.current);
          };
          overlayImg.src = measureOverlaySnapshotRef.current;
        } else {
          drawPendingMeasureLabels(
            drawCtx,
            drawCanvas.width,
            drawCanvas.height,
          );
          renderEntities(draftEntityRef.current);
        }
      };
      img.onerror = () => {
        setError("Unable to load screenshot image.");
      };
      img.src = blobUrl;
    },
    [drawPendingMeasureLabels, renderEntities],
  );

  useEffect(() => {
    if (!screenshotUrl || isMeasuring) {
      return;
    }

    // Wait until overlay canvases are mounted before drawing the captured frame.
    const rafId = window.requestAnimationFrame(() => {
      drawScreenshotToCanvas(screenshotUrl);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
    // Intentionally exclude drawScreenshotToCanvas so hover/edit state updates
    // do not retrigger image/draw-canvas rehydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeasuring, screenshotUrl]);

  useEffect(() => {
    if (typeof screenshotUrl === "string" && screenshotUrl.trim().length > 0) {
      lastValidScreenshotUrlRef.current = screenshotUrl;
    }
  }, [screenshotUrl]);

  const getViewerScreenshot = useCallback((viewer) => {
    return new Promise((resolve, reject) => {
      const screenshot = new Image();
      const timeout = setTimeout(() => {
        reject(new Error("Screenshot request timed out."));
      }, 7000);

      screenshot.onload = () => {
        clearTimeout(timeout);
        resolve(screenshot);
      };

      screenshot.onerror = (err) => {
        clearTimeout(timeout);
        reject(err || new Error("Screenshot image failed to load."));
      };

      try {
        const containerWidth = Math.max(
          1,
          Number(viewer?.container?.clientWidth || 0),
        );
        const containerHeight = Math.max(
          1,
          Number(viewer?.container?.clientHeight || 0),
        );

        const preferredScale = sheetsSelected
          ? MARKUP_CAPTURE_SCALE_2D
          : MARKUP_CAPTURE_SCALE_3D;

        const scaleByDimension = Math.min(
          MARKUP_CAPTURE_MAX_DIMENSION / containerWidth,
          MARKUP_CAPTURE_MAX_DIMENSION / containerHeight,
        );

        const captureScale = Math.max(
          1,
          Math.min(preferredScale, scaleByDimension),
        );

        const captureWidth = Math.max(1, Math.round(containerWidth * captureScale));
        const captureHeight = Math.max(1, Math.round(containerHeight * captureScale));

        viewer.getScreenShot(
          captureWidth,
          captureHeight,
          (imageUrl) => {
            if (!imageUrl) {
              clearTimeout(timeout);
              reject(new Error("Viewer did not return screenshot data."));
              return;
            }
            screenshot.src = imageUrl;
          },
        );
      } catch (err) {
        clearTimeout(timeout);
        reject(err);
      }
    });
  }, [sheetsSelected]);

  const drawPinOnCapturedCanvas = useCallback(
    (ctx, point, canvas, renderScale = 1) => {
    if (!ctx || !point || !canvas) {
      return;
    }

    const safeScale = Number.isFinite(renderScale) && renderScale > 0
      ? renderScale
      : 1;

    // The clicked point is the pin tip, not the head center.
    const headRadius = 6 * safeScale;
    const stemLength = 11 * safeScale;
    const edgePadding = 8 * safeScale;
    const x = Math.min(
      Math.max(edgePadding, point.x),
      Math.max(edgePadding, canvas.width - edgePadding),
    );
    const y = Math.min(
      Math.max(headRadius * 0.9 + stemLength + 2, point.y),
      Math.max(edgePadding, canvas.height - 2 * safeScale),
    );
    const headBaseY = y - stemLength;
    const headCenterY = headBaseY - headRadius * 0.9;

    ctx.save();
    ctx.lineCap = "round";

    ctx.beginPath();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.92)";
    ctx.lineWidth = Math.max(1, 5 * safeScale);
    ctx.moveTo(x, headBaseY);
    ctx.lineTo(x, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.strokeStyle = "#c62828";
    ctx.lineWidth = Math.max(1, 3 * safeScale);
    ctx.moveTo(x, headBaseY);
    ctx.lineTo(x, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.fillStyle = "#e53935";
    ctx.arc(x, headCenterY, headRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = Math.max(1, 2 * safeScale);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
    ctx.stroke();

    ctx.beginPath();
    ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
    ctx.arc(
      x - 1.5 * safeScale,
      headCenterY - 1.5 * safeScale,
      1.4 * safeScale,
      0,
      Math.PI * 2,
    );
    ctx.fill();

    ctx.restore();
    },
    [],
  );

  const cropCanvasToAspectRatio = useCallback(
    (sourceCanvas, targetAspectRatio = MARKUP_DEFAULT_ASPECT_RATIO) => {
      const sourceWidth = Math.max(1, Number(sourceCanvas?.width || 0));
      const sourceHeight = Math.max(1, Number(sourceCanvas?.height || 0));
      const safeTargetAspect =
        Number.isFinite(targetAspectRatio) && targetAspectRatio > 0
          ? Number(targetAspectRatio)
          : MARKUP_DEFAULT_ASPECT_RATIO;

      const sourceAspect = sourceWidth / sourceHeight;
      let cropWidth = sourceWidth;
      let cropHeight = sourceHeight;

      if (sourceAspect > safeTargetAspect) {
        cropWidth = Math.max(1, Math.round(sourceHeight * safeTargetAspect));
      } else if (sourceAspect < safeTargetAspect) {
        cropHeight = Math.max(1, Math.round(sourceWidth / safeTargetAspect));
      }

      const offsetX = Math.max(0, Math.floor((sourceWidth - cropWidth) / 2));
      const offsetY = Math.max(0, Math.floor((sourceHeight - cropHeight) / 2));

      const outputCanvas = document.createElement("canvas");
      outputCanvas.width = cropWidth;
      outputCanvas.height = cropHeight;

      const outputCtx = outputCanvas.getContext("2d");
      outputCtx.clearRect(0, 0, cropWidth, cropHeight);
      outputCtx.drawImage(
        sourceCanvas,
        offsetX,
        offsetY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight,
      );

      return {
        canvas: outputCanvas,
        crop: {
          offsetX,
          offsetY,
          width: cropWidth,
          height: cropHeight,
        },
      };
    },
    [],
  );

  const captureScreenshot = useCallback(
    async (options = {}) => {
      const multiDiscipline = Boolean(options?.multiDiscipline);
      const pinPoint = options?.pinPoint || null;
      const pinWorldPoint = options?.pinWorldPoint || null;
      const pinDbId = options?.pinDbId || null;
      const pinModelId = options?.pinModelId || null;
      const pinModelGuid = options?.pinModelGuid || null;
      const pinModelName = options?.pinModelName || null;
      if (!viewerState?.viewer) {
        setError("Viewer is not ready yet.");
        return;
      }

      setIsCapturing(true);
      setIsAwaitingPinPlacement(false);
      setPendingCaptureMultiDiscipline(false);
      setError(null);

      const viewer = viewerState.viewer;
      try {
        const screenshot = await getViewerScreenshot(viewer);
        const viewerWidth = Math.max(1, Number(viewer.container.clientWidth || 0));
        const viewerHeight = Math.max(1, Number(viewer.container.clientHeight || 0));

        const capturedCanvas = document.createElement("canvas");
        capturedCanvas.width = Math.max(1, Number(screenshot.width || viewerWidth));
        capturedCanvas.height = Math.max(
          1,
          Number(screenshot.height || viewerHeight),
        );
        const captureCtx = capturedCanvas.getContext("2d");
        captureCtx.clearRect(0, 0, capturedCanvas.width, capturedCanvas.height);
        captureCtx.drawImage(
          screenshot,
          0,
          0,
          capturedCanvas.width,
          capturedCanvas.height,
        );

        const strictCaptureResult = cropCanvasToAspectRatio(
          capturedCanvas,
          MARKUP_DEFAULT_ASPECT_RATIO,
        );
        const strictCanvas = strictCaptureResult.canvas;
        const strictCrop = strictCaptureResult.crop;
        const strictCtx = strictCanvas.getContext("2d");

        const pinScaleX = capturedCanvas.width / viewerWidth;
        const pinScaleY = capturedCanvas.height / viewerHeight;
        const pinRenderScale = Math.max(1, (pinScaleX + pinScaleY) / 2);
        const sourceScaledPinPoint = pinPoint
          ? {
              x: pinPoint.x * pinScaleX,
              y: pinPoint.y * pinScaleY,
            }
          : null;

        const scaledPinPoint = sourceScaledPinPoint
          ? {
              x: sourceScaledPinPoint.x - strictCrop.offsetX,
              y: sourceScaledPinPoint.y - strictCrop.offsetY,
            }
          : null;

        if (scaledPinPoint) {
          drawPinOnCapturedCanvas(
            strictCtx,
            scaledPinPoint,
            strictCanvas,
            pinRenderScale,
          );
        }

        const normalizedCapturePin = normalizePinPoint(
          scaledPinPoint
            ? {
                x: scaledPinPoint.x,
                y: scaledPinPoint.y,
                xRatio:
                  strictCanvas.width > 0
                    ? scaledPinPoint.x / strictCanvas.width
                    : null,
                yRatio:
                  strictCanvas.height > 0
                    ? scaledPinPoint.y / strictCanvas.height
                    : null,
                sourceWidth: strictCanvas.width,
                sourceHeight: strictCanvas.height,
                worldX: pinWorldPoint?.x ?? null,
                worldY: pinWorldPoint?.y ?? null,
                worldZ: pinWorldPoint?.z ?? null,
                dbId: pinDbId,
                modelId: pinModelId,
                modelGuid: pinModelGuid,
                modelName: pinModelName,
              }
            : null,
        );

        const mergedData = strictCanvas.toDataURL("image/png");

        setScreenshotUrl((prev) => {
          if (prev?.startsWith("blob:")) {
            URL.revokeObjectURL(prev);
          }
          return mergedData;
        });
        setIsMarkupMode(true);
        setIsMultiDisciplineMode(multiDiscipline);
        setPendingImagePlacement(null);
        setActiveTool(DEFAULT_TOOL_ID);
        setMultiDisciplineUserTagName(currentUserDisplayName);
        setMultiDisciplineUserTagDate(toUserTagDate(new Date()));
        setSaveCategory((prev) => {
          if (!prev || prev === MULTI_DISCIPLINE_CATEGORY) {
            return DEFAULT_MARKUP_CATEGORY;
          }
          return prev;
        });
        setCurrentMarkupId(null);
        setActiveCapturePin(normalizedCapturePin);
      } catch (e) {
        setError(
          `Unable to capture viewer screenshot. ${e?.message || ""}`.trim(),
        );
      } finally {
        setIsCapturing(false);
      }
    },
    [
      currentUserDisplayName,
      cropCanvasToAspectRatio,
      drawPinOnCapturedCanvas,
      getViewerScreenshot,
      normalizePinPoint,
      toUserTagDate,
      viewerState,
    ],
  );

  const beginCaptureWithPinPlacement = useCallback(
    (multiDiscipline = false) => {
      if (!viewerState?.viewer) {
        setError("Viewer is not ready yet.");
        return;
      }

      if (isCapturing || isMarkupMode) {
        return;
      }

      if (showAllSavedPins) {
        setShowAllSavedPins(false);
      }

      setPendingCaptureMultiDiscipline(Boolean(multiDiscipline));
      setIsAwaitingPinPlacement(true);
      setPinPlacementWarningModalOpen(false);
      setError(null);
    },
    [isCapturing, isMarkupMode, showAllSavedPins, viewerState],
  );

  const cancelPinPlacement = useCallback(() => {
    setIsAwaitingPinPlacement(false);
    setPendingCaptureMultiDiscipline(false);
    setPinPlacementWarningModalOpen(false);
    setError(null);
  }, []);

  const acknowledgePinPlacementWarning = useCallback(() => {
    setPinPlacementWarningModalOpen(false);
    setError(null);
  }, []);

  const handlePinPlacement = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const viewer = viewerState?.viewer;
      const containerRect = viewer?.container?.getBoundingClientRect?.();
      if (!viewer || !containerRect?.width || !containerRect?.height) {
        setIsAwaitingPinPlacement(false);
        setPendingCaptureMultiDiscipline(false);
        return;
      }

      const pinPoint = {
        x: Math.min(
          Math.max(0, event.clientX - containerRect.left),
          containerRect.width,
        ),
        y: Math.min(
          Math.max(0, event.clientY - containerRect.top),
          containerRect.height,
        ),
      };

      let pinWorldPoint = null;
      let pinDbId = null;
      let pinModelId = null;
      let pinModelGuid = null;
      let pinModelName = null;
      let hasValidModelHit = false;
      try {
        const hit = viewer.impl?.hitTest?.(pinPoint.x, pinPoint.y, true);
        if (hit?.point) {
          const viewerModelReference = getViewerModelReference(hit.model);
          const hitDbId = Number(hit.dbId);
          hasValidModelHit =
            Boolean(hit.model) && Number.isFinite(hitDbId) && hitDbId > 0;

          if (!sheetsSelected && !hasValidModelHit) {
            setPinPlacementWarningModalOpen(true);
            return;
          }

          pinWorldPoint = {
            x: Number(hit.point.x),
            y: Number(hit.point.y),
            z: Number(hit.point.z),
          };
          pinDbId = Number.isFinite(hitDbId) ? hitDbId : null;
          pinModelId = viewerModelReference.modelRuntimeId;
          pinModelGuid = viewerModelReference.modelGuid;
          pinModelName = viewerModelReference.modelName;
        }
      } catch (err) {
        pinWorldPoint = null;
      }

      if (!sheetsSelected && !hasValidModelHit) {
        setPinPlacementWarningModalOpen(true);
        return;
      }

      captureScreenshot({
        multiDiscipline: pendingCaptureMultiDiscipline,
        pinPoint,
        pinWorldPoint,
        pinDbId,
        pinModelId,
        pinModelGuid,
        pinModelName,
      });
    },
    [
      captureScreenshot,
      getViewerModelReference,
      pendingCaptureMultiDiscipline,
      sheetsSelected,
      viewerState,
    ],
  );

  useEffect(() => {
    if (!isAwaitingPinPlacement || isMarkupMode) {
      return undefined;
    }

    const handleAwaitingPinEscape = (event) => {
      if (event.defaultPrevented) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        cancelPinPlacement();
      }
    };

    window.addEventListener("keydown", handleAwaitingPinEscape);
    return () => {
      window.removeEventListener("keydown", handleAwaitingPinEscape);
    };
  }, [cancelPinPlacement, isAwaitingPinPlacement, isMarkupMode]);

  const captureViewerFrameDataUrl = useCallback(
    async (viewer) => {
      const screenshot = await getViewerScreenshot(viewer);
      const capturedCanvas = document.createElement("canvas");
      capturedCanvas.width = Math.max(
        1,
        Number(screenshot.width || viewer.container.clientWidth || 0),
      );
      capturedCanvas.height = Math.max(
        1,
        Number(screenshot.height || viewer.container.clientHeight || 0),
      );
      const captureCtx = capturedCanvas.getContext("2d");
      captureCtx.clearRect(0, 0, capturedCanvas.width, capturedCanvas.height);
      captureCtx.drawImage(
        screenshot,
        0,
        0,
        capturedCanvas.width,
        capturedCanvas.height,
      );

      const activeCanvas = imageCanvasRef.current;
      const activeAspectRatio =
        Number(captureCanvasSize.width) > 0 && Number(captureCanvasSize.height) > 0
          ? Number(captureCanvasSize.width) / Number(captureCanvasSize.height)
          : Number(activeCanvas?.width) > 0 && Number(activeCanvas?.height) > 0
            ? Number(activeCanvas.width) / Number(activeCanvas.height)
          : MARKUP_DEFAULT_ASPECT_RATIO;

      const cropped = cropCanvasToAspectRatio(capturedCanvas, activeAspectRatio);
      return cropped.canvas.toDataURL("image/png");
    },
    [captureCanvasSize.height, captureCanvasSize.width, cropCanvasToAspectRatio, getViewerScreenshot],
  );

  const bakeMeasureLabelsIntoOverlayDataUrl = useCallback(
    async (
      baseOverlayUrl,
      measuredFrameUrl,
      labels,
      fallbackWidth = 0,
      fallbackHeight = 0,
    ) => {
      if (!Array.isArray(labels) || !labels.length) {
        return baseOverlayUrl;
      }

      let canvasWidth = 0;
      let canvasHeight = 0;
      let baseImage = null;
      let measuredImage = null;

      if (baseOverlayUrl) {
        try {
          baseImage = await loadImage(baseOverlayUrl);
          canvasWidth = baseImage.width;
          canvasHeight = baseImage.height;
        } catch (err) {
          baseImage = null;
        }
      }

      if (measuredFrameUrl) {
        try {
          measuredImage = await loadImage(measuredFrameUrl);
          if (!canvasWidth || !canvasHeight) {
            canvasWidth = measuredImage.width;
            canvasHeight = measuredImage.height;
          }
        } catch (err) {
          measuredImage = null;
        }
      }

      if (!canvasWidth || !canvasHeight) {
        const firstLabel = labels[0] || {};
        canvasWidth = Number(firstLabel.sourceWidth || fallbackWidth || 0);
        canvasHeight = Number(firstLabel.sourceHeight || fallbackHeight || 0);
      }

      if (!canvasWidth || !canvasHeight) {
        return baseOverlayUrl;
      }

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return baseOverlayUrl;
      }

      if (baseImage) {
        ctx.drawImage(baseImage, 0, 0, canvasWidth, canvasHeight);
      }

      if (!measuredImage) {
        return canvas.toDataURL("image/png");
      }

      labels.forEach((label) => {
        if (!label?.text || !label.width || !label.height) {
          return;
        }

        const scaleX = label.sourceWidth ? canvasWidth / label.sourceWidth : 1;
        const scaleY = label.sourceHeight
          ? canvasHeight / label.sourceHeight
          : 1;

        const srcX = Math.max(0, Math.floor(label.x || 0));
        const srcY = Math.max(0, Math.floor(label.y || 0));
        const srcW = Math.max(1, Math.ceil(label.width));
        const srcH = Math.max(1, Math.ceil(label.height));

        const drawW = Math.max(1, Math.round(srcW * scaleX));
        const drawH = Math.max(1, Math.round(srcH * scaleY));
        const drawX = Math.max(0, Math.round(srcX * scaleX));
        const drawY = Math.max(0, Math.round(srcY * scaleY));

        const clampedSrcW = Math.max(
          1,
          Math.min(srcW, measuredImage.width - srcX),
        );
        const clampedSrcH = Math.max(
          1,
          Math.min(srcH, measuredImage.height - srcY),
        );

        ctx.drawImage(
          measuredImage,
          srcX,
          srcY,
          clampedSrcW,
          clampedSrcH,
          drawX,
          drawY,
          drawW,
          drawH,
        );
      });

      return canvas.toDataURL("image/png");
    },
    [loadImage],
  );

  const extractOverlayDiffDataUrl = useCallback(
    async (withMeasureUrl, cleanUrl) => {
      const [withMeasureImg, cleanImg] = await Promise.all([
        loadImage(withMeasureUrl),
        loadImage(cleanUrl),
      ]);

      const width = withMeasureImg.width;
      const height = withMeasureImg.height;

      const withCanvas = document.createElement("canvas");
      withCanvas.width = width;
      withCanvas.height = height;
      const withCtx = withCanvas.getContext("2d");
      withCtx.drawImage(withMeasureImg, 0, 0, width, height);
      const withData = withCtx.getImageData(0, 0, width, height).data;

      const cleanCanvas = document.createElement("canvas");
      cleanCanvas.width = width;
      cleanCanvas.height = height;
      const cleanCtx = cleanCanvas.getContext("2d");
      cleanCtx.drawImage(cleanImg, 0, 0, width, height);
      const cleanData = cleanCtx.getImageData(0, 0, width, height).data;

      const outCanvas = document.createElement("canvas");
      outCanvas.width = width;
      outCanvas.height = height;
      const outCtx = outCanvas.getContext("2d");
      const outImage = outCtx.createImageData(width, height);
      const outData = outImage.data;

      // Keep pixels that differ between measured and clean frames.
      // Lower threshold + light dilation helps preserve thin measurement glyphs.
      const threshold = 8;
      const changedMask = new Uint8Array(width * height);

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixelIndex = y * width + x;
          const i = pixelIndex * 4;

          const dr = Math.abs(withData[i] - cleanData[i]);
          const dg = Math.abs(withData[i + 1] - cleanData[i + 1]);
          const db = Math.abs(withData[i + 2] - cleanData[i + 2]);
          const da = Math.abs(withData[i + 3] - cleanData[i + 3]);
          const delta = dr + dg + db + da;

          if (delta > threshold) {
            changedMask[pixelIndex] = 1;
          }
        }
      }

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixelIndex = y * width + x;

          let keepPixel = changedMask[pixelIndex] === 1;
          if (!keepPixel) {
            for (
              let ny = Math.max(0, y - 1);
              ny <= Math.min(height - 1, y + 1);
              ny += 1
            ) {
              let foundChangedNeighbor = false;
              for (
                let nx = Math.max(0, x - 1);
                nx <= Math.min(width - 1, x + 1);
                nx += 1
              ) {
                const neighborIndex = ny * width + nx;
                if (changedMask[neighborIndex] === 1) {
                  keepPixel = true;
                  foundChangedNeighbor = true;
                  break;
                }
              }

              if (foundChangedNeighbor) {
                break;
              }
            }
          }

          const i = pixelIndex * 4;
          if (keepPixel) {
            outData[i] = withData[i];
            outData[i + 1] = withData[i + 1];
            outData[i + 2] = withData[i + 2];
            outData[i + 3] = 255;
          } else {
            outData[i] = 0;
            outData[i + 1] = 0;
            outData[i + 2] = 0;
            outData[i + 3] = 0;
          }
        }
      }

      outCtx.putImageData(outImage, 0, 0);
      return outCanvas.toDataURL("image/png");
    },
    [loadImage],
  );

  const mergeOverlayDataUrls = useCallback(
    async (baseOverlayUrl, addOverlayUrl) => {
      if (!baseOverlayUrl) {
        return addOverlayUrl;
      }
      if (!addOverlayUrl) {
        return baseOverlayUrl;
      }

      const [baseImg, addImg] = await Promise.all([
        loadImage(baseOverlayUrl),
        loadImage(addOverlayUrl),
      ]);

      const width = baseImg.width;
      const height = baseImg.height;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(baseImg, 0, 0, width, height);
      ctx.drawImage(addImg, 0, 0, width, height);
      return canvas.toDataURL("image/png");
    },
    [loadImage],
  );

  const captureCurrentOverlayDataUrl = useCallback(() => {
    const drawCanvas = drawCanvasRef.current;
    const entityCanvas = entityCanvasRef.current;
    if (!drawCanvas || !entityCanvas) {
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = drawCanvas.width;
    canvas.height = drawCanvas.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(drawCanvas, 0, 0, canvas.width, canvas.height);
    ctx.drawImage(entityCanvas, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  }, []);

  const clearMeasureState = useCallback((viewer, measureExt) => {
    try {
      const callCandidates = (target) => {
        if (!target) {
          return;
        }

        const methodNames = [
          "clear",
          "clearMeasurement",
          "clearMeasurements",
          "clearCurrentMeasurement",
          "deleteCurrentMeasurement",
          "removeCurrentMeasurement",
          "removeAllMeasurements",
          "reset",
          "resetMeasurements",
          "cancel",
          "cancelMeasurement",
          "destroyMeasurement",
        ];

        methodNames.forEach((name) => {
          if (typeof target?.[name] === "function") {
            try {
              target[name]();
            } catch (err) {
              // Ignore signature/version differences.
            }
          }
        });
      };

      const tool = measureExt?.measureTool;
      const indicator = tool?.indicator;

      [measureExt, tool, indicator].forEach((target) => {
        callCandidates(target);
      });

      viewer?.impl?.invalidate?.(true, true, true);
    } catch (err) {
      // Ignore version-specific measure internals.
    }
  }, []);

  const rotatePoint = useCallback((point, center, angle) => {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    return {
      x: center.x + dx * Math.cos(angle) - dy * Math.sin(angle),
      y: center.y + dx * Math.sin(angle) + dy * Math.cos(angle),
    };
  }, []);

  const getEntityCenter = useCallback(
    (entity, ctx) => {
      const bounds = getEntityBounds(entity, ctx);
      return {
        x: (bounds.left + bounds.right) / 2,
        y: (bounds.top + bounds.bottom) / 2,
        bounds,
      };
    },
    [getEntityBounds],
  );

  const getRotateHandlePoint = useCallback(
    (entity, ctx) => {
      const { x: cx, y: cy, bounds } = getEntityCenter(entity, ctx);
      const rotation = entity.rotation || 0;
      const topMid = { x: (bounds.left + bounds.right) / 2, y: bounds.top };
      const topMidRotated = rotatePoint(topMid, { x: cx, y: cy }, rotation);
      return {
        x: topMidRotated.x + 22 * Math.sin(rotation),
        y: topMidRotated.y - 22 * Math.cos(rotation),
      };
    },
    [getEntityCenter, rotatePoint],
  );

  const getRectCalloutTailHandlePoint = useCallback(
    (entity, ctx) => {
      if (entity.type !== TOOL_IDS.RECT_CALLOUT || !entity.from || !entity.to) {
        return null;
      }

      const { x: cx, y: cy } = getEntityCenter(entity, ctx);
      const geometry = getRectCalloutGeometry({
        from: entity.from,
        to: entity.to,
        tailEdge: entity.tailEdge,
        tailTip: entity.tailTip,
      });

      return rotatePoint(
        geometry.tailTip,
        { x: cx, y: cy },
        entity.rotation || 0,
      );
    },
    [TOOL_IDS.RECT_CALLOUT, getEntityCenter, rotatePoint],
  );

  const getSelectionGeometry = useCallback(
    (entity, ctx) => {
      const { x: cx, y: cy, bounds } = getEntityCenter(entity, ctx);
      const rotation = entity.rotation || 0;
      const corners = [
        { x: bounds.left, y: bounds.top },
        { x: bounds.right, y: bounds.top },
        { x: bounds.right, y: bounds.bottom },
        { x: bounds.left, y: bounds.bottom },
      ].map((p) => rotatePoint(p, { x: cx, y: cy }, rotation));

      const topMid = {
        x: (corners[0].x + corners[1].x) / 2,
        y: (corners[0].y + corners[1].y) / 2,
      };
      const rotateHandle = {
        x: topMid.x + 22 * Math.sin(rotation),
        y: topMid.y - 22 * Math.cos(rotation),
      };

      return {
        bounds,
        center: { x: cx, y: cy },
        rotation,
        corners,
        rotateHandle,
        tailHandle: getRectCalloutTailHandlePoint(entity, ctx),
      };
    },
    [getEntityCenter, getRectCalloutTailHandlePoint, rotatePoint],
  );

  const scaleEntity = useCallback(
    (entity, ctx, scaleX, scaleY) => {
      const sx = Math.max(0.1, scaleX);
      const sy = Math.max(0.1, scaleY);
      const next = cloneEntity(entity);
      const { x: cx, y: cy } = getEntityCenter(entity, ctx);
      const center = { x: cx, y: cy };
      const rotation = entity.rotation || 0;

      const transformPoint = (point) => {
        const local = rotatePoint(point, center, -rotation);
        const scaledLocal = {
          x: cx + (local.x - cx) * sx,
          y: cy + (local.y - cy) * sy,
        };
        return rotatePoint(scaledLocal, center, rotation);
      };

      if (next.points?.length) {
        next.points = next.points.map(transformPoint);
      }
      if (next.from) {
        next.from = transformPoint(next.from);
      }
      if (next.to) {
        next.to = transformPoint(next.to);
      }
      if (next.point) {
        next.point = transformPoint(next.point);
      }

      const scaleAvg = (sx + sy) / 2;
      if (typeof next.penSize === "number") {
        next.penSize = Math.max(1, Math.min(48, next.penSize * scaleAvg));
      }
      if (next.type === TOOL_IDS.TEXT) {
        next.fontSize = Math.max(
          8,
          Math.min(200, (next.fontSize || 16) * scaleAvg),
        );
      }

      return next;
    },
    [TOOL_IDS.TEXT, cloneEntity, getEntityCenter, rotatePoint],
  );

  const distancePointToSegment = useCallback((point, start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;

    if (dx === 0 && dy === 0) {
      return Math.hypot(point.x - start.x, point.y - start.y);
    }

    const t =
      ((point.x - start.x) * dx + (point.y - start.y) * dy) /
      (dx * dx + dy * dy);
    const clamped = Math.max(0, Math.min(1, t));
    const closestX = start.x + clamped * dx;
    const closestY = start.y + clamped * dy;

    return Math.hypot(point.x - closestX, point.y - closestY);
  }, []);

  const isPointNearPolyline = useCallback(
    (point, points, tolerance, closed = false) => {
      if (!Array.isArray(points) || points.length < 2) {
        return false;
      }

      for (let i = 1; i < points.length; i += 1) {
        if (
          distancePointToSegment(point, points[i - 1], points[i]) <= tolerance
        ) {
          return true;
        }
      }

      if (closed) {
        return (
          distancePointToSegment(point, points[points.length - 1], points[0]) <=
          tolerance
        );
      }

      return false;
    },
    [distancePointToSegment],
  );

  const isPointNearEllipseStroke = useCallback((point, from, to, tolerance) => {
    const cx = (from.x + to.x) / 2;
    const cy = (from.y + to.y) / 2;
    const rx = Math.abs(to.x - from.x) / 2;
    const ry = Math.abs(to.y - from.y) / 2;

    if (rx < 1 || ry < 1) {
      return false;
    }

    const normalizedDx = (point.x - cx) / rx;
    const normalizedDy = (point.y - cy) / ry;
    const distanceFromCenter = Math.hypot(normalizedDx, normalizedDy);

    const avgRadius = (rx + ry) / 2;
    const toleranceRatio = tolerance / Math.max(avgRadius, 1);

    return Math.abs(distanceFromCenter - 1) <= toleranceRatio;
  }, []);

  const getStrokeHitTolerance = useCallback((entity) => {
    return Math.max(6, (entity.penSize || 2) * 1.75);
  }, []);

  const hitTestEntity = useCallback(
    (point, entity, ctx) => {
      const { x: cx, y: cy, bounds } = getEntityCenter(entity, ctx);
      const rotation = entity.rotation || 0;
      const local = rotatePoint(point, { x: cx, y: cy }, -rotation);

      const tolerance = getStrokeHitTolerance(entity);

      if (
        entity.type === TOOL_IDS.FREEHAND ||
        entity.type === TOOL_IDS.POLYLINE ||
        entity.type === TOOL_IDS.HIGHLIGHT
      ) {
        return isPointNearPolyline(
          local,
          entity.points || [],
          tolerance,
          false,
        );
      }

      if (entity.type === TOOL_IDS.ARROW && entity.from && entity.to) {
        const from = entity.from;
        const to = entity.to;
        const headLength = Math.max(12, (entity.penSize || 2) * 4);
        const headWidth = Math.max(8, (entity.penSize || 2) * 2.4);
        const angle = Math.atan2(to.y - from.y, to.x - from.x);
        const base = {
          x: to.x - headLength * Math.cos(angle),
          y: to.y - headLength * Math.sin(angle),
        };
        const normalX = Math.sin(angle);
        const normalY = -Math.cos(angle);
        const left = {
          x: base.x + (headWidth / 2) * normalX,
          y: base.y + (headWidth / 2) * normalY,
        };
        const right = {
          x: base.x - (headWidth / 2) * normalX,
          y: base.y - (headWidth / 2) * normalY,
        };

        return (
          distancePointToSegment(local, from, base) <= tolerance ||
          distancePointToSegment(local, to, left) <= tolerance ||
          distancePointToSegment(local, left, right) <= tolerance ||
          distancePointToSegment(local, right, to) <= tolerance
        );
      }

      if (
        (entity.type === TOOL_IDS.RECTANGLE ||
          entity.type === TOOL_IDS.TRIANGLE ||
          entity.type === TOOL_IDS.REDACT) &&
        entity.from &&
        entity.to
      ) {
        if (entity.type === TOOL_IDS.TRIANGLE) {
          const left = Math.min(entity.from.x, entity.to.x);
          const top = Math.min(entity.from.y, entity.to.y);
          const right = Math.max(entity.from.x, entity.to.x);
          const bottom = Math.max(entity.from.y, entity.to.y);
          const points = [
            { x: (left + right) / 2, y: top },
            { x: right, y: bottom },
            { x: left, y: bottom },
          ];
          return isPointNearPolyline(local, points, tolerance, true);
        }

        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const right = Math.max(entity.from.x, entity.to.x);
        const bottom = Math.max(entity.from.y, entity.to.y);
        const corners = [
          { x: left, y: top },
          { x: right, y: top },
          { x: right, y: bottom },
          { x: left, y: bottom },
        ];
        return isPointNearPolyline(local, corners, tolerance, true);
      }

      if (entity.type === TOOL_IDS.LINE && entity.from && entity.to) {
        return (
          distancePointToSegment(local, entity.from, entity.to) <= tolerance
        );
      }

      if (entity.type === TOOL_IDS.CROSS && entity.from && entity.to) {
        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const right = Math.max(entity.from.x, entity.to.x);
        const bottom = Math.max(entity.from.y, entity.to.y);

        return (
          distancePointToSegment(
            local,
            { x: left, y: top },
            { x: right, y: bottom },
          ) <= tolerance ||
          distancePointToSegment(
            local,
            { x: right, y: top },
            { x: left, y: bottom },
          ) <= tolerance
        );
      }

      if (entity.type === TOOL_IDS.RECT_CALLOUT && entity.from && entity.to) {
        const geometry = getRectCalloutGeometry({
          from: entity.from,
          to: entity.to,
          tailEdge: entity.tailEdge,
          tailTip: entity.tailTip,
        });
        return isPointNearPolyline(local, geometry.pathPoints, tolerance, true);
      }

      if (entity.type === TOOL_IDS.ELLIPSE && entity.from && entity.to) {
        return isPointNearEllipseStroke(
          local,
          entity.from,
          entity.to,
          tolerance,
        );
      }

      if (entity.type === TOOL_IDS.CLOUD && entity.from && entity.to) {
        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const right = Math.max(entity.from.x, entity.to.x);
        const bottom = Math.max(entity.from.y, entity.to.y);
        const corners = [
          { x: left, y: top },
          { x: right, y: top },
          { x: right, y: bottom },
          { x: left, y: bottom },
        ];
        return isPointNearPolyline(local, corners, tolerance, true);
      }

      if (entity.type === TOOL_IDS.CALLOUT && entity.point) {
        const radius = Math.max(10, (entity.penSize || 2) * 3);
        const distance = Math.hypot(
          local.x - entity.point.x,
          local.y - entity.point.y,
        );
        return Math.abs(distance - radius) <= tolerance;
      }

      return (
        local.x >= bounds.left &&
        local.x <= bounds.right &&
        local.y >= bounds.top &&
        local.y <= bounds.bottom
      );
    },
    [
      TOOL_IDS.ARROW,
      TOOL_IDS.CALLOUT,
      TOOL_IDS.CLOUD,
      TOOL_IDS.CROSS,
      TOOL_IDS.ELLIPSE,
      TOOL_IDS.FREEHAND,
      TOOL_IDS.HIGHLIGHT,
      TOOL_IDS.LINE,
      TOOL_IDS.POLYLINE,
      TOOL_IDS.RECT_CALLOUT,
      TOOL_IDS.RECTANGLE,
      TOOL_IDS.REDACT,
      TOOL_IDS.TRIANGLE,
      distancePointToSegment,
      getEntityCenter,
      getRectCalloutGeometry,
      getStrokeHitTolerance,
      isPointNearEllipseStroke,
      isPointNearPolyline,
      rotatePoint,
    ],
  );

  const findTopEntityAtPoint = useCallback(
    (point, ctx) => {
      for (let i = entitiesRef.current.length - 1; i >= 0; i -= 1) {
        const entity = entitiesRef.current[i];
        if (hitTestEntity(point, entity, ctx)) {
          return entity;
        }
      }
      return null;
    },
    [hitTestEntity],
  );

  const findRectCalloutTailHandleAtPoint = useCallback(
    (point, ctx) => {
      for (let i = entitiesRef.current.length - 1; i >= 0; i -= 1) {
        const entity = entitiesRef.current[i];
        if (entity?.type !== TOOL_IDS.RECT_CALLOUT) {
          continue;
        }

        const tailHandle = getRectCalloutTailHandlePoint(entity, ctx);
        if (!tailHandle) {
          continue;
        }

        const distance = Math.hypot(
          point.x - tailHandle.x,
          point.y - tailHandle.y,
        );

        if (distance <= 12) {
          return entity;
        }
      }

      return null;
    },
    [TOOL_IDS.RECT_CALLOUT, getRectCalloutTailHandlePoint],
  );

  const saveUndoState = useCallback(() => {
    undoStackRef.current = [
      ...undoStackRef.current,
      {
        entities: entitiesRef.current.map((entity) => cloneEntity(entity)),
        calloutCounter: calloutCounterRef.current,
      },
    ];
    setUndoStack(undoStackRef.current);
  }, [cloneEntity]);

  const nextEntityId = useCallback(() => {
    const id = `entity-${entityIdCounterRef.current}`;
    entityIdCounterRef.current += 1;
    return id;
  }, []);

  const requestImagePlacementFile = useCallback(() => {
    if (!isMarkupMode || !screenshotUrl) {
      return;
    }

    const input = imageUploadInputRef.current;
    if (!input) {
      return;
    }

    setImageUploadMode("insert");
    setPendingReplaceImageEntityId(null);
    input.value = "";
    input.click();
  }, [isMarkupMode, screenshotUrl]);

  const requestImageReplacementFile = useCallback(() => {
    if (!isMarkupMode || !screenshotUrl) {
      return;
    }

    const selectedId = selectedEntityIdRef.current;
    if (!selectedId) {
      return;
    }

    const selected = entitiesRef.current.find((entity) => entity.id === selectedId);
    if (!selected || selected.type !== TOOL_IDS.IMAGE) {
      return;
    }

    const input = imageUploadInputRef.current;
    if (!input) {
      return;
    }

    setImageUploadMode("replace");
    setPendingReplaceImageEntityId(selectedId);
    input.value = "";
    input.click();
  }, [isMarkupMode, screenshotUrl]);

  const handleImageUploadSelection = useCallback((event) => {
    const file = event.target?.files?.[0];
    if (!file) {
      return;
    }

    if (!String(file.type || "").startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (!dataUrl) {
        setError("Unable to read selected image.");
        return;
      }

      const probe = new Image();
      probe.onload = () => {
        if (imageUploadMode === "replace" && pendingReplaceImageEntityId) {
          saveUndoState();
          setEntities((prev) =>
            prev.map((entity) => {
              if (
                entity.id !== pendingReplaceImageEntityId ||
                entity.type !== TOOL_IDS.IMAGE
              ) {
                return entity;
              }

              return {
                ...entity,
                imageDataUrl: dataUrl,
                imageName: file.name || "Uploaded image",
                imageNaturalWidth: Number(probe.width || 0),
                imageNaturalHeight: Number(probe.height || 0),
              };
            }),
          );
          setPendingReplaceImageEntityId(null);
          setImageUploadMode("insert");
          setError(null);
          return;
        }

        setPendingImagePlacement({
          imageDataUrl: dataUrl,
          imageName: file.name || "Uploaded image",
          naturalWidth: Number(probe.width || 0),
          naturalHeight: Number(probe.height || 0),
        });
        setActiveTool(TOOL_IDS.IMAGE);
        setError(null);
      };

      probe.onerror = () => {
        setError("Unable to load selected image.");
      };

      probe.src = dataUrl;
    };

    reader.onerror = () => {
      setError("Unable to read selected image.");
    };

    reader.readAsDataURL(file);
  }, [imageUploadMode, pendingReplaceImageEntityId, saveUndoState]);

  const handlePenColorChange = useCallback((value) => {
    setPenColor(value);

    const selectedId = selectedEntityIdRef.current;
    if (!selectedId) {
      return;
    }

    setEntities((prev) =>
      prev.map((entity) =>
        entity.id === selectedId ? { ...entity, penColor: value } : entity,
      ),
    );
  }, []);

  const handleFillColorChange = useCallback((value) => {
    setFillColor(value);

    const selectedId = selectedEntityIdRef.current;
    if (!selectedId) {
      return;
    }

    setEntities((prev) =>
      prev.map((entity) => {
        if (
          entity.id !== selectedId ||
          !FILLABLE_TOOL_IDS.includes(entity.type)
        ) {
          return entity;
        }
        return {
          ...entity,
          fillEnabled: true,
          fillColor: value,
        };
      }),
    );
  }, []);

  const openFillColorMenu = useCallback(() => {
    const selectedId = selectedEntityIdRef.current;
    if (!selectedId) {
      return;
    }

    const selected = entitiesRef.current.find(
      (entity) => entity.id === selectedId,
    );
    if (!selected || !FILLABLE_TOOL_IDS.includes(selected.type)) {
      return;
    }

    saveUndoState();
    setEntities((prev) =>
      prev.map((entity) => {
        if (
          entity.id !== selectedId ||
          !FILLABLE_TOOL_IDS.includes(entity.type)
        ) {
          return entity;
        }

        return {
          ...entity,
          fillEnabled: true,
          fillColor: entity.fillColor || fillColor,
        };
      }),
    );

    window.requestAnimationFrame(() => {
      fillColorInputRef.current?.click();
    });
  }, [fillColor, saveUndoState]);

  const disableSelectedFill = useCallback(() => {
    const selectedId = selectedEntityIdRef.current;
    if (!selectedId) {
      return;
    }

    const selected = entitiesRef.current.find(
      (entity) => entity.id === selectedId,
    );
    if (!selected || !FILLABLE_TOOL_IDS.includes(selected.type)) {
      return;
    }

    saveUndoState();
    setEntities((prev) =>
      prev.map((entity) => {
        if (
          entity.id !== selectedId ||
          !FILLABLE_TOOL_IDS.includes(entity.type)
        ) {
          return entity;
        }

        return {
          ...entity,
          fillEnabled: false,
        };
      }),
    );
  }, [saveUndoState]);

  const zoomInCanvas = useCallback(() => {
    const viewport = zoomViewportRef.current;
    const stage =
      entityCanvasRef.current || drawCanvasRef.current || imageCanvasRef.current;
    const width = stage?.clientWidth || viewport?.clientWidth || 0;
    const height = stage?.clientHeight || viewport?.clientHeight || 0;
    setZoomLevel((prev) => {
      const next = Math.min(MARKUP_CANVAS_MAX_ZOOM, Number((prev + 0.2).toFixed(2)));
      setZoomOffset((prevOffset) => {
        const minX = width - width * next;
        const minY = height - height * next;
        return {
          x: Math.min(0, Math.max(minX, prevOffset.x)),
          y: Math.min(0, Math.max(minY, prevOffset.y)),
        };
      });
      return next;
    });
  }, []);

  const zoomOutCanvas = useCallback(() => {
    const viewport = zoomViewportRef.current;
    const stage =
      entityCanvasRef.current || drawCanvasRef.current || imageCanvasRef.current;
    const width = stage?.clientWidth || viewport?.clientWidth || 0;
    const height = stage?.clientHeight || viewport?.clientHeight || 0;
    setZoomLevel((prev) => {
      const next = Math.max(1, Number((prev - 0.2).toFixed(2)));
      setZoomOffset((prevOffset) => {
        const minX = width - width * next;
        const minY = height - height * next;
        return {
          x: Math.min(0, Math.max(minX, prevOffset.x)),
          y: Math.min(0, Math.max(minY, prevOffset.y)),
        };
      });
      return next;
    });
  }, []);

  const resetCanvasZoom = useCallback(() => {
    setZoomLevel(1);
    setZoomOffset({ x: 0, y: 0 });
  }, []);

  const handleCanvasWheelZoom = useCallback((event) => {
    if (!sheetsSelected) {
      return;
    }

    event.preventDefault();

    const stage =
      entityCanvasRef.current || drawCanvasRef.current || imageCanvasRef.current;
    const stageRect = stage?.getBoundingClientRect?.();
    if (!stageRect?.width || !stageRect?.height) {
      return;
    }

    const pointerX = Math.max(
      0,
      Math.min(stageRect.width, event.clientX - stageRect.left),
    );
    const pointerY = Math.max(
      0,
      Math.min(stageRect.height, event.clientY - stageRect.top),
    );

    const delta = event.deltaY < 0 ? 0.1 : -0.1;
    setZoomLevel((prevZoom) => {
      const nextZoom = Math.max(
        1,
        Math.min(MARKUP_CANVAS_MAX_ZOOM, Number((prevZoom + delta).toFixed(2))),
      );

      if (nextZoom === prevZoom) {
        return prevZoom;
      }

      setZoomOffset((prevOffset) => {
        const worldX = (pointerX - prevOffset.x) / prevZoom;
        const worldY = (pointerY - prevOffset.y) / prevZoom;
        const minX = stageRect.width - stageRect.width * nextZoom;
        const minY = stageRect.height - stageRect.height * nextZoom;
        const nextOffsetX = pointerX - worldX * nextZoom;
        const nextOffsetY = pointerY - worldY * nextZoom;
        return {
          x: Math.min(0, Math.max(minX, nextOffsetX)),
          y: Math.min(0, Math.max(minY, nextOffsetY)),
        };
      });

      return nextZoom;
    });
  }, [sheetsSelected]);

  const setCanvasCursorIfNeeded = useCallback((nextCursor) => {
    if (canvasCursorRef.current === nextCursor) {
      return;
    }
    canvasCursorRef.current = nextCursor;
    setCanvasCursor(nextCursor);
  }, []);

  const stopMiddleMousePan = useCallback(() => {
    if (!middleMousePanRef.current.active) {
      return;
    }

    middleMousePanRef.current = {
      active: false,
      startClientX: 0,
      startClientY: 0,
      startOffsetX: 0,
      startOffsetY: 0,
    };

    if (!isMarkupMode || isMeasuring) {
      return;
    }

    if (activeTool === TOOL_IDS.SELECT) {
      setCanvasCursorIfNeeded("default");
      return;
    }

    if (activeTool === TOOL_IDS.IMAGE) {
      setCanvasCursorIfNeeded(
        pendingImagePlacement?.imageDataUrl ? "copy" : "pointer",
      );
      return;
    }

    setCanvasCursorIfNeeded("crosshair");
  }, [
    activeTool,
    isMarkupMode,
    isMeasuring,
    pendingImagePlacement,
    setCanvasCursorIfNeeded,
  ]);

  const handleMiddleMousePanMove = useCallback(
    (event) => {
      const panState = middleMousePanRef.current;
      if (!panState.active || !sheetsSelected) {
        return;
      }

      event.preventDefault();

      const viewport = zoomViewportRef.current;
      const stage =
        entityCanvasRef.current || drawCanvasRef.current || imageCanvasRef.current;
      const width = stage?.clientWidth || viewport?.clientWidth || 0;
      const height = stage?.clientHeight || viewport?.clientHeight || 0;
      if (!width || !height) {
        return;
      }

      const nextOffsetX =
        panState.startOffsetX + (event.clientX - panState.startClientX);
      const nextOffsetY =
        panState.startOffsetY + (event.clientY - panState.startClientY);
      const minX = width - width * zoomLevel;
      const minY = height - height * zoomLevel;

      setZoomOffset({
        x: Math.min(0, Math.max(minX, nextOffsetX)),
        y: Math.min(0, Math.max(minY, nextOffsetY)),
      });
    },
    [sheetsSelected, zoomLevel],
  );

  useEffect(() => {
    if (!isMarkupMode || !sheetsSelected) {
      return undefined;
    }

    const handleWindowMouseMove = (event) => {
      handleMiddleMousePanMove(event);
    };
    const handleWindowMouseUp = () => {
      stopMiddleMousePan();
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);
    window.addEventListener("blur", handleWindowMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
      window.removeEventListener("blur", handleWindowMouseUp);
      stopMiddleMousePan();
    };
  }, [handleMiddleMousePanMove, isMarkupMode, sheetsSelected, stopMiddleMousePan]);

  useEffect(() => {
    if (!isMarkupMode || isMeasuring) {
      return;
    }

    if (activeTool === TOOL_IDS.SELECT) {
      setCanvasCursorIfNeeded("default");
      return;
    }

    if (activeTool === TOOL_IDS.IMAGE) {
      setCanvasCursorIfNeeded(
        pendingImagePlacement?.imageDataUrl ? "copy" : "pointer",
      );
      return;
    }

    setCanvasCursorIfNeeded("crosshair");
  }, [
    activeTool,
    isMarkupMode,
    isMeasuring,
    pendingImagePlacement,
    setCanvasCursorIfNeeded,
  ]);

  const shouldAutoReturnToSelect = useCallback(
    (toolId) =>
      toolId !== TOOL_IDS.SELECT &&
      toolId !== TOOL_IDS.FREEHAND &&
      toolId !== TOOL_IDS.HIGHLIGHT,
    [],
  );

  const getDefaultEntityPenSize = useCallback(
    (toolId) => {
      if (!sheetsSelected) {
        return penSize;
      }

      if (toolId === TOOL_IDS.STAMP) {
        return Math.max(penSize, MARKUP_2D_STAMP_DEFAULT_PEN_SIZE);
      }

      if (toolId === TOOL_IDS.CALLOUT) {
        return Math.max(penSize, MARKUP_2D_CALLOUT_DEFAULT_PEN_SIZE);
      }

      return penSize;
    },
    [penSize, sheetsSelected],
  );

  const startDrawing = useCallback(
    (event) => {
      if (!screenshotUrl) return;

      // Let the focused textarea blur naturally so click-off commits text.
      if (textEditor.visible) {
        return;
      }

      event.preventDefault();

      const canvas = entityCanvasRef.current;
      if (!canvas) {
        return;
      }

      if (!event?.touches && !event?.changedTouches && event.button === 1) {
        if (!sheetsSelected) {
          return;
        }

        middleMousePanRef.current = {
          active: true,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startOffsetX: zoomOffset.x,
          startOffsetY: zoomOffset.y,
        };
        setHoveredEntityId(null);
        setCanvasCursorIfNeeded("grabbing");
        return;
      }

      const ctx = canvas.getContext("2d");
      const startPoint = getPointerPosition(event);
      if (!startPoint) {
        return;
      }

      const selectedIds = selectedEntityIdsRef.current;
      const hasSingleSelection = selectedIds.length === 1;
      const selected = hasSingleSelection
        ? entitiesRef.current.find((item) => item.id === selectedEntityIdRef.current)
        : null;
      if (selected) {
        const selection = getSelectionGeometry(selected, ctx);
        const resizeIndex = selection.corners.findIndex(
          (corner) =>
            Math.hypot(startPoint.x - corner.x, startPoint.y - corner.y) <= 10,
        );
        if (resizeIndex >= 0) {
          saveUndoState();
          const startBounds = getEntityBounds(selected, ctx);
          interactionRef.current = {
            mode: "resize",
            entityId: selected.id,
            handleIndex: resizeIndex,
            startPoint,
            startEntity: cloneEntity(selected),
            startAspectRatio:
              Math.max(1, Math.abs(startBounds.right - startBounds.left)) /
              Math.max(1, Math.abs(startBounds.bottom - startBounds.top)),
          };
          return;
        }

        if (selection.tailHandle) {
          const tailDistance = Math.hypot(
            startPoint.x - selection.tailHandle.x,
            startPoint.y - selection.tailHandle.y,
          );
          if (tailDistance <= 10) {
            saveUndoState();
            interactionRef.current = {
              mode: "tail",
              entityId: selected.id,
              startPoint,
              startEntity: cloneEntity(selected),
            };
            return;
          }
        }

        const handle = getRotateHandlePoint(selected, ctx);
        const distance = Math.hypot(
          startPoint.x - handle.x,
          startPoint.y - handle.y,
        );
        if (distance <= 10) {
          const center = getEntityCenter(selected, ctx);
          saveUndoState();
          interactionRef.current = {
            mode: "rotate",
            entityId: selected.id,
            startPoint,
            startEntity: cloneEntity(selected),
            startAngle: Math.atan2(
              startPoint.y - center.y,
              startPoint.x - center.x,
            ),
            startRotation: selected.rotation || 0,
          };
          return;
        }
      }

      const tailHandleEntity = findRectCalloutTailHandleAtPoint(
        startPoint,
        ctx,
      );
      if (tailHandleEntity && isEntityOwnedByCurrentUser(tailHandleEntity)) {
        setEntitySelection([tailHandleEntity.id]);
        saveUndoState();
        interactionRef.current = {
          mode: "tail",
          entityId: tailHandleEntity.id,
          entityIds: null,
          startPoint,
          startEntity: cloneEntity(tailHandleEntity),
          startEntities: null,
          selectionRect: null,
        };
        return;
      }

      const hitEntity = findTopEntityAtPoint(startPoint, ctx);
      if (hitEntity && isEntityOwnedByCurrentUser(hitEntity)) {
        const hitIsInSelection = selectedIds.includes(hitEntity.id);
        if (activeTool === TOOL_IDS.SELECT && hitIsInSelection && selectedIds.length > 1) {
          saveUndoState();
          interactionRef.current = {
            mode: "move-selection",
            entityId: null,
            entityIds: [...selectedIds],
            startPoint,
            startEntity: null,
            startEntities: Object.fromEntries(
              entitiesRef.current
                .filter((entity) => selectedIds.includes(entity.id))
                .map((entity) => [entity.id, cloneEntity(entity)]),
            ),
            selectionRect: null,
          };
          return;
        }

        setEntitySelection([hitEntity.id]);
        saveUndoState();
        interactionRef.current = {
          mode: "move",
          entityId: hitEntity.id,
          entityIds: null,
          startPoint,
          startEntity: cloneEntity(hitEntity),
          startEntities: null,
          selectionRect: null,
        };
        return;
      }

      setEntitySelection([]);

      if (activeTool === TOOL_IDS.SELECT) {
        interactionRef.current = {
          mode: "marquee",
          entityId: null,
          entityIds: null,
          startPoint,
          startEntity: null,
          startEntities: null,
          selectionRect: {
            left: startPoint.x,
            top: startPoint.y,
            width: 0,
            height: 0,
          },
        };
        renderEntities(draftEntityRef.current);
        return;
      }

      if (activeTool === TOOL_IDS.IMAGE) {
        if (!pendingImagePlacement?.imageDataUrl) {
          requestImagePlacementFile();
          return;
        }

        const imageSourceWidth = Math.max(
          1,
          Number(pendingImagePlacement.naturalWidth || 0) || 1200,
        );
        const imageSourceHeight = Math.max(
          1,
          Number(pendingImagePlacement.naturalHeight || 0) || 800,
        );
        const canvasWidth = Number(canvas.width || 0);
        const canvasHeight = Number(canvas.height || 0);
        const maxPlacementWidth = Math.max(90, canvasWidth * 0.42);
        const maxPlacementHeight = Math.max(90, canvasHeight * 0.42);

        const baseScale = Math.min(
          maxPlacementWidth / imageSourceWidth,
          maxPlacementHeight / imageSourceHeight,
          1,
        );

        const placedWidth = Math.max(
          56,
          Math.round(imageSourceWidth * baseScale),
        );
        const placedHeight = Math.max(
          42,
          Math.round(imageSourceHeight * baseScale),
        );

        const left = Math.max(
          4,
          Math.min(startPoint.x - placedWidth / 2, canvasWidth - placedWidth - 4),
        );
        const top = Math.max(
          4,
          Math.min(startPoint.y - placedHeight / 2, canvasHeight - placedHeight - 4),
        );

        saveUndoState();
        const imageEntity = {
          id: nextEntityId(),
          type: TOOL_IDS.IMAGE,
          from: { x: left, y: top },
          to: { x: left + placedWidth, y: top + placedHeight },
          imageDataUrl: pendingImagePlacement.imageDataUrl,
          imageName: pendingImagePlacement.imageName || "Uploaded image",
          imageNaturalWidth: imageSourceWidth,
          imageNaturalHeight: imageSourceHeight,
          penColor,
          penSize,
          ...buildCurrentEntityUserTag(),
          rotation: 0,
        };

        setEntities((prev) => [...prev, imageEntity]);
        setEntitySelection([imageEntity.id]);
        setPendingImagePlacement(null);
        if (shouldAutoReturnToSelect(activeTool)) {
          setActiveTool(TOOL_IDS.SELECT);
        }
        renderEntities(null);
        return;
      }

      if (activeTool === TOOL_IDS.POLYLINE) {
        const currentDraft = draftEntityRef.current;
        if (currentDraft?.type === TOOL_IDS.POLYLINE) {
          const points = currentDraft.points || [];
          if (points.length) {
            const anchoredPoints = points.slice(0, -1);
            const clickPoint = { x: startPoint.x, y: startPoint.y };
            draftEntityRef.current = {
              ...currentDraft,
              points: [...anchoredPoints, clickPoint, clickPoint],
            };
            renderEntities(draftEntityRef.current);
          }
        } else {
          saveUndoState();
          draftEntityRef.current = {
            id: nextEntityId(),
            type: TOOL_IDS.POLYLINE,
            penColor,
            penSize,
            ...buildCurrentEntityUserTag(),
            rotation: 0,
            points: [startPoint, startPoint],
          };
          renderEntities(draftEntityRef.current);
        }
        return;
      }

      if (activeTool === TOOL_IDS.TEXT) {
        setTextEditor({
          visible: true,
          x: startPoint.x,
          y: startPoint.y,
          value: "",
          entityId: null,
          fontSize: null,
          penColor: null,
        });
        return;
      }

      saveUndoState();
      drawingRef.current = true;
      lastPointRef.current = startPoint;

      const defaultEntityPenSize = getDefaultEntityPenSize(activeTool);

      const baseEntity = {
        id: nextEntityId(),
        type: activeTool,
        penColor,
        fillColor,
        fillEnabled: false,
        penSize: defaultEntityPenSize,
        ...buildCurrentEntityUserTag(),
        rotation: 0,
      };

      if (
        activeTool === TOOL_IDS.FREEHAND ||
        activeTool === TOOL_IDS.HIGHLIGHT
      ) {
        draftEntityRef.current = { ...baseEntity, points: [startPoint] };
      } else if (
        activeTool === TOOL_IDS.ARROW ||
        activeTool === TOOL_IDS.LINE ||
        activeTool === TOOL_IDS.CROSS ||
        activeTool === TOOL_IDS.RECTANGLE ||
        activeTool === TOOL_IDS.TRIANGLE ||
        activeTool === TOOL_IDS.RECT_CALLOUT ||
        activeTool === TOOL_IDS.ELLIPSE ||
        activeTool === TOOL_IDS.CLOUD ||
        activeTool === TOOL_IDS.REDACT
      ) {
        draftEntityRef.current = {
          ...baseEntity,
          from: startPoint,
          to: startPoint,
          ...(activeTool === TOOL_IDS.RECT_CALLOUT
            ? {
                tailEdge: getRectCalloutTailEdge({
                  from: startPoint,
                  to: startPoint,
                }),
              }
            : {}),
        };
      } else if (activeTool === TOOL_IDS.CALLOUT) {
        draftEntityRef.current = {
          ...baseEntity,
          point: startPoint,
          number: getNextCalloutNumber(),
        };
      } else if (activeTool === TOOL_IDS.STAMP) {
        draftEntityRef.current = {
          ...baseEntity,
          point: startPoint,
          text: getStampText(),
        };
      }

      renderEntities(draftEntityRef.current);
    },
    [
      activeTool,
      cloneEntity,
      findRectCalloutTailHandleAtPoint,
      findTopEntityAtPoint,
      getEntityBounds,
      getEntityCenter,
      getSelectionGeometry,
      getNextCalloutNumber,
      getPointerPosition,
      getRectCalloutTailEdge,
      getRotateHandlePoint,
      getStampText,
      buildCurrentEntityUserTag,
      isEntityOwnedByCurrentUser,
      fillColor,
      getDefaultEntityPenSize,
      pendingImagePlacement,
      nextEntityId,
      penColor,
      penSize,
      renderEntities,
      requestImagePlacementFile,
      saveUndoState,
      setEntitySelection,
      setCanvasCursorIfNeeded,
      shouldAutoReturnToSelect,
      screenshotUrl,
      sheetsSelected,
      textEditor.visible,
      zoomOffset,
    ],
  );

  const handleEntityDoubleClick = useCallback(
    (event) => {
      if (!screenshotUrl) {
        return;
      }

      event.preventDefault();
      const canvas = entityCanvasRef.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext("2d");
      const point = getPointerPosition(event);
      if (!point) {
        return;
      }

      if (
        activeTool === TOOL_IDS.POLYLINE &&
        draftEntityRef.current?.type === TOOL_IDS.POLYLINE
      ) {
        const draftPoints = draftEntityRef.current.points || [];
        const finalizedPoints = draftPoints.slice(0, -1);
        if (finalizedPoints.length >= 2) {
          const finalized = {
            ...cloneEntity(draftEntityRef.current),
            points: finalizedPoints,
          };
          setEntities((prev) => [...prev, finalized]);
          setEntitySelection([finalized.id]);
          if (shouldAutoReturnToSelect(activeTool)) {
            setActiveTool(TOOL_IDS.SELECT);
          }
        }
        draftEntityRef.current = null;
        drawingRef.current = false;
        renderEntities(null);
        return;
      }

      const hitEntity = findTopEntityAtPoint(point, ctx);
      if (
        !hitEntity ||
        (hitEntity.type !== TOOL_IDS.TEXT &&
          hitEntity.type !== TOOL_IDS.RECT_CALLOUT) ||
        !isEntityOwnedByCurrentUser(hitEntity)
      ) {
        return;
      }

      setEntitySelection([hitEntity.id]);
      if (hitEntity.type === TOOL_IDS.RECT_CALLOUT) {
        const textBox = getRectCalloutTextBox(hitEntity);
        const defaultCalloutEditorFont = getDefaultEditorFontSize(
          hitEntity.penSize,
        );
        setTextEditor({
          visible: true,
          x: textBox.x,
          y: textBox.y,
          value: String(hitEntity.text || ""),
          entityId: hitEntity.id,
          fontSize: Boolean(sheetsSelected)
            ? Math.max(hitEntity.fontSize || 0, defaultCalloutEditorFont)
            : hitEntity.fontSize || textBox.fontSize,
          penColor: hitEntity.penColor || penColor,
          width: textBox.width,
          height: textBox.height,
          targetType: hitEntity.type,
        });
        return;
      }

      setTextEditor({
        visible: true,
        x: hitEntity.point?.x ?? point.x,
        y: hitEntity.point?.y ?? point.y,
        value: String(hitEntity.text || ""),
        entityId: hitEntity.id,
        fontSize: hitEntity.fontSize || getDefaultEditorFontSize(hitEntity.penSize),
        penColor: hitEntity.penColor || penColor,
        width: null,
        height: null,
        targetType: hitEntity.type,
      });
    },
    [
      activeTool,
      cloneEntity,
      findTopEntityAtPoint,
      isEntityOwnedByCurrentUser,
      getRectCalloutTextBox,
      getPointerPosition,
      penColor,
      getDefaultEditorFontSize,
      renderEntities,
      sheetsSelected,
      setEntitySelection,
      shouldAutoReturnToSelect,
      screenshotUrl,
    ],
  );

  const draw = useCallback(
    (event) => {
      const canvas = entityCanvasRef.current;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext("2d");
      const nextPoint = getPointerPosition(event);
      if (!nextPoint) {
        return;
      }

      if (!event?.touches && !event?.changedTouches) {
        if (!interactionRef.current.mode && !drawingRef.current) {
          const hoveredEntity = findTopEntityAtPoint(nextPoint, ctx);
          const nextHoveredId = hoveredEntity?.id || null;
          setHoveredEntityId((prev) =>
            prev === nextHoveredId ? prev : nextHoveredId,
          );
        } else {
          setHoveredEntityId((prev) => (prev === null ? prev : null));
        }

        let nextCursor =
          activeTool === TOOL_IDS.SELECT
            ? "default"
            : activeTool === TOOL_IDS.IMAGE
              ? pendingImagePlacement?.imageDataUrl
                ? "copy"
                : "pointer"
              : "crosshair";

        if (interactionRef.current.mode) {
          nextCursor = "grabbing";
        } else {
          const selected =
            selectedEntityIdsRef.current.length === 1
              ? entitiesRef.current.find(
                  (item) => item.id === selectedEntityIdRef.current,
                )
              : null;

          if (selected) {
            const selection = getSelectionGeometry(selected, ctx);
            const overResizeHandle = selection.corners.some(
              (corner) =>
                Math.hypot(nextPoint.x - corner.x, nextPoint.y - corner.y) <=
                10,
            );
            const overRotateHandle =
              Math.hypot(
                nextPoint.x - selection.rotateHandle.x,
                nextPoint.y - selection.rotateHandle.y,
              ) <= 10;
            const overTailHandle =
              selection.tailHandle &&
              Math.hypot(
                nextPoint.x - selection.tailHandle.x,
                nextPoint.y - selection.tailHandle.y,
              ) <= 10;

            if (overResizeHandle || overRotateHandle || overTailHandle) {
              nextCursor = "pointer";
            }
          }

          if (
            nextCursor === "crosshair" ||
            nextCursor === "default" ||
            nextCursor === "copy"
          ) {
            const tailHandleEntity = findRectCalloutTailHandleAtPoint(
              nextPoint,
              ctx,
            );
            const hitEntity =
              tailHandleEntity || findTopEntityAtPoint(nextPoint, ctx);
            if (hitEntity) {
              nextCursor = "pointer";
            }
          }
        }

        setCanvasCursorIfNeeded(nextCursor);
      }

      if (interactionRef.current.mode === "move") {
        event.preventDefault();
        const { startEntity, startPoint, entityId } = interactionRef.current;
        const dx = nextPoint.x - startPoint.x;
        const dy = nextPoint.y - startPoint.y;
        setEntities((prev) =>
          prev.map((entity) =>
            entity.id === entityId
              ? translateEntity(startEntity, dx, dy)
              : entity,
          ),
        );
        return;
      }

      if (interactionRef.current.mode === "move-selection") {
        event.preventDefault();
        const { startPoint, entityIds, startEntities } = interactionRef.current;
        const dx = nextPoint.x - startPoint.x;
        const dy = nextPoint.y - startPoint.y;
        const selectedSet = new Set(entityIds || []);
        setEntities((prev) =>
          prev.map((entity) => {
            if (!selectedSet.has(entity.id)) {
              return entity;
            }
            const baselineEntity = startEntities?.[entity.id] || entity;
            return translateEntity(baselineEntity, dx, dy);
          }),
        );
        return;
      }

      if (interactionRef.current.mode === "marquee") {
        event.preventDefault();
        const startPoint = interactionRef.current.startPoint || nextPoint;
        interactionRef.current = {
          ...interactionRef.current,
          selectionRect: {
            left: Math.min(startPoint.x, nextPoint.x),
            top: Math.min(startPoint.y, nextPoint.y),
            width: Math.abs(nextPoint.x - startPoint.x),
            height: Math.abs(nextPoint.y - startPoint.y),
          },
        };
        renderEntities(draftEntityRef.current);
        return;
      }

      if (interactionRef.current.mode === "rotate") {
        event.preventDefault();
        const { startEntity, startAngle, startRotation, entityId } =
          interactionRef.current;
        const center = getEntityCenter(startEntity, ctx);
        const currentAngle = Math.atan2(
          nextPoint.y - center.y,
          nextPoint.x - center.x,
        );
        const delta = currentAngle - startAngle;
        setEntities((prev) =>
          prev.map((entity) =>
            entity.id === entityId
              ? { ...entity, rotation: startRotation + delta }
              : entity,
          ),
        );
        return;
      }

      if (interactionRef.current.mode === "resize") {
        event.preventDefault();
        const { startEntity, handleIndex, entityId, startAspectRatio } =
          interactionRef.current;
        const { x: cx, y: cy, bounds } = getEntityCenter(startEntity, ctx);
        const rotation = startEntity.rotation || 0;
        const cornerLocals = [
          { x: bounds.left, y: bounds.top },
          { x: bounds.right, y: bounds.top },
          { x: bounds.right, y: bounds.bottom },
          { x: bounds.left, y: bounds.bottom },
        ];
        const startCorner = cornerLocals[handleIndex] || cornerLocals[0];
        const pointerLocal = rotatePoint(
          nextPoint,
          { x: cx, y: cy },
          -rotation,
        );

        const dx0 = startCorner.x - cx;
        const dy0 = startCorner.y - cy;
        const dx = pointerLocal.x - cx;
        const dy = pointerLocal.y - cy;

        let scaleX = Math.max(
          0.1,
          Math.abs(dx0) > 0.001 ? Math.abs(dx / dx0) : 1,
        );
        let scaleY = Math.max(
          0.1,
          Math.abs(dy0) > 0.001 ? Math.abs(dy / dy0) : 1,
        );

        if (
          event?.shiftKey &&
          startEntity?.type === TOOL_IDS.IMAGE &&
          Number.isFinite(startAspectRatio)
        ) {
          const uniformScale = Math.max(scaleX, scaleY);
          scaleX = uniformScale;
          scaleY = uniformScale;
        }

        const resized = scaleEntity(startEntity, ctx, scaleX, scaleY);
        setEntities((prev) =>
          prev.map((entity) => (entity.id === entityId ? resized : entity)),
        );
        return;
      }

      if (interactionRef.current.mode === "tail") {
        event.preventDefault();
        const { startEntity, entityId } = interactionRef.current;
        const { x: cx, y: cy } = getEntityCenter(startEntity, ctx);
        const pointerLocal = rotatePoint(
          nextPoint,
          { x: cx, y: cy },
          -(startEntity.rotation || 0),
        );

        setEntities((prev) =>
          prev.map((entity) =>
            entity.id === entityId
              ? { ...entity, tailTip: pointerLocal }
              : entity,
          ),
        );
        return;
      }

      if (!drawingRef.current || !draftEntityRef.current) {
        if (
          activeTool === TOOL_IDS.POLYLINE &&
          draftEntityRef.current?.type === TOOL_IDS.POLYLINE
        ) {
          event.preventDefault();
          const points = draftEntityRef.current.points || [];
          if (points.length >= 2) {
            const anchoredPoints = points.slice(0, -1);
            draftEntityRef.current = {
              ...draftEntityRef.current,
              points: [...anchoredPoints, nextPoint],
            };
            renderEntities(draftEntityRef.current);
          }
        }
        return;
      }

      event.preventDefault();
      const draft = draftEntityRef.current;
      if (draft.points) {
        const last = draft.points[draft.points.length - 1];
        if (Math.hypot(nextPoint.x - last.x, nextPoint.y - last.y) >= 1) {
          draft.points = [...draft.points, nextPoint];
        }
      } else if (draft.to) {
        draft.to = nextPoint;
        if (draft.type === TOOL_IDS.RECT_CALLOUT) {
          draft.tailEdge = getRectCalloutTailEdge({
            from: draft.from,
            to: nextPoint,
          });
        }
      } else if (draft.point) {
        draft.point = nextPoint;
      }

      lastPointRef.current = nextPoint;
      renderEntities(draftEntityRef.current);
    },
    [
      activeTool,
      findRectCalloutTailHandleAtPoint,
      findTopEntityAtPoint,
      getEntityCenter,
      getPointerPosition,
      getRectCalloutTailEdge,
      getSelectionGeometry,
      pendingImagePlacement,
      renderEntities,
      rotatePoint,
      scaleEntity,
      setCanvasCursorIfNeeded,
      translateEntity,
    ],
  );

  const stopDrawing = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }

      const canvas = entityCanvasRef.current;
      const ctx = canvas?.getContext("2d");

      if (interactionRef.current.mode) {
        if (interactionRef.current.mode === "marquee" && ctx) {
          const selectionRect = interactionRef.current.selectionRect;
          const isValidRect =
            selectionRect &&
            Number(selectionRect.width) >= 2 &&
            Number(selectionRect.height) >= 2;

          const selectedIds = isValidRect
            ? entitiesRef.current
                .filter((entity) => {
                  if (!isEntityOwnedByCurrentUser(entity)) {
                    return false;
                  }
                  const bounds = getEntityBounds(entity, ctx);
                  return !(
                    bounds.right < selectionRect.left ||
                    bounds.left > selectionRect.left + selectionRect.width ||
                    bounds.bottom < selectionRect.top ||
                    bounds.top > selectionRect.top + selectionRect.height
                  );
                })
                .map((entity) => entity.id)
            : [];

          setEntitySelection(selectedIds);
          renderEntities(draftEntityRef.current);
        }

        interactionRef.current = {
          mode: null,
          entityId: null,
          entityIds: null,
          startPoint: null,
          startEntity: null,
          startEntities: null,
          selectionRect: null,
        };
        return;
      }

      if (!drawingRef.current || !draftEntityRef.current) {
        return;
      }

      if (draftEntityRef.current.type === TOOL_IDS.POLYLINE) {
        return;
      }

      const finalized = cloneEntity(draftEntityRef.current);
      if (
        finalized.type === TOOL_IDS.RECT_CALLOUT &&
        finalized.from &&
        finalized.to
      ) {
        const currentWidth = Math.abs(finalized.to.x - finalized.from.x);
        const currentHeight = Math.abs(finalized.to.y - finalized.from.y);

        // Click-only callout placement can leave a near-zero rectangle; expand it
        // to a readable default box while keeping it inside the canvas.
        if (currentWidth < 8 && currentHeight < 8) {
          const defaultWidth = sheetsSelected ? 240 : 180;
          const defaultHeight = sheetsSelected ? 136 : 108;
          const halfWidth = defaultWidth / 2;
          const halfHeight = defaultHeight / 2;
          const centerX = (finalized.from.x + finalized.to.x) / 2;
          const centerY = (finalized.from.y + finalized.to.y) / 2;
          const canvasWidth = Number(canvas?.width || 0);
          const canvasHeight = Number(canvas?.height || 0);

          const minLeft = 8;
          const minTop = 8;
          const maxLeft =
            canvasWidth > 0
              ? Math.max(minLeft, canvasWidth - defaultWidth - 8)
              : centerX - halfWidth;
          const maxTop =
            canvasHeight > 0
              ? Math.max(minTop, canvasHeight - defaultHeight - 8)
              : centerY - halfHeight;

          const left = Math.min(
            Math.max(centerX - halfWidth, minLeft),
            maxLeft,
          );
          const top = Math.min(
            Math.max(centerY - halfHeight, minTop),
            maxTop,
          );

          finalized.from = { x: left, y: top };
          finalized.to = {
            x: left + defaultWidth,
            y: top + defaultHeight,
          };
          finalized.tailEdge = getRectCalloutTailEdge({
            from: finalized.from,
            to: finalized.to,
          });
        }
      }
      if (
        finalized.type === TOOL_IDS.REDACT &&
        finalized.from &&
        finalized.to
      ) {
        const currentWidth = Math.abs(finalized.to.x - finalized.from.x);
        const currentHeight = Math.abs(finalized.to.y - finalized.from.y);

        // Click-only redact placement can leave a near-zero box; expand it
        // to a usable default while keeping it inside the canvas.
        if (currentWidth < 8 && currentHeight < 8) {
          const defaultWidth = sheetsSelected ? 220 : 170;
          const defaultHeight = sheetsSelected ? 130 : 100;
          const halfWidth = defaultWidth / 2;
          const halfHeight = defaultHeight / 2;
          const centerX = (finalized.from.x + finalized.to.x) / 2;
          const centerY = (finalized.from.y + finalized.to.y) / 2;
          const canvasWidth = Number(canvas?.width || 0);
          const canvasHeight = Number(canvas?.height || 0);

          const minLeft = 8;
          const minTop = 8;
          const maxLeft =
            canvasWidth > 0
              ? Math.max(minLeft, canvasWidth - defaultWidth - 8)
              : centerX - halfWidth;
          const maxTop =
            canvasHeight > 0
              ? Math.max(minTop, canvasHeight - defaultHeight - 8)
              : centerY - halfHeight;

          const left = Math.min(
            Math.max(centerX - halfWidth, minLeft),
            maxLeft,
          );
          const top = Math.min(
            Math.max(centerY - halfHeight, minTop),
            maxTop,
          );

          finalized.from = { x: left, y: top };
          finalized.to = {
            x: left + defaultWidth,
            y: top + defaultHeight,
          };
        }
      }
      draftEntityRef.current = null;
      drawingRef.current = false;

      setEntities((prev) => [...prev, finalized]);
      setEntitySelection([finalized.id]);
      if (shouldAutoReturnToSelect(activeTool)) {
        setActiveTool(TOOL_IDS.SELECT);
      }
      if (finalized.type === TOOL_IDS.RECT_CALLOUT) {
        const textBox = getRectCalloutTextBox(finalized);
        const defaultCalloutEditorFont = getDefaultEditorFontSize(
          finalized.penSize,
        );
        setTextEditor({
          visible: true,
          x: textBox.x,
          y: textBox.y,
          value: String(finalized.text || ""),
          entityId: finalized.id,
          fontSize: Boolean(sheetsSelected)
            ? Math.max(finalized.fontSize || 0, defaultCalloutEditorFont)
            : finalized.fontSize || textBox.fontSize,
          penColor: finalized.penColor || penColor,
          width: textBox.width,
          height: textBox.height,
          targetType: finalized.type,
        });
      }
      renderEntities(null);
    },
    [
      cloneEntity,
      activeTool,
      getEntityBounds,
      isEntityOwnedByCurrentUser,
      getDefaultEditorFontSize,
      getRectCalloutTextBox,
      penColor,
      renderEntities,
      shouldAutoReturnToSelect,
      setEntitySelection,
      sheetsSelected,
    ],
  );

  const handleCanvasLeave = useCallback(
    (event) => {
      if (middleMousePanRef.current.active) {
        setHoveredEntityId(null);
        return;
      }

      stopDrawing(event);
      setHoveredEntityId(null);
      setCanvasCursorIfNeeded(
        activeTool === TOOL_IDS.SELECT ? "default" : "crosshair",
      );
    },
    [activeTool, setCanvasCursorIfNeeded, stopDrawing],
  );

  const commitText = useCallback(() => {
    if (!textEditor.visible) {
      return;
    }

    const canvas = entityCanvasRef.current;
    if (!canvas) {
      setTextEditor((prev) => ({ ...prev, visible: false }));
      return;
    }

    const value = textEditor.value.trim();
    if (!value) {
      setTextEditor((prev) => ({
        ...prev,
        visible: false,
        entityId: null,
        fontSize: null,
        penColor: null,
      }));
      return;
    }

    saveUndoState();
    if (textEditor.entityId) {
      setEntities((prev) =>
        prev.map((entity) => {
          if (entity.id !== textEditor.entityId) {
            return entity;
          }
          if (entity.type === TOOL_IDS.RECT_CALLOUT) {
            return {
              ...entity,
              text: value,
              fontSize:
                textEditor.fontSize ||
                entity.fontSize ||
                getDefaultEditorFontSize(),
              penColor: textEditor.penColor || entity.penColor || penColor,
            };
          }
          return {
            ...entity,
            point: { x: textEditor.x, y: textEditor.y },
            text: value,
            fontSize:
              textEditor.fontSize ||
              entity.fontSize ||
              getDefaultEditorFontSize(),
            penColor: textEditor.penColor || entity.penColor || penColor,
          };
        }),
      );
      setEntitySelection([textEditor.entityId]);
    } else {
      const fontSize = textEditor.fontSize || getDefaultEditorFontSize();
      const next = {
        id: nextEntityId(),
        type: TOOL_IDS.TEXT,
        point: { x: textEditor.x, y: textEditor.y },
        text: value,
        penColor: textEditor.penColor || penColor,
        fillColor,
        fillEnabled: false,
        penSize,
        ...buildCurrentEntityUserTag(),
        fontSize,
        rotation: 0,
      };

      setEntities((prev) => [...prev, next]);
      setEntitySelection([next.id]);
    }

    if (shouldAutoReturnToSelect(activeTool)) {
      setActiveTool(TOOL_IDS.SELECT);
    }

    setTextEditor({
      visible: false,
      x: 0,
      y: 0,
      value: "",
      entityId: null,
      fontSize: null,
      penColor: null,
      width: null,
      height: null,
      targetType: null,
    });
  }, [
    buildCurrentEntityUserTag,
    fillColor,
    getDefaultEditorFontSize,
    nextEntityId,
    penColor,
    penSize,
    saveUndoState,
    activeTool,
    shouldAutoReturnToSelect,
    setEntitySelection,
    textEditor,
  ]);

  const clearMarkup = useCallback(() => {
    const drawCanvas = drawCanvasRef.current;
    if (drawCanvas) {
      const ctx = drawCanvas.getContext("2d");
      saveUndoState();
      ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
      measureOverlaySnapshotRef.current = null;
      pendingMeasureLabelsRef.current = [];
      calloutCounterRef.current = 1;
      setEntities([]);
      setEntitySelection([]);
      setTextEditor((prev) => ({
        ...prev,
        visible: false,
        value: "",
        entityId: null,
        fontSize: null,
        penColor: null,
      }));
    }
  }, [saveUndoState, setEntitySelection]);

  const openClearConfirmModal = useCallback(() => {
    setClearConfirmModalOpen(true);
  }, []);

  const closeClearConfirmModal = useCallback(() => {
    setClearConfirmModalOpen(false);
  }, []);

  const confirmClearMarkup = useCallback(() => {
    clearMarkup();
    setClearConfirmModalOpen(false);
  }, [clearMarkup]);

  const deleteSelectedEntity = useCallback(() => {
    const selectedIds = selectedEntityIdsRef.current;
    if (!selectedIds.length) {
      return;
    }

    const ownedIds = selectedIds.filter((id) =>
      isEntityOwnedByCurrentUser(
        entitiesRef.current.find((entity) => entity.id === id),
      ),
    );
    if (!ownedIds.length) {
      return;
    }

    const selectedSet = new Set(ownedIds);
    saveUndoState();
    setEntities((prev) => prev.filter((entity) => !selectedSet.has(entity.id)));
    setEntitySelection([]);
    setTextEditor((prev) =>
      !prev.entityId || !selectedSet.has(prev.entityId)
        ? prev
        : {
            visible: false,
            x: 0,
            y: 0,
            value: "",
            entityId: null,
            fontSize: null,
            penColor: null,
            width: null,
            height: null,
            targetType: null,
          },
    );
  }, [isEntityOwnedByCurrentUser, saveUndoState, setEntitySelection]);

  useEffect(() => {
    if (!isMarkupMode) {
      return undefined;
    }

    const handleGlobalDeleteKey = (event) => {
      if (event.defaultPrevented || textEditor.visible) {
        return;
      }

      const target = event.target;
      const tagName = String(target?.tagName || "").toLowerCase();
      const isEditableTarget =
        Boolean(target?.isContentEditable) ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select";

      if (isEditableTarget) {
        return;
      }

      if (
        (event.key === "Delete" || event.key === "Backspace") &&
        selectedEntityIdsRef.current.length
      ) {
        event.preventDefault();
        deleteSelectedEntity();
      }
    };

    window.addEventListener("keydown", handleGlobalDeleteKey);
    return () => {
      window.removeEventListener("keydown", handleGlobalDeleteKey);
    };
  }, [deleteSelectedEntity, isMarkupMode, textEditor.visible]);

  const undoLast = useCallback(() => {
    const previous = undoStackRef.current[undoStackRef.current.length - 1];
    if (!previous) {
      return;
    }

    undoStackRef.current = undoStackRef.current.slice(0, -1);
    setUndoStack(undoStackRef.current);
    calloutCounterRef.current = previous.calloutCounter || 1;
    setEntities((previous.entities || []).map((entity) => cloneEntity(entity)));
    setEntitySelection([]);
  }, [cloneEntity, setEntitySelection]);

  const downloadAnnotatedImage = useCallback(() => {
    if (!screenshotUrl) return;

    const imageCanvas = imageCanvasRef.current;
    const drawCanvas = drawCanvasRef.current;
    const entityCanvas = entityCanvasRef.current;
    if (!imageCanvas || !drawCanvas || !entityCanvas) {
      return;
    }

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = imageCanvas.width;
    exportCanvas.height = imageCanvas.height;

    const exportCtx = exportCanvas.getContext("2d");
    if (!exportCtx) {
      return;
    }

    exportCtx.drawImage(
      imageCanvas,
      0,
      0,
      exportCanvas.width,
      exportCanvas.height,
    );
    exportCtx.drawImage(
      drawCanvas,
      0,
      0,
      exportCanvas.width,
      exportCanvas.height,
    );
    exportCtx.drawImage(
      entityCanvas,
      0,
      0,
      exportCanvas.width,
      exportCanvas.height,
    );

    const link = document.createElement("a");
    link.href = exportCanvas.toDataURL("image/png");
    link.download = `viewer-markup-${Date.now()}.png`;
    link.click();
  }, [screenshotUrl]);

  const closeOverlay = useCallback(() => {
    if (typeof measureEventCleanupRef.current === "function") {
      measureEventCleanupRef.current();
      measureEventCleanupRef.current = null;
    }
    resetOverlayState();
  }, [resetOverlayState]);

  const getMeasureDoneButtonNode = useCallback(() => {
    const viewer = viewerState?.viewer;
    const viewerContainer = viewer?.container;
    if (!viewerContainer) {
      return null;
    }

    const byId = document.getElementById("toolbar-measureTool-done");
    if (byId) {
      return byId;
    }

    const root = viewerContainer.parentElement || viewerContainer;
    const controlCandidates = root.querySelectorAll(
      "button, [role='button'], .adsk-button",
    );

    for (const node of controlCandidates) {
      const title = String(node.getAttribute("title") || "").trim();
      const aria = String(node.getAttribute("aria-label") || "").trim();
      const text = String(node.textContent || "").trim();
      const label = `${title} ${aria} ${text}`.trim().toLowerCase();

      if (!label || label !== "done") {
        continue;
      }

      const measureScoped = Boolean(
        node.closest("[class*='measure'], [class*='Measure']"),
      );
      if (!measureScoped) {
        continue;
      }

      return node;
    }

    return null;
  }, [viewerState]);

  const toggleAutodeskMeasureDoneButton = useCallback(
    (hideButton) => {
      const node = getMeasureDoneButtonNode();
      if (!node) {
        return;
      }

      if (hideButton) {
        if (
          !Object.prototype.hasOwnProperty.call(node.dataset, "mwhDoneDisplay")
        ) {
          node.dataset.mwhDoneDisplay = node.style.display || "";
        }
        if (
          !Object.prototype.hasOwnProperty.call(
            node.dataset,
            "mwhDoneVisibility",
          )
        ) {
          node.dataset.mwhDoneVisibility = node.style.visibility || "";
        }

        node.style.display = node.dataset.mwhDoneDisplay || "";
        node.style.visibility = "hidden";
        node.style.pointerEvents = "none";
        return;
      }

      if (Object.prototype.hasOwnProperty.call(node.dataset, "mwhDoneDisplay")) {
        node.style.display = node.dataset.mwhDoneDisplay || "";
        delete node.dataset.mwhDoneDisplay;
      }
      if (Object.prototype.hasOwnProperty.call(node.dataset, "mwhDoneVisibility")) {
        node.style.visibility = node.dataset.mwhDoneVisibility || "";
        delete node.dataset.mwhDoneVisibility;
      }
      node.style.pointerEvents = "";
    },
    [getMeasureDoneButtonNode],
  );

  useEffect(() => {
    if (!isMarkupMode || !isMeasuring || !viewerState?.viewer?.container) {
      return undefined;
    }

    const root =
      viewerState.viewer.container.parentElement ||
      viewerState.viewer.container;
    const applyHide = () => {
      toggleAutodeskMeasureDoneButton(true);
    };

    applyHide();

    const observer = new MutationObserver(() => {
      applyHide();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "title", "aria-label"],
    });

    return () => {
      observer.disconnect();
      toggleAutodeskMeasureDoneButton(false);
    };
  }, [isMarkupMode, isMeasuring, toggleAutodeskMeasureDoneButton, viewerState]);

  useEffect(() => {
    if (!isMarkupMode || !isMeasuring || !viewerState?.viewer?.container) {
      setMeasureDoneAnchorRect(null);
      return undefined;
    }

    const viewerContainer = viewerState.viewer.container;
    const root = viewerContainer.parentElement || viewerContainer;

    const updateAnchorRect = () => {
      const doneButton = getMeasureDoneButtonNode();
      if (!doneButton) {
        setMeasureDoneAnchorRect(null);
        return;
      }

      const doneRect = doneButton.getBoundingClientRect();
      const viewerRect = viewerContainer.getBoundingClientRect();
      const width = Math.max(60, Math.round(doneRect.width || 0));
      const height = Math.max(30, Math.round(doneRect.height || 0));

      setMeasureDoneAnchorRect({
        left: Math.round(doneRect.left - viewerRect.left),
        top: Math.round(doneRect.top - viewerRect.top),
        width,
        height,
      });
    };

    updateAnchorRect();
    const rafId = window.requestAnimationFrame(updateAnchorRect);

    const observer = new MutationObserver(() => {
      updateAnchorRect();
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "title", "aria-label"],
    });

    window.addEventListener("resize", updateAnchorRect);

    return () => {
      window.cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener("resize", updateAnchorRect);
    };
  }, [
    getMeasureDoneButtonNode,
    isMarkupMode,
    isMeasuring,
    viewerState,
  ]);

  const stopMeasureInViewer = useCallback(
    async (options = {}) => {
      const viewer = viewerState?.viewer;
      if (!viewer || !isMarkupMode) {
        setIsMeasuring(false);
        return;
      }

      const shouldCaptureMeasureFrame = options.captureMeasureFrame !== false;
      const forceAbort = options.forceAbort === true;
      const measureExt = viewer.getExtension?.("Autodesk.Measure");
      const previousScreenshotUrl =
        (typeof screenshotUrl === "string" && screenshotUrl.trim().length > 0
          ? screenshotUrl
          : null) || lastValidScreenshotUrlRef.current;
      let structuredMeasureWidgets = [];

      const hardTeardownMeasure = async () => {
        try {
          clearMeasureState(viewer, measureExt);
        } catch (clearErr) {
          // Ignore reset issues and continue teardown.
        }

        if (typeof measureEventCleanupRef.current === "function") {
          try {
            measureEventCleanupRef.current();
          } catch (cleanupErr) {
            // Ignore event listener cleanup issues.
          }
          measureEventCleanupRef.current = null;
        }

        try {
          measureExt?.deactivate?.();
        } catch (deactivateErr) {
          // Ignore extension deactivation differences.
        }

        try {
          viewer.toolController?.deactivateTool?.("measure");
        } catch (toolDeactivateErr) {
          // Ignore tool-controller differences.
        }

        if (typeof viewer.unloadExtension === "function") {
          try {
            await viewer.unloadExtension("Autodesk.Measure");
          } catch (unloadErr) {
            // Ignore unload differences across viewer versions.
          }
        }
      };

      try {
        try {
          const extensionMeasureWidgets =
            extractStructuredMeasureWidgetsFromExtension(viewer, measureExt);
          const domStructuredMeasureWidgets =
            extractStructuredMeasureWidgetsFromDom(viewer);
          structuredMeasureWidgets = mergeStructuredMeasureWidgets(
            extensionMeasureWidgets,
            domStructuredMeasureWidgets,
          );
        } catch (measureWidgetErr) {
          structuredMeasureWidgets = [];
        }

        const viewerCanvasWidth =
          entityCanvasRef.current?.width || viewer.container?.clientWidth || 0;
        const viewerCanvasHeight =
          entityCanvasRef.current?.height || viewer.container?.clientHeight || 0;
        const coordinateEntity = createMeasureCoordinateEntityFromWidgets(
          structuredMeasureWidgets,
          viewerCanvasWidth,
          viewerCanvasHeight,
          penColor,
        );

        if (coordinateEntity) {
          setEntities((prev) => [...prev, coordinateEntity]);
          setEntitySelection([coordinateEntity.id]);
        }

        if (shouldCaptureMeasureFrame && !forceAbort) {
          const combinedOverlay = captureCurrentOverlayDataUrl();
          if (combinedOverlay) {
            measureOverlaySnapshotRef.current = combinedOverlay;
          }

          // Preserve Autodesk measurement lines + labels exactly as rendered.
          const measuredFrame = await captureViewerFrameDataUrl(viewer);
          const measuredFrameWithWidgets =
            await bakeStructuredMeasureWidgetsIntoImageDataUrl(
              measuredFrame,
              structuredMeasureWidgets,
              viewer.container?.clientWidth || 0,
              viewer.container?.clientHeight || 0,
            );

          // Capture a clean frame from the same viewer state so diffing isolates
          // only the new measurement, instead of subtracting prior baked marks.
          clearMeasureState(viewer, measureExt);
          await new Promise((resolve) => {
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(resolve);
            });
          });
          const cleanFrame = await captureViewerFrameDataUrl(viewer);

          // Do not redraw label boxes separately.
          pendingMeasureLabelsRef.current = [];
          shouldRestoreOverlayRef.current = !!measureOverlaySnapshotRef.current;

          let nextScreenshotUrl = measuredFrameWithWidgets;
          if (screenshotUrl && cleanFrame) {
            try {
              const newMeasurementDelta = await extractOverlayDiffDataUrl(
                measuredFrameWithWidgets,
                cleanFrame,
              );
              if (newMeasurementDelta) {
                nextScreenshotUrl = await mergeOverlayDataUrls(
                  screenshotUrl,
                  newMeasurementDelta,
                );
              }
            } catch (mergeErr) {
              nextScreenshotUrl = measuredFrameWithWidgets;
            }
          }

          const hasNextScreenshot =
            typeof nextScreenshotUrl === "string" &&
            nextScreenshotUrl.trim().length > 0;
          if (!hasNextScreenshot && previousScreenshotUrl) {
            nextScreenshotUrl = previousScreenshotUrl;
          }

          setScreenshotUrl((prev) => {
            if (prev?.startsWith("blob:")) {
              URL.revokeObjectURL(prev);
            }
            return nextScreenshotUrl;
          });

          if (
            typeof nextScreenshotUrl === "string" &&
            nextScreenshotUrl.trim().length > 0
          ) {
            lastValidScreenshotUrlRef.current = nextScreenshotUrl;
          }
        }

        let label = "";
        try {
          label =
            extractMeasureLabel(viewer, measureExt) ||
            extractMeasureLabelFromDom(viewer);
        } catch (labelErr) {
          label = "";
        }
        if (label) {
          setLastMeasureLabel(label);
        }
      } catch (err) {
        // Ignore measure processing errors; teardown runs in finally.
      } finally {
        await hardTeardownMeasure();
        setIsMeasuring(false);
      }
    },
    [
      captureCurrentOverlayDataUrl,
      captureViewerFrameDataUrl,
      clearMeasureState,
      extractStructuredMeasureWidgetsFromExtension,
      extractStructuredMeasureWidgetsFromDom,
      mergeStructuredMeasureWidgets,
      createMeasureCoordinateEntityFromWidgets,
      extractMeasureLabel,
      extractMeasureLabelFromDom,
      bakeStructuredMeasureWidgetsIntoImageDataUrl,
      extractOverlayDiffDataUrl,
      mergeOverlayDataUrls,
      isMarkupMode,
      penColor,
      screenshotUrl,
      setEntitySelection,
      viewerState,
    ],
  );

  const startMeasureInViewer = useCallback(async () => {
    if (!isMarkupMode || !screenshotUrl || !viewerState?.viewer) {
      return;
    }

    const viewer = viewerState.viewer;
    setError(null);

    const desiredMarkupViewportState =
      loadedMarkupViewerStateRef.current ||
      viewer.getState?.({
        viewport: true,
        objectSet: false,
        renderOptions: false,
        cutplanes: false,
        seedURN: false,
        explodeScale: false,
      });

    if (
      desiredMarkupViewportState &&
      typeof viewer.restoreState === "function"
    ) {
      try {
        viewer.restoreState(
          JSON.parse(JSON.stringify(desiredMarkupViewportState)),
          null,
          true,
        );
      } catch (restoreErr) {
        // Continue into measure mode even if one restore signature fails.
      }
    }

    const lockedViewportState = desiredMarkupViewportState;

    try {
      if (typeof measureEventCleanupRef.current === "function") {
        measureEventCleanupRef.current();
        measureEventCleanupRef.current = null;
      }

      if (typeof viewer.unloadExtension === "function") {
        try {
          await viewer.unloadExtension("Autodesk.Measure");
        } catch (unloadErr) {
          // Ignore unload failures and continue with fresh load attempt.
        }
      }

      const measureExt =
        (await viewer.loadExtension?.("Autodesk.Measure")) ||
        viewer.getExtension?.("Autodesk.Measure");

      // Best-effort reset without unloading extensions.
      try {
        measureExt?.deactivate?.();
        viewer.toolController?.deactivateTool?.("measure");
        clearMeasureState(viewer, measureExt);
      } catch (clearErr) {
        // Ignore viewer-version specific reset errors.
      }

      measureOverlaySnapshotRef.current = captureCurrentOverlayDataUrl();
      shouldRestoreOverlayRef.current = false;
      pendingMeasureLabelsRef.current = [];
      setLastMeasureLabel("");

      const eventNames = new Set([
        "measurement.changed",
        "measurement.completed",
        "measure.changed",
        "measure.completed",
      ]);

      const av = window?.Autodesk?.Viewing;
      if (av) {
        if (av.MEASUREMENT_CHANGED_EVENT) {
          eventNames.add(av.MEASUREMENT_CHANGED_EVENT);
        }
        if (av.MEASUREMENT_COMPLETED_EVENT) {
          eventNames.add(av.MEASUREMENT_COMPLETED_EVENT);
        }
      }

      const onMeasureEvent = (event) => {
        const label = extractMeasureLabel(viewer, measureExt, event);
        if (label) {
          setLastMeasureLabel(label);
        }
      };

      const attachedEventNames = [];
      if (typeof viewer.addEventListener === "function") {
        eventNames.forEach((name) => {
          viewer.addEventListener(name, onMeasureEvent);
          attachedEventNames.push(name);
        });
      }

      measureEventCleanupRef.current = () => {
        if (typeof viewer.removeEventListener !== "function") {
          return;
        }
        attachedEventNames.forEach((name) => {
          viewer.removeEventListener(name, onMeasureEvent);
        });
      };

      measureExt?.setUnits?.("mm");

      if (measureExt?.activate) {
        measureExt.activate();
      } else if (viewer.toolController?.activateTool) {
        viewer.toolController.activateTool("measure");
      }

      // Measure activation can trigger an unexpected zoom-to-fit in some builds.
      // Restore the markup viewport so the user stays on the same captured view.
      if (lockedViewportState && typeof viewer.restoreState === "function") {
        const restoreViewport = () => {
          try {
            viewer.restoreState(lockedViewportState, null, true);
            viewer.impl?.invalidate?.(true, true, true);
          } catch (restoreErr) {
            // Ignore restore issues for viewer versions with different state signatures.
          }
        };

        restoreViewport();
        window.requestAnimationFrame(restoreViewport);
      }

      setIsMeasuring(true);
    } catch (err) {
      setError("Unable to start Autodesk measure tool.");
      setIsMeasuring(false);
    }
  }, [
    captureCurrentOverlayDataUrl,
    clearMeasureState,
    extractMeasureLabel,
    isMarkupMode,
    screenshotUrl,
    viewerState,
  ]);

  const openExitConfirmModal = useCallback(() => {
    if (!isMarkupMode || !screenshotUrl) {
      return;
    }
    setExitConfirmModalOpen(true);
  }, [isMarkupMode, screenshotUrl]);

  const closeExitConfirmModal = useCallback(() => {
    setExitConfirmModalOpen(false);
  }, []);

  const confirmExitMarkupMode = useCallback(async () => {
    await stopMeasureInViewer({ captureMeasureFrame: false });

    const isLoadedMarkup = Boolean(currentMarkupId);
    if (isLoadedMarkup && viewerState?.viewer) {
      try {
        await clearViewerSectionArtifacts(viewerState.viewer);
      } catch (sectionClearErr) {
        // Always allow exiting markup mode even if section cleanup fails.
      }
    }

    closeOverlay();
    setExitConfirmModalOpen(false);
  }, [
    clearViewerSectionArtifacts,
    closeOverlay,
    currentMarkupId,
    stopMeasureInViewer,
    viewerState,
  ]);

  const selectedEntity =
    entities.find((entity) => entity.id === selectedEntityId) || null;
  const selectedEntityCount = selectedEntityIds.length;
  const is2DMarkupContext = Boolean(sheetsSelected);
  const selectedImageEntity =
    selectedEntityCount === 1 && selectedEntity?.type === TOOL_IDS.IMAGE
      ? selectedEntity
      : null;
  const canFillSelected =
    selectedEntityCount === 1 &&
    !!selectedEntity &&
    FILLABLE_TOOL_IDS.includes(selectedEntity.type);
  const selectedHasFill = !!selectedEntity?.fillEnabled;
  const shouldPulseMarkupActionIcons =
    isMarkupMode && screenshotUrl && !saveBusy;
  const selectedInfoMarkupStatus = getMarkupStatusDisplay(selectedInfoMarkup || {});
  const measureFinishButtonRect = useMemo(() => {
    if (!measureDoneAnchorRect) {
      return null;
    }

    const inset = 1;
    const anchorWidth = Math.max(0, Number(measureDoneAnchorRect.width) || 0);
    const anchorHeight = Math.max(0, Number(measureDoneAnchorRect.height) || 0);
    const availableWidth = Math.max(0, anchorWidth - inset * 2);
    const availableHeight = Math.max(0, anchorHeight - inset * 2);

    const width = Math.max(44, Math.min(64, availableWidth));
    const height = Math.max(30, availableHeight);
    const left =
      Number(measureDoneAnchorRect.left || 0) +
      inset +
      Math.max(0, Math.round((availableWidth - width) / 2));

    return {
      left,
      top: Number(measureDoneAnchorRect.top || 0) + inset,
      width,
      height,
    };
  }, [measureDoneAnchorRect]);

  return (
    <div className="screenshot-markup" style={{ padding: "10px" }}>
      <style>
        {`@keyframes mwhMarkupPing {
          0% {
            box-shadow: 0 0 0 0 rgba(147, 213, 0, 0.65), 0 0 0 0 rgba(147, 213, 0, 0.35);
            transform: scale(1);
          }
          60% {
            box-shadow: 0 0 0 10px rgba(147, 213, 0, 0.0), 0 0 18px 4px rgba(147, 213, 0, 0.42);
            transform: scale(1.03);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(147, 213, 0, 0.0), 0 0 0 0 rgba(147, 213, 0, 0.0);
            transform: scale(1);
          }
        }

        @keyframes mwhMarkupPingCycle {
          0%, 1.8%, 4.6%, 7.4%, 10.2%, 14.285%, 100% {
            box-shadow: 0 0 0 0 rgba(147, 213, 0, 0.0), 0 0 0 0 rgba(147, 213, 0, 0.0);
            transform: scale(1);
          }
          0.9%, 3.7%, 6.5%, 9.3%, 12.1% {
            box-shadow: 0 0 0 10px rgba(147, 213, 0, 0.0), 0 0 18px 4px rgba(147, 213, 0, 0.42);
            transform: scale(1.03);
          }
        }

        @keyframes mwhMarkupPingExit {
          0% {
            box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.58), 0 0 0 0 rgba(220, 53, 69, 0.32);
            transform: scale(1);
          }
          60% {
            box-shadow: 0 0 0 10px rgba(220, 53, 69, 0.0), 0 0 16px 3px rgba(220, 53, 69, 0.38);
            transform: scale(1.03);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.0), 0 0 0 0 rgba(220, 53, 69, 0.0);
            transform: scale(1);
          }
        }`}
      </style>
      {!isPanelCollapsed && (
        <>
          <div
            className="screenshot-markup__header"
            style={{
              marginBottom: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <h2
              className="screenshot-markup__title"
              style={{ marginBottom: 0 }}
            >
              Markups
            </h2>
            <button
              type="button"
              className="home-btn home-btn--ghost"
              disabled={savedMarkupsLoading}
              onClick={fetchSavedMarkups}
            >
              Refresh
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              maxHeight: "calc(100vh - 150px)",
              minHeight: 0,
            }}
          >
            <div className="screenshot-markup__save-panel">
              <div
                className="screenshot-markup__save-title"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "6px",
                }}
              >
                <span>Filters</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger screenshot-markup__filter-reset-btn"
                  style={{ minWidth: "72px" }}
                  onClick={() => {
                    setMarkupFilterCat(null);
                    setMarkupFilterValue("");
                  }}
                >
                  Reset
                </button>
              </div>
              <div style={{ marginBottom: "8px", display: "grid", gap: "8px" }}>
              <Select
                className="screenshot-markup__filter-select"
                menuPortalTarget={menuPortalTarget || undefined}
                styles={panelSelectStyles}
                options={markupFilterOptions}
                value={
                  markupFilterCat ||
                  markupFilterOptions.find((option) => option.value === "")
                }
                onChange={(option) => {
                  setMarkupFilterCat(option && option.value ? option : null);
                  setMarkupFilterValue("");
                }}
              />

              {markupFilterCat?.value === "userName" ? (
                <Select
                  className="screenshot-markup__filter-select"
                  menuPortalTarget={menuPortalTarget || undefined}
                  styles={panelSelectStyles}
                  options={createdByFilterOptions}
                  value={
                    createdByFilterOptions.find(
                      (option) => option.value === markupFilterValue,
                    ) || null
                  }
                  onChange={(option) =>
                    setMarkupFilterValue(String(option?.value || ""))
                  }
                  isClearable
                />
              ) : markupFilterCat?.value === "createdFor" ? (
                <Select
                  className="screenshot-markup__filter-select"
                  menuPortalTarget={menuPortalTarget || undefined}
                  styles={panelSelectStyles}
                  options={createdForFilterOptions}
                  value={
                    createdForFilterOptions.find(
                      (option) => option.value === markupFilterValue,
                    ) || null
                  }
                  onChange={(option) =>
                    setMarkupFilterValue(String(option?.value || ""))
                  }
                  isClearable
                />
              ) : markupFilterCat?.value === "category" ? (
                <Select
                  className="screenshot-markup__filter-select"
                  menuPortalTarget={menuPortalTarget || undefined}
                  styles={panelSelectStyles}
                  options={categoryFilterOptions}
                  value={
                    categoryFilterOptions.find(
                      (option) => option.value === markupFilterValue,
                    ) || null
                  }
                  onChange={(option) =>
                    setMarkupFilterValue(String(option?.value || ""))
                  }
                  isClearable
                />
              ) : markupFilterCat?.value === "status" ? (
                <Select
                  className="screenshot-markup__filter-select"
                  menuPortalTarget={menuPortalTarget || undefined}
                  styles={panelSelectStyles}
                  options={statusFilterOptions}
                  value={
                    statusFilterOptions.find(
                      (option) => option.value === markupFilterValue,
                    ) || null
                  }
                  onChange={(option) =>
                    setMarkupFilterValue(String(option?.value || ""))
                  }
                  isClearable
                />
              ) : markupFilterCat?.value === "asset" ? (
                <Select
                  className="screenshot-markup__filter-select"
                  menuPortalTarget={menuPortalTarget || undefined}
                  styles={panelSelectStyles}
                  options={assetFilterOptions}
                  value={
                    assetFilterOptions.find(
                      (option) => option.value === markupFilterValue,
                    ) || null
                  }
                  onChange={(option) =>
                    setMarkupFilterValue(String(option?.value || ""))
                  }
                  isClearable
                />
              ) :
              markupFilterCat?.value && markupFilterCat?.value !== "archived" ? (
                <div className="form-floating" style={{ marginBottom: 0 }}>
                  <input
                    type="text"
                    className="form-control"
                    id="screenshot_markup_filter_value"
                    placeholder="Enter filter value"
                    value={markupFilterValue}
                    onChange={(event) => setMarkupFilterValue(event.target.value)}
                  />
                  <label htmlFor="screenshot_markup_filter_value">
                    Enter filter value
                  </label>
                </div>
                ) : null}
              </div>
            </div>

            <div
              ref={savedMarkupListRef}
              className="screenshot-markup__save-panel"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: "4px",
              }}
            >
              {error && (
                <div
                  className="alert alert-warning screenshot-markup__inline-alert"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <div className="markup-list screenshot-markup__save-list">
              {savedMarkupsLoading && (
                <div className="screenshot-markup__save-state">
                  Loading markups...
                </div>
              )}
              {!savedMarkupsLoading && savedMarkups.length === 0 && (
                <div className="screenshot-markup__save-state">
                  No saved markups found for this project.
                </div>
              )}
              {!savedMarkupsLoading &&
                savedMarkups.length > 0 &&
                filteredSavedMarkups.length === 0 && (
                  <div className="screenshot-markup__save-state">
                    No markups match the current filter.
                  </div>
                )}
                {!savedMarkupsLoading && filteredSavedMarkups.length > 0 && (
                  <div>
                    {filteredSavedMarkups.map((item) => (
                    <div
                      key={item.id}
                      className={`card text-white bg-secondary mb-3 screenshot-markup__save-card${
                        String(item.id) === String(currentMarkupId)
                          ? " screenshot-markup__save-card--active"
                          : ""
                      }`}
                      data-active={String(item.id) === String(currentMarkupId)}
                    >
                  <div
                    className="card-header markup-header screenshot-markup__save-card-header"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                    }}
                  >
                    <div
                      className="screenshot-markup__save-card-title"
                      data-active={String(item.id) === String(currentMarkupId)}
                      title={item.title}
                      style={{ minWidth: 0, flex: 1 }}
                    >
                      {item.title || `Markup ${item.id}`}
                    </div>
                    <span
                      className="screenshot-markup__save-card-status-badge"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        boxShadow: "0 6px 14px rgba(0, 0, 0, 0.18)",
                        ...(getMarkupStatusDisplay(item).badgeStyle || {}),
                      }}
                    >
                      {getMarkupStatusDisplay(item).label}
                    </span>
                  </div>
                  <div className="card-body markup-body screenshot-markup__save-card-body">
                    <div
                      className="screenshot-markup__save-card-preview"
                      ref={registerSavedMarkupPreviewNode(item.id)}
                      data-markup-id={item.id}
                    >
                      {(() => {
                        const preview = savedMarkupPreviewMap.get(item.id);
                        if (preview?.imageLoading) {
                          return (
                            <div className="screenshot-markup__save-card-preview-state">
                              Loading...
                            </div>
                          );
                        }

                        if (preview?.imageOptimizing) {
                          return (
                            <div className="screenshot-markup__save-card-preview-state screenshot-markup__save-card-preview-state--optimizing">
                              Optimizing...
                            </div>
                          );
                        }

                        if (preview?.imageDeferred) {
                          return (
                            <div className="screenshot-markup__save-card-preview-state">
                              Scroll to load
                            </div>
                          );
                        }

                        if (preview?.imagePreview) {
                          return (
                            <img
                              className="screenshot-markup__save-card-preview-image"
                              src={preview.imagePreview}
                              alt={item.title || `Markup ${item.id}`}
                            />
                          );
                        }

                        return (
                          <div className="screenshot-markup__save-card-preview-state">
                            No preview
                          </div>
                        );
                      })()}
                    </div>
                    <div className="screenshot-markup__save-card-content">
                      <div
                        className={`screenshot-markup__save-card-actions${
                          isViewingArchivedMarkups
                            ? " screenshot-markup__save-card-actions--archived"
                            : ""
                        }`}
                      >
                        <span
                          className="screenshot-markup__load-btn-wrap"
                          title={
                            isLegacyMarkup(item)
                              ? "Markup made in older version of mView, cannot be loaded"
                              : undefined
                          }
                        >
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success screenshot-markup__load-btn"
                            disabled={
                              loadingMarkupId === item.id || isLegacyMarkup(item)
                            }
                            onClick={() => loadScreenshotMarkup(item.id)}
                            aria-label={
                              isLegacyMarkup(item)
                                ? "Markup made in older version of mView, cannot be loaded"
                                : loadingMarkupId === item.id
                                  ? `Loading markup ${item.title || item.id}`
                                  : `Load markup ${item.title || item.id}`
                            }
                          >
                            {loadingMarkupId === item.id ? (
                              <img
                                src={MWHLoadingScreenLogo}
                                alt=""
                                aria-hidden="true"
                                className="screenshot-markup__load-spinner"
                              />
                            ) : (
                              "Load"
                            )}
                          </button>
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary screenshot-markup__load-btn screenshot-markup__info-btn"
                          onClick={() => openMarkupInfoModal(item)}
                          aria-label={`View markup info for ${item.title || item.id}`}
                        >
                          Info
                        </button>
                        {canDeleteMarkup(item) && !isViewingArchivedMarkups && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary screenshot-markup__load-btn screenshot-markup__info-btn"
                            onClick={() => openStatusModal(item)}
                            aria-label={`Update status for ${item.title || item.id}`}
                          >
                            Status
                          </button>
                        )}
                        {canDeleteMarkup(item) && !isViewingArchivedMarkups && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger screenshot-markup__load-btn screenshot-markup__archive-btn"
                            disabled={archivingMarkupId === item.id}
                            onClick={() => openArchiveConfirmModal(item)}
                            aria-label={
                              archivingMarkupId === item.id
                                ? `Archiving markup ${item.title || item.id}`
                                : `Archive markup ${item.title || item.id}`
                            }
                          >
                            {archivingMarkupId === item.id
                              ? "Archiving..."
                              : "Archive"}
                          </button>
                        )}
                        {canDeleteMarkup(item) && isViewingArchivedMarkups && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success screenshot-markup__load-btn screenshot-markup__archived-action-btn"
                              disabled={unarchivingMarkupId === item.id}
                              onClick={() => unarchiveMarkup(item)}
                              aria-label={
                                unarchivingMarkupId === item.id
                                  ? `Unarchiving markup ${item.title || item.id}`
                                  : `Unarchive markup ${item.title || item.id}`
                              }
                            >
                              {unarchivingMarkupId === item.id
                                ? "Unarchiving..."
                                : "Unarchive"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger screenshot-markup__load-btn screenshot-markup__archived-action-btn screenshot-markup__delete-btn"
                              disabled={
                                String(deletingArchivedMarkupId) ===
                                String(item.id)
                              }
                              onClick={() => openDeleteConfirmModal(item)}
                              aria-label={
                                String(deletingArchivedMarkupId) ===
                                String(item.id)
                                  ? `Deleting archived markup ${item.title || item.id}`
                                  : `Delete archived markup ${item.title || item.id}`
                              }
                            >
                              {String(deletingArchivedMarkupId) ===
                              String(item.id)
                                ? "Deleting..."
                                : "Delete"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      <input
        ref={fillColorInputRef}
        type="color"
        value={fillColor}
        onChange={(e) => handleFillColorChange(e.target.value)}
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        ref={imageUploadInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUploadSelection}
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* <div style={{ marginBottom: "10px", fontSize: "12px", color: "#6c757d" }}>
        Mode: Overlay-first. Active tool: {activeTool}.
        {lastMeasureLabel ? ` Last measure: ${lastMeasureLabel}` : ""}
      </div> */}

      {viewerState?.viewer?.container &&
        createPortal(
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10000,
              pointerEvents: "none",
            }}
          >
            <button
              type="button"
              className="btn btn-sm btn-primary screenshot-markup__start-btn"
              onClick={() => beginCaptureWithPinPlacement(false)}
              title={isCapturing ? "Starting" : "Start Markup"}
              aria-label={isCapturing ? "Starting" : "Start Markup"}
              style={{
                position: "absolute",
                top: "10px",
                left: "10px",
                zIndex: 1507,
                pointerEvents: "auto",
                width: "38px",
                height: "30px",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                animation:
                  !isMarkupMode && !isCapturing
                    ? "mwhMarkupPing 1.5s ease-out infinite"
                    : "none",
                borderColor: MWH_GREEN,
                backgroundColor: MWH_GREEN,
                color: "#ffffff",
                fontWeight: 700,
              }}
            >
              {isCapturing ? "◌" : "✎"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary screenshot-markup__start-btn"
              onClick={() => {
                if (savedMarkupPinsLoading) {
                  return;
                }

                if (!showAllSavedPins) {
                  setPinLoadStatus("idle");
                  setShowAllSavedPins(true);
                  return;
                }

                if (pinLoadStatus === "error") {
                  setPinLoadStatus("idle");
                  return;
                }

                setShowAllSavedPins(false);
              }}
              title={
                savedMarkupPinsLoading
                  ? "Loading Saved Pins"
                  : showAllSavedPins
                    ? "Saved Pins On - Click to Hide"
                    : "Saved Pins Off - Click to Show"
              }
              aria-label={
                savedMarkupPinsLoading
                  ? "Loading Saved Pins"
                  : showAllSavedPins
                    ? "Saved Pins On - Click to Hide"
                    : "Saved Pins Off - Click to Show"
              }
              disabled={savedMarkupPinsLoading}
              style={{
                position: "absolute",
                top: "48px",
                left: "10px",
                zIndex: 1507,
                pointerEvents: "auto",
                width: "38px",
                height: "30px",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderColor: showAllSavedPins ? "#2e8c44" : undefined,
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "0.25rem",
                backgroundColor: showAllSavedPins ? "#2e8c44" : undefined,
                backgroundImage: showAllSavedPins
                  ? "linear-gradient(160deg, #165428 0%, #2e8c44 100%)"
                  : undefined,
                color: "#ffffff",
                fontWeight: 700,
                boxShadow: showAllSavedPins
                  ? "0 0 0 2px rgba(255,255,255,0.3), 0 8px 14px rgba(15,63,22,0.45)"
                  : "none",
                transition: "background-color 160ms ease, box-shadow 160ms ease",
                opacity: savedMarkupPinsLoading ? 0.8 : 1,
              }}
            >
              {savedMarkupPinsLoading ? "◌" : "📍"}
            </button>
            {showAllSavedPins && showPinStatusPopup && (
              <div
                style={{
                  position: "absolute",
                  top: "48px",
                  left: "56px",
                  zIndex: 1507,
                  pointerEvents: "none",
                  maxWidth: "210px",
                  padding: "4px 8px",
                  borderRadius: "8px",
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: 600,
                  border: "1px solid rgba(242, 242, 242, 0.25)",
                  background:
                    "linear-gradient(160deg, rgba(58, 58, 58, 0.94) 0%, rgba(0, 93, 133, 0.9) 58%, rgba(156, 197, 202, 0.78) 100%)",
                  boxShadow: "0 8px 18px rgba(58, 58, 58, 0.24)",
                }}
              >
                {pinStatusLabel}
              </div>
            )}
          </div>,
          viewerState.viewer.container,
        )}

      {showAllSavedPins &&
        viewerState?.viewer?.container &&
        createPortal(
          <div
            data-pin-overlay-frame={pinOverlayFrame}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10001,
              pointerEvents: "none",
            }}
          >
            {savedMarkupPins.map((pin) => {
              const renderPoint = getPinRenderPosition(
                pin,
                viewerState?.viewer?.container,
              );

              if (!renderPoint) {
                return null;
              }

              return (
                <button
                  type="button"
                  key={pin.id}
                  onClick={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!pin.markupId || loadingMarkupId) {
                      return;
                    }
                    await loadScreenshotMarkup(pin.markupId);
                    setShowAllSavedPins(false);
                  }}
                  title={
                    pin.userName
                      ? `${pin.title} - ${pin.userName} (${pin.statusLabel || "Open"})`
                      : `${pin.title} (${pin.statusLabel || "Open"})`
                  }
                  aria-label={
                    pin.userName
                      ? `Load ${pin.title} by ${pin.userName} with status ${pin.statusLabel || "Open"}`
                      : `Load ${pin.title} with status ${pin.statusLabel || "Open"}`
                  }
                  style={{
                    position: "absolute",
                    left: `${renderPoint.x}px`,
                    top: `${renderPoint.y}px`,
                    transform: "translate(-50%, -100%)",
                    width: "16px",
                    height: "22px",
                    display: "block",
                    cursor: loadingMarkupId ? "wait" : "pointer",
                    pointerEvents: "auto",
                    padding: 0,
                    margin: 0,
                    border: "none",
                    background: "transparent",
                    overflow: "visible",
                    filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4))",
                  }}
                >
                  <svg
                    width="16"
                    height="22"
                    viewBox="0 0 16 22"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <line
                      x1="8"
                      y1="9"
                      x2="8"
                      y2="22"
                      stroke="rgba(255, 255, 255, 0.92)"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8"
                      y1="9"
                      x2="8"
                      y2="22"
                      stroke={pin.pinStemColor || MARKUP_PIN_STATUS_COLORS.open.stem}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="8"
                      cy="5"
                      r="4.5"
                      fill={pin.pinHeadColor || MARKUP_PIN_STATUS_COLORS.open.head}
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.6"
                    />
                    <circle
                      cx="6.5"
                      cy="3.8"
                      r="1"
                      fill="rgba(255, 255, 255, 0.92)"
                    />
                  </svg>
                </button>
              );
            })}
          </div>,
          viewerState.viewer.container,
        )}

      {isAwaitingPinPlacement &&
        !isMarkupMode &&
        viewerState?.viewer?.container &&
        createPortal(
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10002,
              pointerEvents: "auto",
              cursor: "crosshair",
              background: "rgba(9, 15, 22, 0.08)",
            }}
            onClick={handlePinPlacement}
          >
            <div
              style={{
                position: "absolute",
                top: "12px",
                left: "60px",
                padding: "6px 10px",
                borderRadius: "8px",
                color: "#ffffff",
                fontSize: "12px",
                fontWeight: 600,
                border: "1px solid rgba(242, 242, 242, 0.25)",
                background:
                  "linear-gradient(160deg, rgba(58, 58, 58, 0.94) 0%, rgba(0, 93, 133, 0.9) 58%, rgba(156, 197, 202, 0.78) 100%)",
                boxShadow: "0 8px 18px rgba(58, 58, 58, 0.24)",
              }}
            >
              Click on the viewer to place a pin and start markup
            </div>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                cancelPinPlacement();
              }}
              className="btn btn-sm btn-danger"
              title="Cancel starting markup"
              aria-label="Cancel starting markup"
              style={{
                position: "absolute",
                top: "12px",
                left: "380px",
                zIndex: 10003,
                pointerEvents: "auto",
                height: "30px",
                padding: "0 10px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
          </div>,
          viewerState.viewer.container,
        )}

      {isMarkupMode &&
        screenshotUrl &&
        viewerState?.viewer?.container &&
        createPortal(
          <div
            className="screenshot-markup-overlay-root"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10000,
              pointerEvents: isMeasuring ? "none" : "auto",
            }}
          >
            {!isMeasuring && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10001,
                  pointerEvents: "auto",
                }}
              >
                <div
                  ref={viewerDockRef}
                  className="screenshot-markup__viewer-dock"
                  style={{
                    position: "absolute",
                    top: `${dockTop}px`,
                    left: "10px",
                    transform: `scale(${dockScale})`,
                    transformOrigin: "top left",
                    zIndex: 1506,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "8px",
                    borderRadius: "10px",
                  }}
                >
                  <div className="screenshot-markup__icon-action-row">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={openExitConfirmModal}
                      disabled={!isMarkupMode}
                      title="Exit Markup"
                      aria-label="Exit Markup"
                      style={{
                        animation: shouldPulseMarkupActionIcons
                          ? "mwhMarkupPingExit 1.5s ease-out infinite"
                          : "none",
                        fontSize: "15px",
                        lineHeight: 1,
                        borderColor: "rgba(220, 53, 69, 0.86)",
                        background:
                          "linear-gradient(120deg, rgba(220, 53, 69, 0.28) 0%, rgba(111, 25, 35, 0.44) 100%)",
                        color: "#f2f2f2",
                      }}
                    >
                      ✕
                    </button>
                    {!disableManualSave && (
                      <button
                        type="button"
                        className="btn btn-sm screenshot-markup__save-tick-btn"
                        disabled={saveBusy || !isMarkupMode || !screenshotUrl}
                        onClick={currentMarkupId ? updateLoadedMarkup : openSaveModal}
                        title={currentMarkupId ? "Update markup" : "Save markup"}
                        aria-label={
                          currentMarkupId
                            ? "Update markup"
                            : "Save markup"
                        }
                        style={{
                          animation: shouldPulseMarkupActionIcons
                            ? "mwhMarkupTickPulse 1.5s ease-out infinite"
                            : "none",
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          aria-hidden="true"
                          focusable="false"
                          style={{ display: "block" }}
                        >
                          <path d="M11 2H9v3h2z" />
                          <path d="M1.5 1h11.293L15 3.207V14.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 14.5v-12A1.5 1.5 0 0 1 2.5 1zm3.5 1v4h6V2zM3 8.5v6h10v-6z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <MarkupToolButtons
                    activeTool={activeTool}
                    onSelectTool={setActiveTool}
                    onImageToolRequested={requestImagePlacementFile}
                    stampPresets={STAMP_PRESETS}
                    stampPreset={STAMP_PRESETS[stampPresetIndex]}
                    onSelectStampPreset={selectStampPreset}
                    disabled={!isMarkupMode}
                    vertical
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={undoLast}
                    disabled={!undoStack.length}
                    title="Undo"
                    aria-label="Undo"
                  >
                    ↶
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={openClearConfirmModal}
                    disabled={!isMarkupMode}
                    title="Clear"
                    aria-label="Clear"
                  >
                    ⌫
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={deleteSelectedEntity}
                    disabled={selectedEntityCount < 1}
                    title="Delete selected markup"
                    aria-label="Delete selected markup"
                  >
                    🗑
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    onClick={requestImageReplacementFile}
                    disabled={!selectedImageEntity}
                    title="Replace selected image"
                    aria-label="Replace selected image"
                  >
                    🖼↺
                  </button>
                  {!is2DMarkupContext && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={
                        isMeasuring
                          ? () =>
                              stopMeasureInViewer({
                                captureMeasureFrame: false,
                                forceAbort: true,
                              })
                          : startMeasureInViewer
                      }
                      disabled={!isMarkupMode}
                      title={isMeasuring ? "Finish Measure" : "Measure in Viewer"}
                      aria-label={
                        isMeasuring ? "Finish Measure" : "Measure in Viewer"
                      }
                    >
                      {isMeasuring ? "✓" : "📏"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={downloadAnnotatedImage}
                    disabled={!isMarkupMode}
                    title="Download"
                    aria-label="Download"
                  >
                    ⬇
                  </button>
                  {is2DMarkupContext && (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={zoomInCanvas}
                        disabled={!isMarkupMode}
                        title="Zoom in"
                        aria-label="Zoom in"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={zoomOutCanvas}
                        disabled={!isMarkupMode}
                        title="Zoom out"
                        aria-label="Zoom out"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={resetCanvasZoom}
                        disabled={!isMarkupMode}
                        title="Reset zoom"
                        aria-label="Reset zoom"
                      >
                        {`${Math.round(zoomLevel * 100)}%`}
                      </button>
                    </div>
                  )}
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${selectedHasFill ? "btn-dark" : "btn-outline-dark"}`}
                      onClick={openFillColorMenu}
                      disabled={!canFillSelected}
                      title="Fill selected markup"
                      aria-label="Fill selected markup"
                    >
                      ▣
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${canFillSelected && !selectedHasFill ? "btn-dark" : "btn-outline-dark"}`}
                      onClick={disableSelectedFill}
                      disabled={!canFillSelected}
                      title="No fill"
                      aria-label="No fill"
                    >
                      ∅
                    </button>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${isSizeMenuOpen ? "btn-dark" : "btn-outline-dark"}`}
                      onClick={() => setIsSizeMenuOpen((prev) => !prev)}
                      title="Size"
                      aria-label="Size"
                    >
                      ↔
                    </button>
                    {isSizeMenuOpen && (
                      <div
                        className="screenshot-markup__control"
                        style={{
                          position: "absolute",
                          left: "calc(100% + 8px)",
                          top: "50%",
                          transform: "translateY(-50%)",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: 0,
                          fontSize: "11px",
                          whiteSpace: "nowrap",
                          padding: "6px 8px",
                          border: "1px solid rgba(242, 242, 242, 0.25)",
                          borderRadius: "8px",
                          background: "rgba(0, 0, 0, 0.34)",
                          zIndex: 1510,
                        }}
                      >
                        <span>Size</span>
                        <input
                          className="screenshot-markup__size-slider"
                          type="range"
                          min="1"
                          max="12"
                          value={penSize}
                          onChange={(e) => setPenSize(Number(e.target.value))}
                          style={{ width: "120px" }}
                        />
                        <span>{penSize}</span>
                      </div>
                    )}
                  </div>
                  <label
                    className="screenshot-markup__control screenshot-markup__control--vertical"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                      marginBottom: 0,
                      fontSize: "11px",
                    }}
                  >
                    Color
                    <input
                      className="screenshot-markup__swatch"
                      type="color"
                      value={penColor}
                      onChange={(e) => handlePenColorChange(e.target.value)}
                      style={{
                        width: "32px",
                        height: "28px",
                        padding: 0,
                        border: "none",
                      }}
                    />
                  </label>
                </div>
                <div
                  ref={zoomViewportRef}
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1500,
                    overflow: "hidden",
                    background:
                      "linear-gradient(165deg, #d7e0e7 0%, #c8d3dc 56%, #bac9d4 100%)",
                  }}
                  onWheel={handleCanvasWheelZoom}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: `${fittedCanvasFrame.left}px`,
                      top: `${fittedCanvasFrame.top}px`,
                      width: `${fittedCanvasFrame.width}px`,
                      height: `${fittedCanvasFrame.height}px`,
                      transform: `translate(${zoomOffset.x}px, ${zoomOffset.y}px) scale(${zoomLevel})`,
                      transformOrigin: "top left",
                      backgroundColor: "#e8edf2",
                      backgroundImage: screenshotUrl
                        ? `url(${screenshotUrl})`
                        : "none",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      backgroundSize: "100% 100%",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #9db1c1",
                      boxShadow:
                        "0 12px 26px rgba(27, 47, 61, 0.28), 0 0 0 1px rgba(255, 255, 255, 0.82) inset",
                    }}
                  >
                    <canvas
                      ref={imageCanvasRef}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1501,
                      }}
                    />
                    <canvas
                      ref={drawCanvasRef}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1502,
                        pointerEvents: "none",
                      }}
                    />
                    <canvas
                      ref={entityCanvasRef}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={handleCanvasLeave}
                      onDoubleClick={handleEntityDoubleClick}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1503,
                        cursor: canvasCursor,
                        touchAction: "none",
                      }}
                    />
                    {textEditor.visible && (
                      <textarea
                        value={textEditor.value}
                        onChange={(e) =>
                          setTextEditor((prev) => ({
                            ...prev,
                            value: e.target.value,
                          }))
                        }
                        onBlur={commitText}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            commitText();
                          }
                          if (e.key === "Escape") {
                            setTextEditor({
                              visible: false,
                              x: 0,
                              y: 0,
                              value: "",
                              entityId: null,
                              fontSize: null,
                              penColor: null,
                              width: null,
                              height: null,
                              targetType: null,
                            });
                          }
                        }}
                        autoFocus
                        rows={
                          textEditor.targetType === TOOL_IDS.RECT_CALLOUT
                            ? 3
                            : 2
                        }
                        style={{
                          position: "absolute",
                          left: `${textEditorDisplayMetrics.x}px`,
                          top: `${textEditorDisplayMetrics.y}px`,
                          zIndex: 1504,
                          width: textEditorDisplayMetrics.width
                            ? `${textEditorDisplayMetrics.width}px`
                            : undefined,
                          minWidth: textEditorDisplayMetrics.width
                            ? `${textEditorDisplayMetrics.width}px`
                            : "140px",
                          minHeight: textEditorDisplayMetrics.height
                            ? `${textEditorDisplayMetrics.height}px`
                            : Boolean(sheetsSelected)
                              ? "56px"
                              : "44px",
                          border: `1px solid ${textEditor.penColor || penColor}`,
                          borderRadius: "4px",
                          padding: "4px 6px",
                          background: "rgba(255, 255, 255, 0.96)",
                          fontSize: `${textEditorDisplayMetrics.fontSize}px`,
                          lineHeight: 1.2,
                          resize:
                            textEditor.targetType === TOOL_IDS.RECT_CALLOUT
                              ? "none"
                              : "both",
                        }}
                        placeholder={
                          textEditor.targetType === TOOL_IDS.RECT_CALLOUT
                            ? "Type callout text (Enter or click away to place)"
                            : "Type text (Enter or click away to place)"
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {isMeasuring && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: "10px",
                    left: "10px",
                    zIndex: 11000,
                    pointerEvents: "auto",
                    background: "rgba(255,255,255,0.96)",
                    border: "1px solid #c8c8c8",
                    borderRadius: "8px",
                    padding: "10px",
                    maxWidth: "300px",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                    Measuring in Viewer
                  </div>
                  <div style={{ fontSize: "12px", marginBottom: "0" }}>
                    Use Autodesk Measure tool in the live viewer. Press Finish Measure to return to the markup
                    mode with measurement graphics and text merged.
                  </div>
                </div>

                {measureFinishButtonRect && (
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={stopMeasureInViewer}
                    title="Finish Measure"
                    aria-label="Finish Measure"
                    style={{
                      position: "absolute",
                      zIndex: 12000,
                      pointerEvents: "auto",
                      left: `${measureFinishButtonRect.left}px`,
                      top: `${measureFinishButtonRect.top}px`,
                      width: `${measureFinishButtonRect.width}px`,
                      height: `${measureFinishButtonRect.height}px`,
                      padding: "2px 4px",
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "4px",
                      boxSizing: "border-box",
                      borderColor: "#2b8f4a",
                      background: "linear-gradient(160deg, #2fa74f 0%, #2b8f4a 100%)",
                      boxShadow: "none",
                      fontSize: "10px",
                      fontWeight: 700,
                      lineHeight: 1,
                      whiteSpace: "normal",
                    }}
                  >
                    <span style={{ display: "block" }}>Finish</span>
                    <span style={{ display: "block" }}>Measure</span>
                  </button>
                )}
              </>
            )}
          </div>,
          viewerState.viewer.container,
        )}

      <Modal
        show={markupInfoModalOpen}
        onHide={closeMarkupInfoModal}
        dialogClassName="project-info-modal markup-detail-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header markup-detail-modal__header">
          <Modal.Title
            className="markup-detail-modal__title"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              paddingRight: "52px",
              minWidth: 0,
            }}
          >
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {selectedInfoMarkup?.title || `Markup ${selectedInfoMarkup?.id || ""}`}
            </span>
            <span
              className="screenshot-markup__save-card-status-badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 10px",
                borderRadius: "999px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                boxShadow: "0 6px 14px rgba(0, 0, 0, 0.18)",
                ...(selectedInfoMarkupStatus.badgeStyle || {}),
              }}
            >
              {selectedInfoMarkupStatus.label}
            </span>
          </Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeMarkupInfoModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body markup-detail-modal__body">
          {markupInfoLoading ? (
            <div className="markup-detail-modal__loading">Loading markup details...</div>
          ) : (
            <div className="markup-detail-modal__layout">
              <div className="markup-detail-modal__meta-panel">
                <div className="markup-detail-modal__meta-grid">
                  <p className="markup-detail-modal__meta-label">Category</p>
                  <p className="markup-detail-modal__meta-value">
                    {selectedInfoMarkup?.category || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Asset</p>
                  <p className="markup-detail-modal__meta-value">
                    {selectedInfoMarkup?.asset || selectedInfoMarkup?.processArea || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Created by</p>
                  <p className="markup-detail-modal__meta-value">
                    {selectedInfoMarkup?.userName || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Created date</p>
                  <p className="markup-detail-modal__meta-value">
                    {selectedInfoMarkup?.created || selectedInfoMarkup?.createdDate
                      ? new Date(
                          selectedInfoMarkup?.created ||
                            selectedInfoMarkup?.createdDate,
                        ).toLocaleDateString("en-GB")
                      : "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Assigned to</p>
                  <div className="markup-detail-modal__assigned-list">
                    {normalizeCreatedFor(selectedInfoMarkup?.createdFor).length > 0 ? (
                      normalizeCreatedFor(selectedInfoMarkup?.createdFor).map((userEmail) => (
                        <p key={userEmail} className="markup-detail-modal__meta-value">
                          {userEmail}
                        </p>
                      ))
                    ) : (
                      <p className="markup-detail-modal__meta-value">None</p>
                    )}
                  </div>

                  {selectedInfoMarkup?.archived && (
                    <>
                      <p className="markup-detail-modal__meta-label">Archived by</p>
                      <p className="markup-detail-modal__meta-value">
                        {selectedInfoMarkup?.archivedBy || "None"}
                      </p>
                      <p className="markup-detail-modal__meta-label">Archived on</p>
                      <p className="markup-detail-modal__meta-value">
                        {selectedInfoMarkup?.archivedDate
                          ? new Date(selectedInfoMarkup.archivedDate).toLocaleDateString("en-GB")
                          : "None"}
                      </p>
                    </>
                  )}
                </div>

                <p className="markup-detail-modal__comments-label">Comments</p>
                <div className="markup-detail-modal__comments">
                  <Comments
                    currentUser={userData?.emailId || userDetails?.email || ""}
                    comments={selectedInfoMarkup?.comments || []}
                    updateMarkup={updateMarkupInfoComments}
                  />
                </div>
              </div>

              <div className="markup-detail-modal__preview-panel">
                {selectedInfoMarkupImageLoading ? (
                  <div className="markup-detail-modal__preview-empty">
                    Loading image...
                  </div>
                ) : selectedInfoMarkupImage ? (
                  <img
                    className="markup-detail-modal__preview-image"
                    src={selectedInfoMarkupImage}
                    alt={selectedInfoMarkup?.title || "Markup preview"}
                  />
                ) : (
                  <div className="markup-detail-modal__preview-empty">No preview available</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="markup-detail-modal__footer">
          <div className="markup-detail-modal__footer-actions screenshot-markup__save-card-actions">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger screenshot-markup__load-btn screenshot-markup__delete-btn"
              onClick={closeMarkupInfoModal}
              disabled={markupInfoSaving}
            >
              Close
            </button>
            {/* <button
              type="button"
              className="btn btn-sm btn-outline-primary screenshot-markup__load-btn screenshot-markup__info-btn"
              onClick={closeMarkupInfoModal}
              disabled={markupInfoSaving}
            >
              View
            </button> */}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={statusModalOpen}
        onHide={closeStatusModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Update Markup Status</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeStatusModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <div
            className="save-markup-modal__form"
            style={{ display: "grid", gap: "10px" }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.72)",
                  marginBottom: "4px",
                }}
              >
                Markup
              </div>
              <div style={{ fontWeight: 600 }}>
                {pendingStatusMarkup?.title ||
                  `Markup ${pendingStatusMarkup?.id || ""}`}
              </div>
            </div>

            <div>
              <label
                className="save-markup-modal__label"
                htmlFor="screenshot_markup_status"
              >
                Status
              </label>
              <Select
                inputId="screenshot_markup_status"
                className="save-markup-modal__select"
                classNamePrefix="acc-project-select"
                menuPortalTarget={menuPortalTarget || undefined}
                styles={customStyles}
                options={MARKUP_STATUS_OPTIONS}
                value={
                  MARKUP_STATUS_OPTIONS.find(
                    (option) => option.value === selectedStatusValue,
                  ) || MARKUP_STATUS_OPTIONS[0]
                }
                onChange={(option) =>
                  setSelectedStatusValue(option?.value || "open")
                }
                isDisabled={statusUpdateBusy}
              />
            </div>

            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.72)",
                  marginBottom: "6px",
                }}
              >
                Preview
              </div>
              <span
                className="screenshot-markup__save-card-status-badge"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px 10px",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  boxShadow: "0 6px 14px rgba(0, 0, 0, 0.18)",
                  ...(getMarkupStatusDisplay(
                    buildMarkupStatusPayload(
                      pendingStatusMarkup || {},
                      selectedStatusValue,
                    ),
                  ).badgeStyle || {}),
                }}
              >
                {
                  getMarkupStatusDisplay(
                    buildMarkupStatusPayload(
                      pendingStatusMarkup || {},
                      selectedStatusValue,
                    ),
                  ).label
                }
              </span>
            </div>
          </div>

          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px", marginTop: "14px" }}
          >
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={submitStatusUpdate}
              disabled={statusUpdateBusy || !pendingStatusMarkup}
            >
              {statusUpdateBusy ? "Updating..." : "Update Status"}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeStatusModal}
              disabled={statusUpdateBusy}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={exitConfirmModalOpen}
        onHide={closeExitConfirmModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Exit Markup</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeExitConfirmModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <p style={{ marginBottom: "0.75rem" }}>
            Exit markup mode? Unsaved markup changes will be lost.
          </p>
          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={confirmExitMarkupMode}
            >
              Exit
            </button>
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeExitConfirmModal}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={clearConfirmModalOpen}
        onHide={closeClearConfirmModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Clear Markup</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeClearConfirmModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <p style={{ marginBottom: "0.75rem" }}>
            Are you sure you want to clear the current markup? This will remove all current annotations.
          </p>
          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={confirmClearMarkup}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeClearConfirmModal}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={archiveConfirmModalOpen}
        onHide={closeArchiveConfirmModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Archive Markup</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeArchiveConfirmModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <p style={{ marginBottom: "0.75rem" }}>
            Archive markup "
            {pendingArchiveMarkup?.title || pendingArchiveMarkup?.id || ""}"?
          </p>
          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={confirmArchiveMarkup}
              disabled={Boolean(archivingMarkupId)}
            >
              {archivingMarkupId ? "Archiving..." : "Archive"}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeArchiveConfirmModal}
              disabled={Boolean(archivingMarkupId)}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={deleteConfirmModalOpen}
        onHide={closeDeleteConfirmModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Delete Markup</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeDeleteConfirmModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <p style={{ marginBottom: "0.75rem" }}>
            Delete archived markup "
            {pendingDeleteMarkup?.title || pendingDeleteMarkup?.id || ""}" permanently?
            This cannot be undone.
          </p>
          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={confirmDeleteMarkup}
              disabled={Boolean(deletingArchivedMarkupId)}
            >
              {deletingArchivedMarkupId ? "Deleting..." : "Delete"}
            </button>
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeDeleteConfirmModal}
              disabled={Boolean(deletingArchivedMarkupId)}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={pinPlacementWarningModalOpen}
        onHide={acknowledgePinPlacementWarning}
        dialogClassName="project-info-modal save-markup-modal"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Invalid Pin Placement</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={acknowledgePinPlacementWarning}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
       
            <p style={{ marginBottom: "0.5rem", fontWeight: 700 }}>
              Empty space was selected.
            </p>
            <p style={{ marginBottom: 0 }}>
              Place the pin on an actual model object so mView can determine
              which model the markup belongs to.
            </p>
          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={acknowledgePinPlacementWarning}
            >
              OK, Place Pin Again
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={saveModalOpen}
        onHide={closeSaveModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Markup Additional Information</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeSaveModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <div
            className="form-group save-markup-modal__form"
            style={{ marginBottom: "10px" }}
          >
            <div className="form-floating mb-1">
              <input
                type="text"
                className="form-control save-markup-modal__input"
                id="screenshot_markup_title"
                placeholder="Markup Title"
                value={saveTitle}
                onChange={(e) => setSaveTitle(e.target.value)}
                readOnly={saveModalMode === "info"}
              />
              <label
                htmlFor="screenshot_markup_title"
                className="save-markup-modal__floating-label"
              >
                Markup title
              </label>
            </div>

            <label className="save-markup-modal__label" htmlFor="screenshot_markup_category">
              Category
            </label>
            <Select
              inputId="screenshot_markup_category"
              className="mb-2 save-markup-modal__select"
              classNamePrefix="acc-project-select"
              menuPortalTarget={menuPortalTarget || undefined}
              styles={customStyles}
              options={contextMarkupCategories.map((category) => ({
                label: category,
                value: category,
              }))}
              value={{
                label: getEffectiveCategoryForContext(saveCategory),
                value: getEffectiveCategoryForContext(saveCategory),
              }}
              onChange={(option) =>
                setSaveCategory(option?.value || DEFAULT_MARKUP_CATEGORY)
              }
              isDisabled={saveModalMode === "info"}
            />

            <label className="save-markup-modal__label" htmlFor="screenshot_markup_users">
              Created for
            </label>
            <Select
              inputId="screenshot_markup_users"
              className="mb-2 save-markup-modal__select"
              classNamePrefix="save-markup-users-select"
              menuPortalTarget={menuPortalTarget || undefined}
              styles={saveMarkupCreatedForStyles}
              onChange={(value) => setSaveForUsers(value || [])}
              options={projectUserOptions}
              value={saveForUsers}
              isMulti
              isDisabled={saveModalMode === "info"}
            />
          </div>

          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            {saveModalMode !== "info" && (
              <button
                disabled={
                  saveBusy || !(saveTitle.trim() && saveForUsers.length > 0)
                }
                type="button"
                className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
                onClick={submitSaveModal}
              >
                {saveModalMode === "update" ? "Update Markup" : "Save Markup"}
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeSaveModal}
              disabled={saveBusy}
            >
              {saveModalMode === "info" ? "Close" : "Cancel"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ScreenshotMarkup;
